/**
 * Translation catalog.
 *
 * Keys are dot-namespaced strings; values are the literal text for each
 * supported language. Add new keys as you internationalise more surfaces —
 * an unknown key falls back to the key itself, so it's safe to ship a
 * partial dictionary.
 *
 * Conventions:
 *   - Group by surface (topbar.*, profile.*, dashboard.*).
 *   - Use sentence case in `en`; mirror in `es`.
 *   - Static UI text only. Don't translate user-generated content.
 */

import type { AppLanguage } from '@/lib/prefs/usePrefsStore';

type Dict = Record<string, string>;

const EN: Dict = {
  // ── Common ─────────────────────────────────────────────────────────
  'common.edit': 'Edit',
  'common.comingSoon': 'Coming soon',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.done': 'Done',
  'profile.privacy.eyebrow': 'Privacy',
  'profile.privacy.profileLabel': 'Profile card',
  'profile.privacy.profileHint': 'Who can see your name, bio and theme.',
  'profile.privacy.activityLabel': 'Activity timeline',
  'profile.privacy.activityHint': 'Whether your calendar and event activity appear cross-workspace.',
  'profile.privacy.public': 'Public',
  'profile.privacy.private': 'Private',
  'profile.privacy.saved': 'Privacy preference saved',
  'profile.privacy.saveError': 'Could not save privacy preference',
  'common.close': 'Close',
  'common.delete': 'Delete',
  'common.create': 'Create',
  'common.signOut': 'Sign out',
  'datePicker.placeholder': 'Select a date',
  'datePicker.clear': 'Clear date',
  'datePicker.time': 'Time',
  'tour.start': 'Take a tour',
  'tour.next': 'Next',
  'tour.prev': 'Back',
  'tour.done': 'Done',
  'tour.progress': '{{current}} of {{total}}',
  'tour.restart': 'Restart product tours',
  'tour.restartHint': 'Replay the guided walkthroughs as you visit each page.',
  'tour.restartDone': "Tours reset — they'll show again as you browse.",
  'journeyPreview.title': 'Live preview',
  'brandTree.label': 'Browse projects, events & stations',
  'brandTree.loadError': 'Could not load',
  'brandTree.empty.projects': 'No projects yet',
  'brandTree.empty.events': 'No events',
  'brandTree.empty.stations': 'No stations',
  'common.unassigned': 'Unassigned',
  'common.upcoming': 'Upcoming',
  'common.live': 'Live',
  'common.idle': 'Idle',
  'common.export': 'Export',
  'common.search': 'Search…',
  'common.loading': 'Loading…',

  // ── TopBar ─────────────────────────────────────────────────────────
  'topbar.workspace': 'WORKSPACE',
  'topbar.agency': 'AGENCY',
  'topbar.atledi': 'Atledi',
  'topbar.atlediSoon': 'Atledi assistant coming soon',
  'topbar.resetDemo': 'Reset demo data',
  'topbar.theme': 'Theme',
  'topbar.language': 'Language',
  'topbar.openMenu': 'Open menu',
  'topbar.closeMenu': 'Close menu',
  'topbar.help': 'Help & documentation',

  // ── Workspace switcher (left-aligned brand picker pill) ────────────
  'topbar.workspaceSwitcher.searchPlaceholder': 'Search brands…',
  'topbar.workspaceSwitcher.empty': 'No matching brands',
  'topbar.workspaceSwitcher.clear': 'Clear search',
  'topbar.workspaceSwitcher.noBrand': 'Pick a brand',

  // ── Sidebar brand tree (selected brand + projects/events) ──────────
  'sidebar.brandTree.projects': 'Projects',
  'sidebar.brandTree.events': 'Events',
  'sidebar.brandTree.noProjects': 'No projects yet',
  'sidebar.brandTree.noEvents': 'No events yet',
  'sidebar.brandTree.noStations': 'No stations yet',
  'sidebar.brandTree.expand': 'Expand',
  'sidebar.brandTree.collapse': 'Collapse',

  // ── Brand profile tabs (Projects · Xotskies · Leftovers) ───────────
  'brand.tabs.projects': 'Projects',
  'brand.tabs.xotskies': 'Xotskies',
  'brand.tabs.inventory': 'Inventory',
  'brand.tabs.leftovers': 'Leftovers',
  'brand.leftovers.title': 'Leftover inventory from past events',
  'brand.leftovers.subtitle': 'Past events with unused xotski units still allocated to their stations.',
  'brand.leftovers.emptyTitle': 'Nothing leftover',
  'brand.leftovers.emptyBody': 'No past event finished with available units. Once an event ends with leftovers, it will show up here.',
  'brand.leftovers.noBrand': 'No brand selected',
  'brand.leftovers.noBrandBody': 'Pick a brand from the workspace switcher to see its leftover inventory.',
  'brand.leftovers.totalLeftover': 'Total leftover',
  'brand.leftovers.units': 'units',
  // Legacy — kept for any deep links still referencing the old label.
  'brand.tabs.prizes': 'Inventory',
  'brand.xotskies.title': 'Brand xotskies',
  'brand.xotskies.subtitle': 'Catalog of xotskies owned by this brand. Add or bulk-import from a ZIP.',
  'brand.xotskies.add': 'Add xotski',
  'brand.xotskies.import': 'Bulk import',
  'brand.xotskies.emptyTitle': 'No xotskies yet',
  'brand.xotskies.emptyBody': 'Add the first xotski for this brand or bulk-import a ZIP.',
  'brand.xotskies.noBrand': 'No brand selected',
  'brand.xotskies.noBrandBody': 'Pick a brand from the workspace switcher to manage its xotskies.',
  'brand.xotskies.agencyCatalog': 'Available from agency catalog',
  'brand.xotskies.agencyCatalogEmpty': 'All agency xotskies already adopted, or the catalog is empty.',
  'brand.xotskies.deleteAria': 'Delete xotski',
  'brand.xotskies.deleteBlocked': 'Cannot delete xotski',
  'brand.xotskies.deleteBlockedHint': 'Has active allocations on a station — remove those allocations before deleting.',
  'brand.xotskies.writeOffBlocked': 'Cannot write off more than {available} available unit(s).',

  // ── Theme menu ─────────────────────────────────────────────────────
  'theme.dark': 'Dark',
  'theme.light': 'Light',

  // ── Dashboard / Home ───────────────────────────────────────────────
  'dashboard.portfolio': 'Brand portfolio',
  'dashboard.brandHub': 'Brand Hub',
  'dashboard.brandsLower': 'brands',
  'dashboard.kpi.liveActivations': 'Live activations',
  'dashboard.kpi.spend': 'Spend',
  'dashboard.kpi.peopleReached': 'People reached (YTD)',
  'dashboard.subBrands': 'Sub-brands',
  'dashboard.brandManagers': 'Brand managers',
  'dashboard.newBrand': 'New brand',
  'dashboard.noBrandsTitle': 'No brands yet',
  'dashboard.noBrandsBody': 'Add a brand to start running projects under your workspace.',
  'dashboard.noManagers': 'No managers assigned yet. Invite teammates from Settings.',

  // ── SubBrand card ──────────────────────────────────────────────────
  'subbrand.projects': 'Projects',
  'subbrand.events': 'Events',
  'subbrand.active': 'Active',
  'subbrand.ownedBy': 'Owned by',
  'subbrand.ytd': 'YTD',

  // ── Brand profile page ─────────────────────────────────────────────
  'brand.workspace': 'Brand workspace',
  'brand.editInStudio': 'Edit in Brand Studio',
  'brand.new.cta': 'New brand',
  'brand.new.title': 'Create a new brand',
  'brand.new.subtitle': 'Just the basics — you’ll customise the rest in Brand Studio next.',
  'brand.new.nameLabel': 'Brand name',
  'brand.new.namePlaceholder': 'e.g. Acme Soda',
  'brand.new.industryLabel': 'Industry',
  'brand.new.industryPlaceholder': 'e.g. Beverages (optional)',
  'brand.new.studioHint': 'After creating it we’ll take you straight to Brand Studio to add the logo, colours and fonts.',
  'brand.new.submit': 'Create brand',
  'brand.new.creating': 'Creating…',
  'brand.new.created': 'Brand “{name}” created',
  'brand.new.failed': 'Could not create brand',
  'brand.viewTrends': 'View trends',
  'brand.recentEvents': 'Recent events',
  'brand.recentEvents.count': 'Showing {shown} of {total}',
  'common.open': 'Open',
  'brand.activeProjects': 'Active projects',
  'brand.newProject': 'New project',
  'brand.noProjectsTitle': 'No projects yet',
  'brand.noProjectsBody': 'Start your first campaign to set up events, stations, and xotskies.',
  'brand.createProject': 'Create project',
  'brand.kpi.activeXotskies': 'Active xotskies',
  'brand.kpi.distributed': 'Distributed YTD',
  'brand.kpi.engagement': 'Engagement rate',
  'brand.kpi.health': 'Brand health',

  // ── Profile page ───────────────────────────────────────────────────
  'profile.tabs.events': 'Events',
  'profile.tabs.brands': 'Brands',
  'profile.tabs.projects': 'Projects',
  'profile.tabs.stations': 'Stations',
  'profile.tabs.team': 'Team',
  'profile.events.upcoming': 'Upcoming events',
  'profile.events.past': 'Past events',
  'profile.events.searchPlaceholder': 'Search events…',
  'profile.events.emptyUpcomingTitle': 'No upcoming events',
  'profile.events.emptyUpcomingSubtitle': 'Create a project to get started',
  'profile.events.emptyPastTitle': 'No past events recorded yet.',
  'profile.brands.emptyTitle': 'Brands you manage',
  'profile.brands.emptySubtitle': 'Brands linked to your account will appear here.',
  'profile.brands.nextEvent': 'Next event',
  'profile.brands.noUpcoming': 'No upcoming events',
  'profile.team.emptyTitle': 'Team members',
  'profile.team.emptySubtitle': 'Invite collaborators to see them here.',
  'profile.activity.emptyTitle': 'No activity yet',
  'profile.activity.emptySubtitle':
    'Resources you create, edit or delete will show up here.',
  'profile.activity.loadErrorTitle': 'Could not load activity',
  'profile.activity.loadErrorSubtitle': 'Please try again in a moment.',
  'profile.activity.lastActivity': 'Last activity {date}',
  'profile.activity.historyToggle': 'History ({count})',
  'profile.activity.badgeDeleted': 'Deleted',
  'profile.activity.noun': 'resources',
  'profile.activity.history.created': 'Created this {type}',
  'profile.activity.history.updated': 'Edited {fields}',
  'profile.activity.history.updatedGeneric': 'Edited this {type}',
  'profile.activity.history.deleted': 'Deleted this {type}',
  'profile.activity.history.restored': 'Restored this {type}',
  'profile.activity.type.brand': 'brand',
  'profile.activity.type.event': 'event',
  'profile.activity.type.project': 'project',
  'profile.activity.type.station': 'station',
  'profile.sidebar.experience': 'My experience',
  'profile.sidebar.contact': 'Contact information',
  'profile.sidebar.professional': 'Professional',
  'profile.sidebar.additional': 'Additional info',
  'profile.sidebar.preferences': 'Preferences',
  'profile.sidebar.languages': 'Languages',
  'profile.sidebar.interests': 'Interests',
  'profile.sidebar.editProfile': 'Edit profile',
  'profile.sidebar.changePassword': 'Change password',
  'profile.sidebar.theme': 'Theme',
  'profile.sidebar.language': 'Language',
  'profile.sidebar.noBio':
    'No bio yet. Click "Edit profile" to add your experience.',

  // ── Pagination ─────────────────────────────────────────────────────
  'pagination.previous': 'Previous',
  'pagination.next': 'Next',
  'pagination.previousPage': 'Previous page',
  'pagination.nextPage': 'Next page',
  'pagination.showing': 'Showing',
  'pagination.of': 'of',
  'pagination.items': 'items',

  // ── Global search ──────────────────────────────────────────────────
  'globalSearch.placeholder': 'Search anything — events, brands, stations, stock',
  'globalSearch.clear': 'Clear search',
  'globalSearch.noMatches': 'No matches for',
  'globalSearch.kind.brand': 'Brands',
  'globalSearch.kind.event': 'Events',
  'globalSearch.kind.station': 'Stations',
  'globalSearch.kind.stock': 'Stock',
  'globalSearch.subBrand': 'Brand',
  'globalSearch.subEvent': 'Event',
  'globalSearch.subStation': 'Station',
  'globalSearch.subStockItem': 'Stock item',

  // ── Right sidebar / AI Assistant ───────────────────────────────────
  'assistant.title': 'Atledi',
  'assistant.collapse': 'Collapse assistant',
  'assistant.expand': 'Open assistant',
  // Caso 07 — global launcher + drawer aria labels.
  'assistant.launcher.aria': 'Open AI assistant',
  'assistant.drawer.aria': 'AI assistant',
  'assistant.kickoffPending': 'Reading page context…',
  'assistant.placeholder': 'Ask anything about this page…',
  'assistant.send': 'Send',
  'assistant.suggestions': 'Suggestions',
  'assistant.error': "Couldn't reach the assistant. Try again.",
  'assistant.error.unavailable': 'The assistant is temporarily unavailable. Please try again in a moment.',
  'assistant.retry': 'Try again',

  // ── Export XLSX ────────────────────────────────────────────────────
  'export.button': 'Export XLSX',
  'export.exporting': 'Exporting…',
  'export.nothingTitle': 'Nothing to export',
  'export.nothingBody': 'The list is empty.',
  'export.readyTitle': 'Export ready',
  'export.readyBodyOne': 'row downloaded.',
  'export.readyBodyMany': 'rows downloaded.',
  'export.failedTitle': 'Export failed',

  // ── Forms / file uploader ──────────────────────────────────────────
  'forms.colorField.pickColor': 'Pick color',
  'forms.uploader.formats': 'PNG, JPG, WEBP or SVG',
  'forms.uploader.uploadFailed': 'Upload failed',
  'forms.uploader.cancel': 'Cancel',
  'forms.uploader.edit': 'Edit',
  'forms.uploader.remove': 'Remove',
  'forms.uploader.retry': 'Retry',
  'forms.imageEditor.title': 'Edit image',
  'forms.imageEditor.zoomOut': 'Zoom out',
  'forms.imageEditor.zoomIn': 'Zoom in',
  'forms.imageEditor.rotateLeft': 'Rotate left',
  'forms.imageEditor.rotateRight': 'Rotate right',
  'forms.imageEditor.zoom': 'Zoom',
  'forms.imageEditor.brightness': 'Brightness',
  'forms.imageEditor.contrast': 'Contrast',
  'forms.imageEditor.saturation': 'Saturation',
  'forms.imageEditor.reset': 'Reset',
  'forms.imageEditor.cancel': 'Cancel',
  'forms.imageEditor.apply': 'Apply crop',
  'forms.imageEditor.hint': 'Drag to reposition · Scroll to zoom · ± buttons for fine control',
  'forms.rating.starOne': '1 star',
  'forms.rating.starMany': 'stars',

  // ── Common buttons / states ────────────────────────────────────────
  'common.saving': 'Saving…',
  'common.next': 'Next',
  'common.back': 'Back',
  'common.continue': 'Continue',
  'common.skip': 'Skip',
  'common.submit': 'Submit',
  'common.confirm': 'Confirm',
  'common.add': 'Add',
  'common.remove': 'Remove',
  'common.update': 'Update',
  'common.optional': 'Optional',
  'common.required': 'Required',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.retry': 'Retry',
  'common.viewAll': 'View all',
  'common.error': 'Something went wrong',
  'common.tryAgain': 'Please try again.',
  'common.empty': 'Nothing here yet',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.draft': 'Draft',
  'common.active': 'Active',
  'common.disabled': 'Disabled',
  'common.enabled': 'Enabled',
  'common.preview': 'Preview',
  'common.print': 'Print',
  'common.download': 'Download',
  'common.duplicate': 'Duplicate',
  'common.archive': 'Archive',
  'common.locked': 'Locked',
  'common.unlocked': 'Unlocked',

  // ── Onboarding ─────────────────────────────────────────────────────
  'onboarding.brandProfile.title': 'Tell us about your brand',
  'onboarding.brandProfile.subtitle': 'A few details so we can tailor your workspace.',
  'onboarding.brandProfile.brandName': 'Brand name',
  'onboarding.brandProfile.brandNamePlaceholder': 'e.g. Atledi',
  'onboarding.brandProfile.industry': 'Industry',
  'onboarding.brandProfile.industryPlaceholder': 'Select an industry',
  'onboarding.brandProfile.website': 'Website',
  'onboarding.brandProfile.websitePlaceholder': 'https://yourbrand.com',
  'onboarding.brandProfile.about': 'About',
  'onboarding.brandProfile.aboutPlaceholder': 'Short description of what you do.',
  'onboarding.brandProfile.continue': 'Continue',
  'onboarding.brandKit.title': 'Add your brand kit',
  'onboarding.brandKit.subtitle': 'Upload your logo and pick your brand colors.',
  'onboarding.brandKit.logo': 'Logo',
  'onboarding.brandKit.primaryColor': 'Primary color',
  'onboarding.brandKit.accentColor': 'Accent color',
  'onboarding.brandKit.finish': 'Finish setup',
  'onboarding.brandKit.skip': 'Skip for now',
  'onboarding.profile.title': 'Tell us about your brand',
  'onboarding.profile.subtitle': "We'll spin up your free workspace right after.",
  'onboarding.profile.companyName': 'Brand name',
  'onboarding.profile.companyNamePlaceholder': 'Lime Soda Co.',
  'onboarding.profile.yourName': 'Your name',
  'onboarding.profile.yourNamePlaceholder': 'Jane Founder',
  'onboarding.profile.industry': 'Industry',
  'onboarding.profile.pickOne': 'Pick one',
  'onboarding.profile.continue': 'Continue',
  'onboarding.profile.savedTitle': 'Profile saved',
  'onboarding.profile.savedBody': 'Your free plan is now active. One more step.',
  'onboarding.profile.errorTitle': 'Could not save',
  'onboarding.kit.title': 'Build your BrandKit',
  'onboarding.kit.subtitle': "We'll use these defaults across journeys, the wheel and printables.",
  'onboarding.kit.campaignName': 'Campaign name',
  'onboarding.kit.campaignNamePlaceholder': 'Summer Tour 2026',
  'onboarding.kit.primary': 'Primary',
  'onboarding.kit.secondary': 'Secondary',
  'onboarding.kit.font': 'Font',
  'onboarding.kit.clientLogo': 'Client logo',
  'onboarding.kit.finish': 'Finish setup',
  'onboarding.kit.savedTitle': 'BrandKit saved!',
  'onboarding.kit.savedBody': 'Welcome to Xotski.',
  'onboarding.kit.errorTitle': 'Could not save BrandKit',

  // ── Stock ──────────────────────────────────────────────────────────
  'stock.title': 'Xotskies',
  'stock.subtitle': 'Manage the items you offer at activations.',
  'stock.addButton': 'Add xotski',
  'stock.empty.title': 'No xotskies yet',
  'stock.empty.body': 'Add your first xotski to start running activations.',
  'stock.empty.cta': 'Add xotski',
  'stock.column.name': 'Name',
  'stock.column.rating': 'Rating',
  'stock.column.classification': 'Classification',
  'stock.column.status': 'Status',
  'stock.column.actions': 'Actions',
  'stock.searchPlaceholder': 'Search xotskies…',
  'stock.confirmDelete': 'Delete this xotski?',
  'stock.deletedTitle': 'Xotski deleted',
  'stock.savedTitle': 'Xotski saved',
  'stock.errorTitle': 'Couldn\'t save xotski',
  'stock.dialog.addTitle': 'Add xotski',
  'stock.dialog.editTitle': 'Edit xotski',
  'stock.dialog.name': 'Name',
  'stock.dialog.namePlaceholder': 'Sample bottle, postcard, sticker…',
  'stock.dialog.translate': 'Spanish name',
  'stock.dialog.translatePlaceholder': 'Optional translation',
  'stock.dialog.image': 'Image',
  'stock.dialog.logo': 'Logo',
  'stock.dialog.rating': 'Wheel probability',
  'stock.dialog.classification': 'Classification',
  'stock.dialog.classificationPlaceholder': 'Beverage, swag, voucher…',
  'stock.dialog.status': 'Status',
  'stock.list.title': 'My Merch',
  'stock.list.subtitle': 'The stock items that show up on your spin wheels and printables.',
  'stock.list.addButton': 'Add a xotski',
  'stock.list.filter': 'Filter',
  'stock.list.allClassifications': 'All classifications',
  'stock.list.unclassified': 'Unclassified',
  'stock.list.itemOne': 'item',
  'stock.list.itemMany': 'items',
  'stock.list.emptyTitle': 'No stock items',
  'stock.list.emptyBody': 'Add the products and merch your guests can win.',
  'stock.list.allClassified': 'Everything is classified',
  'stock.list.allClassifiedBody': 'Every item has a SEE / ENGAGE / TRY / ACT label.',
  'stock.list.noneInClass': 'No xotskies classified as',
  'stock.list.editToSetClass': 'Edit a xotski and set its classification to',
  'stock.list.editToSetClassSuffix': 'to see it here.',
  'stock.list.createdTitle': 'Xotski added to inventory',
  'stock.list.updatedTitle': 'Xotski updated',
  'stock.list.createFailed': 'Create failed',
  'stock.list.updateFailed': 'Update failed',
  'stock.card.edit': 'Edit',
  'stock.card.delete': 'Delete',
  'stock.card.rarityTier': 'Rarity tier',
  'stock.card.rarityLabel.1': 'Common',
  'stock.card.rarityLabel.2': 'Uncommon',
  'stock.card.rarityLabel.3': 'Rare',
  'stock.card.rarityLabel.4': 'Epic',
  'stock.card.rarityLabel.5': 'Legendary',
  'stock.card.qty': 'Qty',
  'stock.card.ratingOf': 'Rating',
  'stock.dialog.editingTitle': 'Edit xotski',
  'stock.dialog.addingTitle': 'Add a xotski',
  'stock.dialog.updateHeading': 'Update',
  'stock.dialog.updateHeadingFallback': 'merch',
  'stock.dialog.newHeading': "What's the new merch?",
  'stock.dialog.editLead': 'Tweak the basics — changes apply everywhere this xotski appears.',
  'stock.dialog.newLead': "Set the basics. You'll assign quantities to specific stations from My Xotskies later.",
  'stock.dialog.section.identity': 'Identity',
  'stock.dialog.section.visuals': 'Visuals',
  'stock.dialog.section.inventory': 'Inventory',
  'stock.dialog.section.funnel': 'Funnel stage',
  'stock.dialog.section.rarity': 'Rarity',
  'stock.dialog.field.name': 'Name',
  'stock.dialog.field.namePlaceholder': 'e.g. Joyly enamel pin',
  'stock.dialog.field.translate': 'Spanish name (translate)',
  'stock.dialog.field.translatePlaceholder': 'Shown when journey runs in en-es',
  'stock.dialog.field.description': 'Description',
  'stock.dialog.field.descriptionPlaceholder': 'A short blurb shown on the prize-won screen…',
  'stock.dialog.field.productImage': 'Product image',
  'stock.dialog.field.wheelLogo': 'Wheel logo',
  'stock.dialog.field.initialQty': 'Initial quantity',
  'stock.dialog.field.unitCost': 'Unit cost (USD)',
  'stock.dialog.field.wheelLogoLibrary': 'Or pick from library',
  'stock.dialog.field.wheelLogoLibraryEmpty':
    'No curated logos yet. Drop files into ox-platform/uploads/wheel-logos/ to populate the library.',
  'stock.dialog.field.restockThreshold': 'Restock threshold',
  'stock.dialog.field.restockPlaceholder': 'Alert below…',
  'stock.dialog.field.classification': 'Classification (SEE · ENGAGE · TRY · ACT)',
  'stock.dialog.field.classUnset': 'Unset',
  'stock.dialog.field.rarityTier': 'Rarity tier (drives wheel probability)',
  'stock.dialog.weight.weightOn': 'Weight on the wheel:',
  'stock.dialog.weight.hint': '1 = common (frequent win), 5 = legendary (rare drop).',
  'stock.dialog.fixFieldsOne': 'Please fix 1 field before saving:',
  'stock.dialog.fixFieldsMany': 'Please fix {count} fields before saving:',
  'stock.dialog.cancel': 'Cancel',
  'stock.dialog.saving': 'Saving…',
  'stock.dialog.saveChanges': 'Save changes',
  'stock.dialog.addToInventory': 'Add to inventory',
  'stock.dialog.error.nameLen': 'Name must be at least 2 characters',
  'stock.dialog.error.qtyGt0': 'Initial quantity must be greater than 0',
  'stock.dialog.error.qtyInt': 'Must be a whole number',
  'stock.dialog.error.costNeg': 'Unit cost cannot be negative',
  'stock.dialog.error.threshNeg': 'Threshold cannot be negative',
  'stock.dialog.error.threshExceeds': 'Threshold cannot exceed initial quantity',
  'stock.dialog.error.ratingRange': 'Rating must be between 1 and 5',
  'stock.dialog.classHint.SEE': 'Awareness — visibility only',
  'stock.dialog.classHint.ENGAGE': 'Engagement — small touchpoint reward',
  'stock.dialog.classHint.TRY': 'Trial — sampling / tasting',
  'stock.dialog.classHint.ACT': 'Action — high-intent conversion',

  // ── Stations ───────────────────────────────────────────────────────
  'stations.title': 'Stations',
  'stations.subtitle': 'Where your team distributes xotskies on event day.',
  'stations.addButton': 'New station',
  'stations.empty.title': 'No stations yet',
  'stations.empty.body': 'Add a station to assign xotskies and capture interactions.',
  'stations.empty.cta': 'Create station',
  'stations.searchPlaceholder': 'Search stations…',
  'stations.column.name': 'Name',
  'stations.column.event': 'Event',
  'stations.column.surveys': 'Journeys',
  'stations.column.status': 'Status',
  'stations.confirmDelete': 'Delete this station?',
  'stations.deletedTitle': 'Station deleted',
  'stations.savedTitle': 'Station saved',
  'stations.dialog.addTitle': 'New station',
  'stations.dialog.editTitle': 'Edit station',
  'stations.dialog.name': 'Station name',
  'stations.dialog.event': 'Event',
  'stations.dialog.surveys': 'Journeys',
  'stations.dialog.status': 'Status',
  'stations.detail.qrCode': 'QR code',
  'stations.detail.printQr': 'Print QR',
  'stations.detail.copyLink': 'Copy link',
  'stations.detail.linkCopied': 'Link copied to clipboard',
  'stations.detail.stockAssigned': 'Xotskies assigned',
  'stations.detail.noStock': 'No xotskies assigned to this station.',
  'stations.detail.responses': 'Interactions',
  'stations.detail.noResponses': 'No interactions captured yet.',
  'stations.print.title': 'Print station QR codes',
  'stations.print.instructions': 'Use your browser\'s print dialog to send these QR codes to a printer or save as PDF.',
  'stations.list.subtitleLong': 'Every QR code lives on a station. Each station belongs to an event.',
  'stations.list.emptyDesc': "Create stations inside a project's events.",
  'stations.list.goToProjects': 'Go to projects',
  'stations.list.allStations': 'All stations',
  'stations.list.column.survey': 'Journey',
  'stations.list.column.actions': 'Actions',
  'stations.list.allocate': 'Allocate',
  'stations.list.qrShort': 'QR',
  'stations.detail.notFound': 'Station not found',
  'stations.detail.back': 'Back',
  'stations.detail.eventLabel': 'Event',
  'stations.detail.surveyLabel': 'Journey',
  'stations.detail.edit': 'Edit',
  'stations.detail.viewQr': 'View QR code',
  'stations.detail.allocationsTitle': 'Stock allocations',
  'stations.detail.stock': 'Stock',
  'stations.detail.loadingStocks': 'Loading stock catalog…',
  'stations.detail.allAllocated': 'All brand stocks already allocated to this station',
  'stations.detail.noStocksForBrand': 'No stock items yet for this brand',
  'stations.detail.createStocksCta': 'Create stock',
  'stations.detail.available': 'available',
  'stations.detail.pickStock': 'Pick a stock item',
  'stations.detail.quantity': 'Quantity',
  'stations.detail.qtyError': 'Must be ≥ 1',
  'stations.detail.overrideRating': 'Override rating',
  'stations.detail.usesDefault': 'Uses stock default',
  'stations.detail.wheelWeight': 'Wheel weight:',
  'stations.detail.allocate': 'Allocate',
  'stations.detail.noAllocations': 'No allocations yet.',
  'stations.detail.stockCol': 'Stock',
  'stations.detail.ratingCol': 'Rating',
  'stations.detail.availTotalCol': 'Available / total',
  'stations.detail.out': 'Out',
  'stations.detail.removeAria': 'Remove',
  'stations.detail.allocatedToast': 'Stock allocated',
  'stations.detail.allocateFailed': 'Allocate failed',
  'stations.detail.missingData': 'Missing data',
  // PRD §9 — two-tier brand inventory: the station picker shows how many
  // units the brand still has in its pool and blocks allocations beyond it.
  'stations.detail.brandPool': 'Brand pool',
  'stations.detail.exceedsBrandPool': 'Exceeds brand pool',
  // PRD §9 v2 — Event Prize Pool picker (eligible brand prizes for an event).
  'eventPrizePool.title': 'Eligible prizes for this event',
  'eventPrizePool.subtitle':
    "Pick which of the brand's prizes can be allocated to this event's stations. Leave empty to allow all brand prizes (back-compat).",
  'eventPrizePool.brandEmpty':
    'The brand has no adopted prizes yet. Open Brand Studio → Prizes to adopt from the agency catalog.',
  'eventPrizePool.noPoolHint':
    'No prizes selected yet — stations will see the full brand catalog.',
  'eventPrizePool.unitsRemain': 'units',
  'eventPrizePool.savedToast': 'Event prize pool updated',
  'eventPrizePool.saveFailed': 'Failed to update prize pool',
  // ── Station Detail v2 (header + tabs) ────────────────────────────
  'stations.detail.inventoryTab': 'Inventory',
  'stations.detail.responsesTab': 'Interactions',
  'stations.detail.dashboardTab': 'Dashboard',
  'stations.detail.usedCol': 'Used',
  'stations.detail.totalAllocated': 'Total',
  'stations.detail.totalUsed': 'Used',
  'stations.detail.totalRemaining': 'Remaining',
  'stations.detail.noSurveyAssigned': 'No journey assigned to this station yet.',
  'stations.detail.responsesOverTime': 'Interactions over time',
  'stations.detail.stockDistribution': 'Stock won by item',
  'stations.detail.inventoryDonut': 'Inventory used vs remaining',
  'stations.detail.topAnswers': 'Top answers',
  'stations.detail.dashboardEmpty': 'Not enough data to draw this chart yet — come back once respondents start submitting.',
  'stations.detail.responseTimestamp': 'When',
  'stations.detail.responseSummary': 'Preview',
  'stations.detail.prevPage': 'Previous page',
  'stations.detail.nextPage': 'Next page',
  'stations.editDialog.title': 'Edit station',
  'stations.editDialog.description': 'Rename or re-attach the journey. Allocations stay intact.',
  'stations.editDialog.stationName': 'Station name',
  'stations.editDialog.nameRequired': 'Station name is required',
  'stations.editDialog.survey': 'Journey',
  'stations.editDialog.surveyDisabledTitle': "The journey is set on the event's Design Journey tab. Open the event to change it.",
  'stations.editDialog.noSurvey': '— no journey —',
  'stations.editDialog.template': 'template',
  'stations.editDialog.global': 'global',
  'stations.editDialog.surveyHintPre': "Set on the event's",
  'stations.editDialog.surveyHintLink': 'Design Journey',
  'stations.editDialog.surveyHintPost': 'tab — applies to all stations of this event.',
  'stations.editDialog.confirmHeader': 'Delete station',
  'stations.editDialog.confirmBody': 'The station and its stock allocations will be hidden from listings.',
  'stations.editDialog.confirmInstruction': 'Click',
  'stations.editDialog.confirmInstructionEmph': 'Confirm delete',
  'stations.editDialog.confirmInstructionEnd': 'to proceed.',
  'stations.editDialog.deleting': 'Deleting…',
  'stations.editDialog.confirmDelete': 'Confirm delete',
  'stations.editDialog.delete': 'Delete',
  'stations.editDialog.cancel': 'Cancel',
  'stations.editDialog.save': 'Save',
  'stations.editDialog.updatedToast': 'Station updated',
  'stations.editDialog.updateFailed': 'Update failed',
  'stations.editDialog.deletedToast': 'Station deleted',
  'stations.editDialog.deleteFailed': 'Delete failed',
  'stations.qrPrint.noSurvey': 'This station has no journey wired yet. Assign one in Journeys → Builder.',
  'stations.qrPrint.back': 'Back',
  'stations.qrPrint.title': 'QR code',
  'stations.qrPrint.shortUrl': 'Short URL',
  'stations.qrPrint.sharedNote': 'This station shares a QR with others — the code below opens the shared station picker. Scanning it lets the consumer choose their station first.',
  'stations.qrPrint.fullUrl': 'Full URL',
  'stations.qrPrint.downloadSvg': 'Download SVG',
  'stations.qrPrint.print': 'Print',
  'stations.qrPrint.openNewTab': 'Open in new tab',

  // ── Events ─────────────────────────────────────────────────────────
  'events.detail.startDate': 'Start',
  'events.detail.endDate': 'End',
  'events.detail.address': 'Address',
  'events.detail.codeRef': 'Reference code',
  'events.detail.sectionCount': 'Sections',
  'events.detail.stations': 'Stations',
  'events.detail.noStations': 'No stations yet for this event.',
  'events.detail.addStation': 'Add station',
  'events.detail.surveyConfig': 'Journey configuration',
  'events.detail.savedTitle': 'Event saved',
  'events.detail.deletedTitle': 'Event deleted',
  'events.detail.confirmDelete': 'Delete this event?',
  'events.dialog.editTitle': 'Edit event',
  'events.dialog.name': 'Event name',
  'events.dialog.address': 'Address',
  'events.dialog.startDate': 'Start date',
  'events.dialog.endDate': 'End date',
  'events.dialog.sectionCount': 'Section count',
  'events.dialog.codeRef': 'Reference code',
  'events.dialog.status': 'Status',
  'events.survey.title': 'Journey configuration',
  'events.survey.subtitle': 'Pick a template or build a custom flow for this event.',
  'events.survey.outputs': 'Outputs',
  'events.survey.preview': 'Preview',
  'events.survey.savedTitle': 'Configuration saved',
  'events.survey.endpoint': 'Endpoint URL',
  'events.survey.endpointPlaceholder': 'https://hooks.example.com/onxite',
  'events.survey.translatable': 'Translatable',
  'events.survey.translatableHint': 'Show language toggle to respondents.',
  'events.survey.locked': 'Lock template',
  'events.survey.lockedHint': 'Prevents accidental edits to the journey body.',
  'events.survey.preview.empty': 'Add steps and components to see a live preview.',
  'events.detail.notFound': 'Event not found',
  'events.detail.backToProject': 'Back to project',
  'events.detail.unknown': 'Unknown',
  'events.detail.tbd': 'TBD',
  'events.detail.sectionOne': 'section',
  'events.detail.sectionMany': 'sections',
  'events.detail.editEvent': 'Edit event',
  'events.detail.viewReport': 'View report',
  // Caso 10 — Promote live monitor to the event header so on-site staff
  // reach it in one click.
  'events.detail.openLiveMonitor': 'Live monitor',
  'events.detail.liveTitle': 'Live monitoring',
  'events.detail.reportTitle': 'Event report',
  // Caso 12 — Event-goal card.
  'goals.title': 'Targets & progress',
  'goals.description':
    'Set per-event targets. Progress is computed from the same metrics as the event report.',
  'goals.empty': 'No targets set yet. Pick a metric below to start.',
  'goals.targetPlaceholder': 'Target',
  'goals.metric.samples': 'Samples',
  'goals.metric.responses': 'Interactions',
  'goals.metric.leads': 'Leads',
  'goals.metric.prizesDelivered': 'Prizes delivered',
  'goals.metric.completionRate': 'Completion rate',
  'goals.status.on-track': 'On track',
  'goals.status.at-risk': 'At risk',
  'goals.status.met': 'Met',
  // Caso 11 — Booth-operator's mobile-first station view.
  'operator.title': 'Operator mode',
  'operator.back': 'Back to stations',
  'operator.invalidId': 'Invalid station id.',
  'operator.unknownEvent': 'Unknown event',
  'operator.surveyActive': 'Active journey: {name}',
  'operator.noSurvey': 'No journey assigned yet.',
  'operator.cta.redeem': 'Redeem',
  'operator.cta.showQr': 'Show QR',
  'operator.stock.title': 'Prizes left',
  'operator.stock.totalRemaining': '{count} total',
  'operator.stock.left': 'left',
  'operator.stock.empty': 'No prizes allocated to this station.',
  'operator.stock.lowAlert':
    '{count} prize(s) running low — flag to your coordinator.',
  'stations.detail.operatorMode': 'Operator mode',
  // Caso 11 v3 — Operator access tokens.
  'stations.detail.generateOperatorLink': 'Operator link',
  'stations.detail.tokenReady': 'Operator link ready',
  'stations.detail.tokenWarning':
    "This URL grants access to the operator view. Copy it now — we won't show it again.",
  'stations.detail.tokenExpiresAt': 'Expires {when}',
  'stations.detail.tokenCopy': 'Copy URL',
  'stations.detail.tokenCopied': 'Copied to clipboard.',
  'stations.detail.tokenCopyFailed': "Couldn't copy. Select the URL manually.",
  'stations.detail.tokenMintFailed': "Couldn't generate the operator link.",
  // Caso 11 v4 — operator login (token exchange) UX.
  'operatorLogin.title': 'Signing you in…',
  'operatorLogin.subtitle': "Exchanging your link for an operator session.",
  'operatorLogin.errorTitle': "Couldn't sign you in",
  'operatorLogin.missingToken': 'This link is missing the access token.',
  'operatorLogin.exchangeFailed':
    'The link is invalid, revoked, or expired. Ask the manager for a new one.',
  // Caso 16 — Breadcrumb labels.
  'breadcrumbs.brand': 'Brand',
  'breadcrumbs.project': 'Project',
  'breadcrumbs.event': 'Event',
  'breadcrumbs.settings': 'Settings',

  // ── Caso 09 — Demographic aggregation panel ────────────────────
  'demographics.title': 'Demographics',
  'demographics.subtitle':
    '{count} contacts with demographic data on file.',
  'demographics.ageRange': 'Age range',
  'demographics.gender': 'Gender',
  'demographics.ethnicity': 'Ethnicity',
  'demographics.city': 'Top cities',
  'demographics.state': 'Top states',
  'demographics.language': 'Top languages',
  'demographics.genderValue.female': 'Female',
  'demographics.genderValue.male': 'Male',
  'demographics.genderValue.non-binary': 'Non-binary',
  'demographics.genderValue.other': 'Other',
  'demographics.genderValue.undisclosed': 'Undisclosed',
  'demographics.brand.noEvents':
    'This brand has no events yet — demographics will appear once you run an event with a journey that collects age, gender or location.',
  'demographics.brand.noData':
    'Scanned {count} event(s); none have demographic interactions yet. Make sure the brand\'s journeys collect age, gender or location.',
  // Caso 10 — Live monitor.
  'liveMonitor.invalidId': 'Invalid event id.',
  'liveMonitor.back': 'Back to report',
  'liveMonitor.live': 'LIVE',
  'liveMonitor.refreshing': 'Refreshing…',
  // Caso 10 v2 — transport indicator (WS push vs polling fallback).
  'liveMonitor.transport.live': 'Realtime',
  'liveMonitor.transport.polling': 'Polling',
  'liveMonitor.unknown': 'Event',
  'liveMonitor.kpi.interactions': 'Interactions',
  'liveMonitor.kpi.last24h': 'Last 24 h',
  'liveMonitor.kpi.leads': 'Leads',
  'liveMonitor.kpi.prizesDistributed': 'Prizes delivered',
  'liveMonitor.alert.lowStock': 'Only {count} prize(s) left across stations.',
  'eventReport.openLiveMonitor': 'Open live monitor',
  'events.detail.stationsTab': 'Stations',
  'events.detail.surveyTab': 'Design Journey',
  'events.detail.touchpoints': 'Stations / Touchpoints',
  'events.detail.eventSurvey': 'Event journey:',
  'events.detail.sharedByStations': '· shared by all stations',
  'events.detail.noEventSurveyPre': 'No event journey yet — pick a template in the',
  'events.detail.noEventSurveyTab': 'Design Journey',
  'events.detail.noEventSurveyPost': 'tab.',
  'events.detail.newStation': 'New station',
  'events.detail.stationName': 'Station name',
  'events.detail.stationNamePlaceholder': 'Main Bar',
  'events.detail.surveyAutoPre': 'auto-assigned from this event\'s Design Journey (set in the',
  'events.detail.surveyAutoTab': 'Design Journey',
  'events.detail.surveyAutoPost': 'tab → pick a template). All stations of an event share the same journey.',
  'events.detail.surveyLabel': 'Journey:',
  'events.detail.createStation': 'Create station',
  'events.detail.stationCreated': 'Station created',
  'events.detail.createFailed': 'Create failed',
  'events.detail.surveyNone': 'none',
  'events.detail.outOfSync': 'out of sync',
  'events.detail.noSurvey': 'no journey',
  'events.detail.outOfSyncTitle': 'Out of sync — event journey is',
  'events.detail.outOfSyncBody': 'Use "Re-sync stations" in Design Journey.',
  'events.detail.noSurveyTitle': 'No journey assigned. Use "Re-sync stations" in Design Journey.',
  'events.detail.editStationAria': 'Edit station',
  'events.detail.editBtn': 'Edit',
  'events.detail.allocateStock': 'View',
  'events.detail.qrShort': 'QR',
  'events.editDialog.title': 'Edit event',
  'events.editDialog.description': 'Tune the schedule, address, status, and capacity for this event.',
  'events.editDialog.name': 'Event name',
  'events.editDialog.address': 'Address',
  'events.editDialog.starts': 'Starts',
  'events.editDialog.ends': 'Ends',
  'events.editDialog.sections': 'Sections',
  'events.editDialog.codeRef': 'Code ref',
  'events.editDialog.statusField': 'Status',
  'events.editDialog.confirmHeader': 'Delete',
  'events.editDialog.confirmBody': 'The event, its stations and stock allocations will be hidden from all listings.',
  'events.editDialog.confirmInstruction': 'Click',
  'events.editDialog.confirmInstructionEmph': 'Confirm delete',
  'events.editDialog.confirmInstructionEnd': 'to proceed.',
  'events.editDialog.deleting': 'Deleting…',
  'events.editDialog.confirmDelete': 'Confirm delete',
  'events.editDialog.delete': 'Delete',
  'events.editDialog.cancel': 'Cancel',
  'events.editDialog.save': 'Save',
  'events.editDialog.updatedToast': 'Event updated',
  'events.editDialog.updateFailed': 'Update failed',
  'events.editDialog.deletedToast': 'Event deleted',
  'events.editDialog.deleteFailed': 'Delete failed',
  'events.editDialog.error.nameRequired': 'Event name is required',
  'events.editDialog.error.startRequired': 'Start date and time is required',
  'events.editDialog.error.startInvalid': 'Invalid start date',
  'events.editDialog.error.endRequired': 'End date and time is required',
  'events.editDialog.error.endInvalid': 'Invalid end date',
  'events.editDialog.error.endAfterStart': 'End must be after start',
  'events.editDialog.error.outOfRange': 'Dates must fall within the project date range',
  'surveyConfig.title': 'Design Journey',
  'surveyConfig.pickTemplateSubtitle': "Pick a xurvey template to start configuring this event. We'll clone it and snapshot your brand identity into the event so changes don't affect the brand.",
  'surveyConfig.noTemplates': 'No xotski templates available yet. Ask your admin to publish one in the ox-journey app.',
  'surveyConfig.stepsLabel': 'step(s)',
  'surveyConfig.useTemplate': 'Use template',
  'surveyConfig.cloned': 'Cloned template — start configuring',
  'surveyConfig.based': 'Based on template',
  'surveyConfig.resyncStations': 'Re-sync stations',
  'surveyConfig.resyncStationsTitle': "Point every active station of this event at the event's journey",
  'surveyConfig.resetIdentity': 'Reset identity',
  'surveyConfig.resetIdentityTitle': 'Re-snapshot Identity from the brand',
  'surveyConfig.changeTemplate': 'Change template',
  'surveyConfig.changeTemplateTitle': 'Swap the xurvey template that backs this event',
  'surveyConfig.changeTemplateDialogTitle': 'Change journey template',
  'surveyConfig.changeTemplateDialogBody': 'Pick a new template. The cloned journey will be replaced and all stations of this event will be re-pointed. Identity, experience, and game overrides are preserved.',
  'surveyConfig.changeTemplateConfirm': 'Replace template',
  'surveyConfig.changeTemplateCancel': 'Cancel',
  'surveyConfig.changeTemplateSuccess': 'Template replaced',
  'surveyConfig.currentTemplateLabel': 'Current:',
  'surveyConfig.saveChanges': 'Save changes',
  'surveyConfig.saved': 'Journey configuration saved',
  'surveyConfig.tab.identity': 'Identity',
  'surveyConfig.tab.experience': 'Experience',
  'surveyConfig.tab.lookfeel': 'Look & Feel',
  'surveyConfig.tab.game': 'Game',
  'surveyConfig.tab.prizes': 'Prizes',
  'surveyConfig.tab.output': 'Output',
  'surveyConfig.confirmResetIdentity': 'Re-sync Identity from the brand? Per-event overrides will be lost.',
  'surveyConfig.identitySynced': 'Identity re-synced from brand',
  'surveyConfig.confirmSyncStations': "Point ALL active stations of this event at the event's journey? Any station that was using a different journey will be overwritten.",
  'surveyConfig.stationsInSync': 'Stations already in sync',
  'surveyConfig.resyncedPrefix': 'Re-synced',
  'surveyConfig.stationOne': 'station',
  'surveyConfig.stationMany': 'stations',
  'surveyConfig.identity.campaignName': 'Campaign name',
  'surveyConfig.identity.campaignPlaceholder': 'Felix Pago 2026',
  'surveyConfig.identity.industry': 'Industry',
  'surveyConfig.identity.industryPlaceholder': 'Beverages, sports, telco…',
  'surveyConfig.identity.font': 'Font',
  'surveyConfig.identity.primaryColor': 'Primary color',
  'surveyConfig.identity.secondaryColor': 'Secondary color',
  'surveyConfig.identity.accentColor': 'Accent color',
  'surveyConfig.identity.backgroundColor': 'Background color',
  'surveyConfig.identity.textColor': 'Text color',
  'surveyConfig.identity.brandLogo': 'Brand logo URL',
  'surveyConfig.identity.clientLogo': 'Client logo URL',
  'surveyConfig.identity.qrThumbnail': 'QR thumbnail URL',
  'surveyConfig.identity.qrPlaceholder': 'https://… (square)',
  'surveyConfig.identity.backgroundImage': 'Background image URL',
  'surveyConfig.exp.language': 'Language',
  'surveyConfig.exp.english': 'English',
  'surveyConfig.exp.englishSpanish': 'English + Spanish',
  'surveyConfig.exp.ageGate': 'Age gate',
  'surveyConfig.exp.ageNone': 'None',
  'surveyConfig.exp.collectName': 'Collect name',
  'surveyConfig.exp.collectEmail': 'Collect email',
  'surveyConfig.exp.collectPhone': 'Collect phone',
  'surveyConfig.exp.demographics': 'Demographics',
  'surveyConfig.exp.age': 'Age',
  'surveyConfig.exp.gender': 'Gender',
  'surveyConfig.exp.ethnicity': 'Ethnicity',
  'surveyConfig.exp.nationality': 'Nationality',
  'surveyConfig.look.buttonStyle': 'Button style',
  'surveyConfig.look.celebration': 'Celebration theme',
  'surveyConfig.look.gameCtaEn': 'Game CTA (EN)',
  'surveyConfig.look.gameCtaEnPlaceholder': 'Spin to win!',
  'surveyConfig.look.gameCtaEs': 'Game CTA (ES)',
  'surveyConfig.look.gameCtaEsPlaceholder': '¡Gira para ganar!',
  'surveyConfig.look.consent': 'Consent wording',
  'surveyConfig.look.consentPlaceholder': 'By continuing, you agree to our Terms…',
  'surveyConfig.game.mechanic': 'Game mechanic',
  'surveyConfig.game.tier': 'Tier',
  'surveyConfig.game.taglineEn': 'Wheel tagline (EN)',
  'surveyConfig.game.taglineEs': 'Wheel tagline (ES)',
  'surveyConfig.game.heroImage': 'Wheel hero image URL',
  'surveyConfig.game.heroPlaceholder': 'https://… (shown on the wheel screen)',
  'surveyConfig.game.none': 'None',
  'surveyConfig.prizes.empty': 'No prize stock allocated yet. Open a Station → "Allocate stock" to add items, then come back here to enable / rate them.',
  'surveyConfig.prizes.lead': "Stocks allocated to this event's stations. Toggle to enable/disable in the wheel; click stars to set rarity (rating). Quantity is set on the Station Detail page.",
  'surveyConfig.prizes.noImg': 'no img',
  'surveyConfig.prizes.stationLabel': 'Station',
  'surveyConfig.prizes.qty': 'qty',
  'surveyConfig.prizes.wheel': 'wheel',
  'surveyConfig.prizes.hidden': 'hidden',
  'surveyConfig.prizes.ratingTitle': 'Rating:',
  'surveyConfig.prizes.setRating': 'Set rating to',

  // ── Surveys ────────────────────────────────────────────────────────
  'surveys.title': 'Journeys',
  'surveys.subtitle': 'Reusable journey templates for your activations.',
  'surveys.newButton': 'New journey',
  'surveys.empty.title': 'No journeys yet',
  'surveys.empty.body': 'Build a journey to collect interactions at your activations.',
  'surveys.empty.cta': 'Create journey',
  'surveys.searchPlaceholder': 'Search journeys…',
  'surveys.column.name': 'Name',
  'surveys.column.steps': 'Steps',
  'surveys.column.product': 'Product',
  'surveys.column.locked': 'Locked',
  'surveys.column.status': 'Status',
  'surveys.confirmDelete': 'Delete this journey?',
  'surveys.deletedTitle': 'Journey deleted',
  'surveys.duplicatedTitle': 'Journey duplicated',
  'surveys.savedTitle': 'Journey saved',
  'surveys.builder.title': 'Design Journey',
  'surveys.builder.addStep': 'Add step',
  'surveys.builder.addComponent': 'Add component',
  'surveys.builder.removeStep': 'Remove step',
  'surveys.builder.removeComponent': 'Remove component',
  'surveys.builder.stepLabel': 'Step',
  'surveys.builder.componentLabel': 'Component',
  'surveys.builder.componentType': 'Type',
  'surveys.builder.componentLabelField': 'Label',
  'surveys.builder.componentOptions': 'Options (one per line)',
  'surveys.builder.namePlaceholder': 'Journey name',
  'surveys.builder.descriptionPlaceholder': 'Short description',
  'surveys.builder.savedTitle': 'Journey saved',
  'surveys.builder.preview': 'Preview',
  'surveys.builder.empty': 'No steps yet — add the first step to get started.',
  'surveys.preview.title': 'Journey preview',
  'surveys.preview.exit': 'Exit preview',
  'surveys.preview.notFound': 'Journey not found.',
  'surveys.templates.title': 'Journey templates',
  'surveys.templates.subtitle': 'Start from a proven template, then customize.',
  'surveys.templates.useTemplate': 'Use template',
  'surveys.templates.preview': 'Preview',
  'surveys.templates.empty': 'No templates available yet.',

  // ── Projects ───────────────────────────────────────────────────────
  'project.detail.kpi.events': 'Events',
  'project.detail.kpi.stations': 'Stations',
  'project.detail.kpi.responses': 'Interactions',
  'project.detail.events': 'Events',
  'project.detail.noEvents': 'No events yet in this project.',
  'project.detail.newEvent': 'New event',
  'project.detail.editProject': 'Edit project',
  'project.detail.deleteProject': 'Delete project',
  'project.detail.confirmDelete': 'Delete this project?',
  'project.detail.deletedTitle': 'Project deleted',
  'project.detail.savedTitle': 'Project saved',
  'project.detail.notFound': 'Project not found.',
  'project.detail.backToBrand': 'Back to brand',

  // ── Contacts ───────────────────────────────────────────────────────
  'contacts.title': 'Contacts',
  'contacts.subtitle': 'Journey interactions captured at your stations.',
  'contacts.empty.title': 'No interactions yet',
  'contacts.empty.body': 'Run a journey at a station and interactions will appear here.',
  'contacts.searchPlaceholder': 'Search contacts…',
  'contacts.column.name': 'Name',
  'contacts.column.email': 'Email',
  'contacts.column.phone': 'Phone',
  'contacts.column.event': 'Event',
  'contacts.column.date': 'Date',
  'contacts.column.source': 'Source',
  'contacts.exportFilename': 'contacts',

  // ── Insights ───────────────────────────────────────────────────────
  'insights.title': 'Insights',
  'insights.subtitle': 'Distribution performance across brands, events and inventory.',
  'insights.kpi.totalResponses': 'Total interactions',
  'insights.kpi.uniqueContacts': 'Unique contacts',
  'insights.kpi.activeStations': 'Active stations',
  'insights.kpi.completionRate': 'Completion rate',
  'insights.responsesByDay': 'Interactions by day',
  'insights.responsesByEvent': 'Interactions by event',
  'insights.responsesByStation': 'Interactions by station',
  'insights.topStock': 'Top xotskies',
  'insights.empty.title': 'Not enough data yet',
  'insights.empty.body': 'Once interactions start coming in, your insights will appear here.',

  // ── Settings ───────────────────────────────────────────────────────
  'settings.title': 'Workspace',
  'settings.subtitle': 'Workspace preferences and account.',
  'settings.section.account': 'Account',
  'settings.section.workspace': 'Workspace',
  'settings.section.notifications': 'Notifications',
  'settings.section.danger': 'Danger zone',
  'settings.workspace.name': 'Workspace name',
  'settings.workspace.timezone': 'Timezone',
  'settings.workspace.language': 'Default language',
  'settings.notifications.email': 'Email notifications',
  'settings.notifications.product': 'Product updates',
  'settings.danger.deleteWorkspace': 'Delete workspace',
  'settings.danger.deleteWorkspaceHint': 'This action cannot be undone.',
  'settings.savedTitle': 'Settings saved',

  // ── Access manager ─────────────────────────────────────────────────
  'access.title': 'Access manager',
  'access.subtitle': 'Invite teammates and control who can do what.',
  'access.tabs.members': 'Members',
  'access.tabs.roles': 'Roles',
  'access.tabs.grants': 'Grants',
  'access.members.invite': 'Invite member',
  'access.members.empty.title': 'No members yet',
  'access.members.empty.body': 'Invite teammates to collaborate on your activations.',
  'access.members.column.name': 'Name',
  'access.members.column.email': 'Email',
  'access.members.column.role': 'Role',
  'access.members.column.status': 'Status',
  'access.members.dialog.inviteTitle': 'Invite member',
  'access.members.dialog.editTitle': 'Edit member',
  'access.members.dialog.email': 'Email',
  'access.members.dialog.role': 'Role',
  'access.members.dialog.firstName': 'First name',
  'access.members.dialog.lastName': 'Last name',
  'access.members.savedTitle': 'Member saved',
  'access.members.removedTitle': 'Member removed',
  'access.members.confirmRemove': 'Remove this member?',
  'access.roles.new': 'New role',
  'access.roles.empty.title': 'No roles yet',
  'access.roles.empty.body': 'Create a role to bundle permissions for teammates.',
  'access.roles.column.name': 'Name',
  'access.roles.column.members': 'Members',
  'access.roles.column.permissions': 'Permissions',
  'access.roles.dialog.newTitle': 'New role',
  'access.roles.dialog.editTitle': 'Edit role',
  'access.roles.dialog.name': 'Role name',
  'access.roles.dialog.description': 'Description',
  'access.roles.dialog.permissions': 'Permissions',
  'access.roles.savedTitle': 'Role saved',
  'access.roles.deletedTitle': 'Role deleted',
  'access.roles.confirmDelete': 'Delete this role?',
  'access.grants.new': 'New grant',
  'access.grants.empty.title': 'No grants yet',
  'access.grants.empty.body': 'Grant a teammate access to a specific brand, project, or event.',
  'access.grants.column.member': 'Member',
  'access.grants.column.scope': 'Scope',
  'access.grants.column.role': 'Role',
  'access.grants.dialog.newTitle': 'New grant',
  'access.grants.dialog.editTitle': 'Edit grant',
  'access.grants.dialog.member': 'Member',
  'access.grants.dialog.scope': 'Scope',
  'access.grants.dialog.role': 'Role',
  'access.grants.savedTitle': 'Grant saved',
  'access.grants.deletedTitle': 'Grant removed',
  'access.grants.confirmDelete': 'Remove this grant?',
  'access.permissions.available': 'Available',
  'access.permissions.selected': 'Selected',
  'access.permissions.searchPlaceholder': 'Search permissions…',

  // ── Brand identity ─────────────────────────────────────────────────
  'brandIdentity.title': 'Brand identity',
  'brandIdentity.subtitle': 'Logo, colors, and voice that define your brand.',
  'brandIdentity.logo': 'Logo',
  'brandIdentity.primaryColor': 'Primary color',
  'brandIdentity.accentColor': 'Accent color',
  'brandIdentity.voice': 'Brand voice',
  'brandIdentity.voicePlaceholder': 'A few adjectives that describe how your brand speaks.',
  'brandIdentity.savedTitle': 'Brand identity saved',

  // ── Brand studio (new page) ────────────────────────────────────────
  'brandStudio.new.title': 'Create a new brand',
  'brandStudio.new.subtitle': 'Set up the essentials. You can refine everything later.',
  'brandStudio.new.brandName': 'Brand name',
  'brandStudio.new.brandNamePlaceholder': 'e.g. Atledi',
  'brandStudio.new.logo': 'Logo',
  'brandStudio.new.primaryColor': 'Primary color',
  'brandStudio.new.accentColor': 'Accent color',
  'brandStudio.new.create': 'Create brand',
  'brandStudio.new.createdTitle': 'Brand created',

  // ── Agency ─────────────────────────────────────────────────────────
  'agency.title': 'Agency profile',
  'agency.subtitle': 'How your agency appears to brands and partners.',
  'agency.section.basics': 'Basics',
  'agency.section.team': 'Team',
  'agency.section.brands': 'Brands',
  'agency.name': 'Agency name',
  'agency.tagline': 'Tagline',
  'agency.about': 'About',
  'agency.aboutPlaceholder': 'A short description of your agency.',
  'agency.logo': 'Logo',
  'agency.website': 'Website',
  'agency.savedTitle': 'Agency saved',
  'agency.team.empty': 'Invite teammates from the Access manager.',
  'agency.brands.empty': 'No brands linked to this agency yet.',
  'agency.home.title': 'Agency dashboard',
  'agency.home.subtitle': 'Track activity across all the brands you manage.',
  'agency.home.activeBrands': 'Active brands',
  'agency.home.liveEvents': 'Live events',
  'agency.home.totalResponses': 'Total interactions',
  'agency.home.teammates': 'Teammates',

  // ── Billing ────────────────────────────────────────────────────────
  'billing.title': 'Billing',
  'billing.subtitle': 'Plans, usage and invoices.',
  'billing.currentPlan': 'Current plan',
  'billing.changePlan': 'Change plan',
  'billing.upgrade': 'Upgrade',
  'billing.downgrade': 'Downgrade',
  'billing.cancelPlan': 'Cancel plan',
  'billing.invoices': 'Invoices',
  'billing.noInvoices': 'No invoices yet.',
  'billing.usageTitle': 'Usage',
  'billing.usage.responses': 'Interactions',
  'billing.usage.stations': 'Stations',
  'billing.usage.events': 'Events',
  'billing.usage.teammates': 'Teammates',
  'billing.usage.usedOf': 'used of',
  'billing.pricing.title': 'Plans',
  'billing.pricing.subtitle': 'Pick the plan that fits your activations.',
  'billing.pricing.month': '/ month',
  'billing.pricing.year': '/ year',
  'billing.pricing.choosePlan': 'Choose plan',
  'billing.pricing.currentPlan': 'Current plan',
  'billing.pricing.contactSales': 'Contact sales',
  'billing.upgradeDialog.title': 'Upgrade plan',
  'billing.upgradeDialog.confirm': 'Confirm upgrade',
  'billing.upgradeDialog.cancel': 'Cancel',
  'billing.checkout.successTitle': 'Payment successful',
  'billing.checkout.successBody': 'Your plan has been updated. Welcome aboard!',
  'billing.checkout.failedTitle': 'Payment failed',
  'billing.checkout.failedBody': 'Something went wrong while processing your payment.',
  'billing.checkout.returnToBilling': 'Return to billing',
  'billing.mock.title': 'Mock checkout',
  'billing.mock.subtitle': 'This is a sandboxed flow — no real payment will be charged.',
  'billing.mock.pay': 'Pay now',
  'billing.mock.cancel': 'Cancel',

  // ── Public survey ──────────────────────────────────────────────────
  'publicSurvey.continue': 'Continue',
  'publicSurvey.back': 'Back',
  'publicSurvey.submit': 'Submit',
  'publicSurvey.finish': 'Finish',
  'publicSurvey.thanks.title': 'Thanks for your interaction!',
  'publicSurvey.thanks.body': 'Your answers have been recorded.',
  'publicSurvey.thanks.replay': 'Take another spin',
  'publicSurvey.notFound.title': 'Journey not found',
  'publicSurvey.notFound.body': 'Double-check the link or QR code and try again.',
  'publicSurvey.required': 'This field is required.',
  'publicSurvey.invalidEmail': 'Please enter a valid email address.',
  'publicSurvey.invalidPhone': 'Please enter a valid phone number.',
  'publicSurvey.wheel.spin': 'Spin the wheel',
  'publicSurvey.wheel.spinning': 'Spinning…',
  'publicSurvey.wheel.youWon': 'You won',
  'publicSurvey.wheel.tryAgain': 'Try again',
  'publicSurvey.wheel.claim': 'Claim your prize',
  'publicSurvey.loading': 'Loading journey…',

  // ── Sidebar ────────────────────────────────────────────────────────
  'sidebar.home': 'Home',
  'sidebar.brands': 'Brands',
  'sidebar.assets': 'My Assets',
  'sidebar.photos': 'Photos',
  'sidebar.schedule': 'My Schedule',
  'sidebar.myXotskies': 'My Merch',
  'sidebar.brandInventory': 'Brand inventory',
  'sidebar.brandStudio': 'Brand studio',
  'sidebar.projects': 'Projects',
  'sidebar.insights': 'Insights',
  'sidebar.contacts': 'Contacts',
  'sidebar.redeem': 'Redeem',
  'sidebar.profile': 'Profile',
  'sidebar.accessManager': 'Access Manager',
  'sidebar.settings': 'Settings',
  'sidebar.admin': 'Admin',
  'sidebar.collapse': 'Collapse sidebar',
  'sidebar.expand': 'Expand sidebar',
  'sidebar.profileMenu.workspace': 'Workspace',
  'sidebar.profileMenu.workspaceSublabel': 'Plan · seats · brands · billing',
  'sidebar.profileMenu.plan': 'Plan',
  'sidebar.profileMenu.planSublabel': 'Seats · brands · billing',
  'sidebar.profileMenu.language': 'Language',
  'sidebar.profileMenu.languagePickerSoon': 'Language picker coming soon',
  'sidebar.profileMenu.openMenu': 'open menu',
  'sidebar.favorites': 'Favorites',
  'sidebar.workspace': 'Workspace',
  'sidebar.notifications': 'Notifications',
  'sidebar.billing': 'Billing',
  'sidebar.followup': 'Follow-up',
  'sidebar.integrations': 'Integrations',
  'sidebar.surveyBuilder': 'Design Journey',

  // ── Brand Inventory (PRD §9, two-tier inventory) ───────────────
  'brandInventory.title': 'Brand inventory',
  'brandInventory.subtitle':
    'Brand-level pool of items. Allocate into projects, return when an event closes, or write off.',
  'brandInventory.noun': 'items',
  'brandInventory.filterByState': 'Filter by state',
  'brandInventory.allStates': 'All',
  'brandInventory.empty': 'No items match these filters.',
  'brandInventory.emptyHint': 'Capture an item from the field or create one from the admin.',
  'brandInventory.emptyNotEnabled': 'Brand Inventory is not enabled',
  'brandInventory.emptyNotEnabledHint':
    'Enable it in Settings › Brand › Inventory to start tracking brand-level items.',
  'brandInventory.estimated': 'estimated',
  'brandInventory.col.name': 'Name',
  'brandInventory.col.quantity': 'Qty',
  'brandInventory.col.state': 'State',
  'brandInventory.col.rating': 'Rarity',
  'brandInventory.col.tags': 'Tags',
  'brandInventory.state.unassigned': 'Unassigned',
  'brandInventory.state.allocated': 'Allocated',
  'brandInventory.state.distributed': 'Distributed',
  'brandInventory.state.claimed': 'Claimed',
  'brandInventory.state.returned': 'Returned',
  'brandInventory.state.written_off': 'Written off',

  // ── Entity Home (PRD §7.1) ─────────────────────────────────────
  'entityHome.atledi.label': 'Atledi',
  'entityHome.atledi.youFallback': 'team',
  'entityHome.atledi.greeting.morning':
    'Good morning, {name}. Three days into a new week — what activation is next on deck?',
  'entityHome.atledi.greeting.afternoon':
    'Good afternoon, {name}. Mid-day is when capture rate climbs. Want me to check your live stations?',
  'entityHome.atledi.greeting.evening':
    'Good evening, {name}. Tomorrow’s prep window starts now. Ready to brief the next event?',
  // Brand home (simplified Entity Home)
  'brandHome.cooking': 'What’s cooking today, {name}?',
  'brandHome.addXotskies': 'Add Merch',
  'brandHome.addXotskiesHint': 'Stock prizes & merch into inventory',
  'brandHome.addEvents': 'Add events',
  'brandHome.addEventsHint': 'Schedule an activation',
  'brandHome.addProject': 'Add a Project',
  'brandHome.addProjectHint': 'Group events under a campaign',
  'brandHome.jumpBack': 'Jump back into your projects',
  'brandHome.askAtledi': 'Ask Atledi for something else…',
  'brandHome.status.planning': 'Planning',
  'brandHome.status.wrapped': 'Wrapped',
  // Event create form (right panel)
  'eventForm.project': 'Project',
  'eventForm.noProjects': 'Create a project first — events live inside a project.',
  'eventForm.name': 'Event name',
  'eventForm.namePlaceholder': 'e.g. Opening Night',
  'eventForm.address': 'Address',
  'eventForm.addressPlaceholder': 'Venue or location',
  'eventForm.starts': 'Starts',
  'eventForm.ends': 'Ends',
  'eventForm.create': 'Create event',
  'eventForm.outOfRange': 'Event dates must fall within the project date range',
  'entityHome.kpi.moments': 'Moments captured',
  'entityHome.kpi.throughput': 'Throughput',
  'entityHome.kpi.inventoryHealth': 'Inventory health',
  'entityHome.kpi.contacts': 'Contacts captured',
  'entityHome.kpi.comingSoon': 'Live insights coming soon',
  'entityHome.kpi.aboveThreshold': 'Items above low-stock threshold',
  'entityHome.kpi.totalDeduped': 'Total deduped contacts',
  'entityHome.nextEvent.label': 'My next event',
  'entityHome.nextEvent.openCta': 'Open event',
  'entityHome.nextEvent.noneTitle': 'No upcoming event scheduled',
  'entityHome.nextEvent.noneHint':
    'Create a project to schedule your first activation.',

  // ── Project Environment (PRD §7.2) ─────────────────────────────
  'project.atledi.label': 'Atledi',
  'project.atledi.emptyProject':
    "{name} doesn't have events yet. Want me to draft the first one from your brand brief?",
  'project.atledi.liveActivity':
    '{name} is live right now across {live} event(s). Watch capture rate against expected throughput.',
  'project.atledi.scheduled':
    '{name} has {total} event(s) on the calendar. Lock the brief 6 days out to hit the season window.',
  'project.panels.nextEvent.label': 'Next event',
  'project.panels.nextEvent.tbd': 'Date TBD',
  'project.panels.nextEvent.empty': 'No upcoming event scheduled.',
  'project.panels.nextEvent.open': 'Open event',
  'project.panels.myXotskies.label': 'My Merch',
  'project.panels.myXotskies.itemsTotal': 'items total',
  'project.panels.myXotskies.lowStock': 'low',
  'project.panels.myXotskies.open': 'Open stock',
  'project.panels.reporters.label': 'Reporters',
  'project.panels.reporters.comingSoon':
    'Reporter View ships in Phase 2 — magic-link onboarding for field staff.',
  'project.capacity.label': 'Capacity check',
  'project.capacity.green': 'On track',
  'project.capacity.yellow': 'Tight',
  'project.capacity.red': 'Under-provisioned',
  'project.capacity.pendingShort': 'Pending',
  'project.capacity.pending':
    "We don't have a historical capture rate for this project yet. Run a manual check or wait for Atledi to learn after the first event.",
  'project.capacity.summary.green':
    'Inventory {inventory} ≥ expected {expected} × 1.15 buffer. You are well-provisioned.',
  'project.capacity.summary.yellow':
    'Inventory {inventory} is between expected {expected} and the 15% buffer. Consider topping up.',
  'project.capacity.summary.red':
    'Inventory {inventory} is below expected captures {expected}. Provision more or add a BA.',

  // ── Caso 15 v2 — Top-of-shell plan alert banner ────────────────
  'plan.banner.viewPlans': 'View plans',
  'plan.banner.upgradeNow': 'Upgrade now',
  'plan.banner.expiringSoon': 'Your plan is about to expire.',
  'plan.banner.limitNear':
    "You're approaching one of your plan limits.",
  'plan.banner.limitCritical':
    "You're about to run out — upgrade now to keep your event running.",
  'plan.banner.expired':
    'Your plan has expired — your account is now on Free.',
  'plan.banner.day': 'day',
  'plan.banner.days': 'days',
  'sidebar.copyright.product': 'A product of',

  // ── Mobile bottom navigation ─────────────────────────────────────
  'mobileNav.label': 'Primary navigation',
  'mobileNav.more': 'More',
  'mobileNav.close': 'Close menu',

  // ── Help Center (chrome only — entries live in helpContent.ts) ───
  'help.title': 'Help Center',
  'help.subtitle': 'Browse what every screen does, the data it shows, and what it is for. Use search or jump to a category.',
  'help.searchPlaceholder': 'Search topics, keywords…',
  'help.whatItDoes': 'What it does',
  'help.whatData': 'What data it shows',
  'help.whatFor': 'What it is for',
  'help.related': 'Related',
  'help.goToPage': 'Go to page',
  'help.noResults': 'No topics match your search.',
  'help.resultsCount': '{count} results',
  'help.hintAria': 'Open help for this page',

  // ── Caso 13 — Outbound CRM integrations ──────────────────────────
  'integrations.title': 'Integrations',
  'integrations.subtitle':
    'Send each new contact to your CRM or any webhook in real time.',
  'integrations.newWebhook': 'New webhook',
  'integrations.empty':
    'No integrations yet. Add a webhook to start syncing contacts.',
  'integrations.row.enabled': 'Enabled',
  'integrations.row.disabled': 'Disabled',

  'integrations.stats.title': 'Sync status',
  'integrations.stats.subtitle':
    'Counts across the outbound queue for this workspace.',
  'integrations.state.PENDING': 'Pending',
  'integrations.state.SENT': 'Sent',
  'integrations.state.FAILED': 'Failed',
  'integrations.state.SKIPPED': 'Skipped',
  'integrations.dispatch.run': 'Run dispatch now',
  'integrations.dispatch.running': 'Dispatching…',
  'integrations.dispatch.lastRun': 'Last run',

  'integrations.oauth.title': 'HubSpot · Mailchimp',
  'integrations.oauth.subtitle':
    'OAuth-based connectors will be enabled once the Onxite apps are registered with each provider.',
  'integrations.oauth.comingSoon': 'Coming soon',

  'integrations.list.title': 'Configured integrations',
  'integrations.list.subtitle':
    'Each row fires a signed POST after every new contact captured.',

  'integrations.editor.newTitle': 'New webhook',
  'integrations.editor.editTitle': 'Edit webhook',
  'integrations.editor.subtitle':
    'Onxite signs every payload with HMAC-SHA256. Verify the X-Onxite-Signature header on your endpoint.',
  'integrations.editor.name': 'Name',
  'integrations.editor.namePlaceholder': 'e.g. Sales CRM webhook',
  'integrations.editor.url': 'Webhook URL',
  'integrations.editor.secret': 'Shared secret',
  'integrations.editor.secretPlaceholder': 'Optional — used to sign payloads',
  'integrations.editor.secretHelp':
    'When set, every request adds X-Onxite-Signature: sha256=<hex>.',
  'integrations.editor.consentOnly':
    'Only send contacts with marketing consent',
  'integrations.editor.enabled': 'Enabled',
  // Caso 13 v2 — multi-provider editor.
  'integrations.new.webhook': 'New webhook',
  'integrations.new.hubspot': 'New HubSpot',
  'integrations.new.mailchimp': 'New Mailchimp',
  'integrations.editor.webhookHelp':
    'Onxite signs every payload with HMAC-SHA256. Verify the X-Onxite-Signature header on your endpoint.',
  'integrations.editor.hubspotHelp':
    'Create a Private App in HubSpot with crm.objects.contacts.write scope and paste its access token below.',
  'integrations.editor.mailchimpHelp':
    'Paste a Mailchimp API key and the target Audience ID. Contacts are upserted as audience members.',
  'integrations.editor.secretSaved': '•••••••• (saved — leave blank to keep)',
  'integrations.editor.hubspotAccessToken': 'Private App access token',
  'integrations.editor.hubspotAccessTokenHelp':
    'HubSpot → Settings → Integrations → Private Apps. Stored encrypted.',
  'integrations.editor.mailchimpApiKey': 'API key',
  'integrations.editor.mailchimpApiKeyHelp':
    'Mailchimp → Account → Extras → API keys. The datacenter suffix (-us21) is required. Stored encrypted.',
  'integrations.editor.mailchimpAudienceId': 'Audience ID',
  'integrations.editor.mailchimpAudienceIdHelp':
    'Mailchimp → Audience → Settings → Audience name and defaults.',

  // ── Caso 08 — Embeddable survey builder (host page) ───────────────
  'surveyBuilder.title': 'Design Journey',
  'surveyBuilder.subtitle':
    'Design journeys in-app. Powered by @onxite/ox-journey-builder.',
  'surveyBuilder.seed.step1Title': 'Welcome',
  'surveyBuilder.seed.q1Label': 'What is your full name?',
  'surveyBuilder.subtitle.persisted':
    'Editing a saved journey. Changes are written transactionally to the server.',
  'surveyBuilder.toast.saved': 'Journey saved.',
  'surveyBuilder.toast.saveFailed': "Couldn't save the journey.",
  'surveyBuilder.toast.draftOnly':
    'Draft mode — open an existing journey at /surveys/builder/:id to save.',
  'surveyBuilder.toast.created': 'Journey created.',
  'surveyBuilder.toast.createFailed': "Couldn't create the journey.",
  'surveyBuilder.create.title': 'Create a new journey',
  'surveyBuilder.create.subtitle':
    'Give it a name to get started — you can refine everything after.',
  'surveyBuilder.create.nameLabel': 'Journey name',
  'surveyBuilder.create.namePlaceholder': 'e.g. Brooklyn Block Party journey',
  'surveyBuilder.create.submit': 'Create and open editor',
  // Caso 08 — workspace: list / import / export / clone / template.
  'surveyBuilder.action.export': 'Export JSON',
  'surveyBuilder.action.import': 'Import JSON',
  'surveyBuilder.action.clone': 'Clone',
  'surveyBuilder.action.markTemplate': 'Save as template',
  'surveyBuilder.action.unmarkTemplate': 'Unmark template',
  'surveyBuilder.templateBadge': 'Template',
  'surveyBuilder.list.title': 'Your journeys',
  'surveyBuilder.list.subtitle':
    'Open one to edit, or clone it to branch off without touching the original.',
  'surveyBuilder.list.empty': 'No journeys yet — create or import one above.',
  'surveyBuilder.toast.cloned': 'Journey cloned.',
  'surveyBuilder.toast.cloneFailed': "Couldn't clone the journey.",
  'surveyBuilder.action.disable': 'Disable',
  'surveyBuilder.action.enable': 'Enable',
  'surveyBuilder.toast.disabled': 'Journey disabled.',
  'surveyBuilder.toast.enabled': 'Journey enabled.',
  'surveyBuilder.toast.statusFailed': "Couldn't update the journey status.",
  'surveyBuilder.toast.deleted': 'Journey deleted.',
  'surveyBuilder.toast.deleteFailed': "Couldn't delete the journey.",
  'surveyBuilder.confirm.deleteTitle': 'Delete this journey?',
  'surveyBuilder.confirm.delete':
    'Delete "{name}"? This soft-deletes the journey; an admin can restore it from the DB.',
  'surveyBuilder.toast.exported': 'Journey exported as JSON.',
  'surveyBuilder.toast.imported': 'Journey imported.',
  'surveyBuilder.toast.importInvalid':
    "That file isn't a valid journey JSON.",
  'surveyBuilder.toast.markedTemplate':
    'Saved as a template — your workspace can now reuse it in events.',
  'surveyBuilder.toast.unmarkedTemplate': 'No longer a template.',
  'surveyBuilder.toast.templateFailed': "Couldn't update template status.",

  // ── Stock bulk import ──────────────────────────────────────────────
  'stock.list.importButton': 'Import',
  'stock.import.title': 'Bulk import stock',
  'stock.import.subtitle': 'Upload a ZIP with stock.xlsx and an images/ folder.',
  'stock.import.dropzone': 'Drop a ZIP file here or click to select',
  'stock.import.dropzoneHint': 'Accepts .zip (max 50 MB)',
  'stock.import.downloadTemplate': 'Download template',
  'stock.import.parsing': 'Reading ZIP…',
  'stock.import.parseFailed': 'Could not read this ZIP',
  'stock.import.preview': 'Preview',
  'stock.import.previewCount': '{count} rows · {errors} errors',
  'stock.import.col.image': 'Image',
  'stock.import.col.name': 'Name',
  'stock.import.col.classification': 'Class',
  'stock.import.col.rating': 'Rating',
  'stock.import.col.qty': 'Qty',
  'stock.import.col.status': 'Status',
  'stock.import.status.new': 'New',
  'stock.import.status.duplicate': 'Already exists',
  'stock.import.status.error': 'Error',
  'stock.import.action.replace': 'Replace existing',
  'stock.import.action.createNew': 'Add as new',
  'stock.import.execute': 'Import {count} items',
  'stock.import.cancel': 'Cancel',
  'stock.import.progress.upload': 'Uploading images',
  'stock.import.progress.create': 'Creating stock',
  'stock.import.progress.replace': 'Updating existing',
  'stock.import.summarySuccess': '{created} created · {updated} updated',
  'stock.import.summaryMixed': '{created} created · {updated} updated · {failed} failed',
  'stock.import.summaryAllFailed': 'Import failed for all {count} rows',
  'stock.import.done': 'Done',
  'stock.import.error.noXlsx': 'No .xlsx file found in the ZIP',
  'stock.import.error.multipleXlsx': 'More than one .xlsx file found — keep only stock.xlsx',
  'stock.import.error.missingNameCol': 'Missing required column: Name',
  'stock.import.error.emptySheet': 'The xlsx has no data rows',
  'stock.import.error.nameRequired': 'Name is required',
  'stock.import.error.nameTooShort': 'Name must be at least 2 characters',
  'stock.import.error.ratingRange': 'Rating must be between 1 and 5',
  'stock.import.error.classificationInvalid': 'Classification must be SEE, ENGAGE, TRY or ACT',
  'stock.import.error.imageNotFound': 'Image "{file}" not found in images/ folder',
  'stock.import.error.logoNotFound': 'Logo "{file}" not found in images/ folder',
  'stock.import.error.qtyNotInt': 'Initial qty must be a whole number',
  'stock.import.error.zipTooLarge': 'ZIP file is larger than 50 MB',
  'stock.import.error.notAZip': 'File must be a .zip',

  // ── Favorites ──────────────────────────────────────────────────────
  'favorites.added': 'Added to favorites',
  'favorites.removed': 'Removed from favorites',
  'favorites.addTitle': 'Add to favorites',
  'favorites.removeTitle': 'Remove from favorites',

  // ── Notifications ──────────────────────────────────────────────────
  'notifications.banner.markRead': 'Mark as read',
  'notifications.banner.moreCount': 'more',
  'notifications.banner.systemSender': 'System',
  'notifications.bell.title': 'Notifications',
  'notifications.bell.empty': 'No notifications yet.',
  'notifications.bell.allCaughtUp': 'All caught up',
  'notifications.bell.unreadCount': '{{count}} unread',
  'notifications.compose.pageTitle': 'Send a notification',
  'notifications.compose.pageSubtitle': 'Write a note and pick the teammates who should see it. They will also get a push notification.',
  'notifications.compose.messageTitle': 'Message',
  'notifications.compose.messageDescription': 'Title shown in the push and as the banner headline.',
  'notifications.compose.titleLabel': 'Title',
  'notifications.compose.titlePlaceholder': 'A short headline…',
  'notifications.compose.bodyLabel': 'Body',
  'notifications.compose.bodyPlaceholder': 'Add detail. Emoji and line breaks work 🎉',
  'notifications.compose.emailCopy': 'Also send by email',
  'notifications.compose.send': 'Send ({{count}})',
  'notifications.compose.recipientsTitle': 'Recipients',
  'notifications.compose.recipientsDescription': 'Teammates within your company tree.',
  'notifications.compose.searchPlaceholder': 'Search by name or email…',
  'notifications.compose.available': 'available',
  'notifications.compose.noCandidates': 'No teammates available to notify.',
  'notifications.compose.titleRequired': 'Title is required',
  'notifications.compose.bodyRequired': 'Body is required',
  'notifications.compose.recipientsRequired': 'Pick at least one recipient',
  'notifications.toast.sent': 'Notification sent to {{count}} teammate(s)',
  'notifications.toast.sendFailed': 'Failed to send notification',
  'notifications.push.disabledTitle': 'Push notifications disabled',
  'notifications.push.insecureContextBody':
    "This dev URL isn't HTTPS or localhost, so the browser blocks the service worker that delivers push. Open the app over HTTPS or via http://localhost to enable it.",
  'notifications.push.permissionDeniedBody':
    'You blocked notifications for this site. Re-enable them from the browser settings (lock icon → Notifications) and reload.',

  // ── Auth · Login ───────────────────────────────────────────────────
  'auth.login.welcomeBack': 'Welcome back',
  'auth.login.subtitle': "Sign in to manage your campaigns. New here? We'll create your account automatically.",
  'auth.login.modeOtp': 'Email code',
  'auth.login.modePassword': 'Password',
  'auth.login.emailLabel': 'Email address',
  'auth.login.email': 'Email',
  'auth.login.password': 'Password',
  'auth.login.continueWithEmail': 'Continue with email',
  'auth.login.signIn': 'Sign in',
  'auth.login.forgotPassword': 'Forgot password?',
  'auth.forgot.title': 'Recover your password',
  'auth.forgot.subtitle': "Enter your email and we'll send a 6-digit code to your recovery email.",
  'auth.forgot.submit': 'Send recovery code',
  'auth.forgot.sentTitle': 'Code sent',
  'auth.forgot.sentBody': 'Check your inbox for the 6-digit code.',
  'auth.forgot.failed': 'Could not send recovery code',
  'auth.forgot.backToLogin': 'Back to sign in',
  'auth.reset.title': 'Set a new password',
  'auth.reset.subtitleFor': 'Enter the 6-digit code sent to {email} and your new password.',
  'auth.reset.subtitleNoEmail': 'Missing email. Request a new recovery code.',
  'auth.reset.requestNew': 'Request a new code',
  'auth.reset.otpLabel': 'Recovery code',
  'auth.reset.newPasswordLabel': 'New password',
  'auth.reset.confirmPasswordLabel': 'Confirm new password',
  'auth.reset.submit': 'Update password',
  'auth.reset.successTitle': 'Password updated',
  'auth.reset.successBody': 'You can now sign in with your new password.',
  'auth.reset.failed': 'Could not reset password',
  'auth.reset.errOtpLength': 'Code must be 6 digits.',
  'auth.reset.errPasswordShort': 'Password must be at least 8 characters.',
  'auth.reset.errMismatch': 'Passwords do not match.',
  'auth.login.demoTipLabel': 'Demo tip:',
  'auth.login.demoTipBody': 'any email works. The OTP is always',
  'auth.login.curious': 'Just curious?',
  'auth.login.seePlans': 'See plans',
  'auth.login.newHere': 'New here?',
  'auth.login.createAccount': 'Create your account',
  'auth.login.heroTitle': 'Measurement-ready event planning',
  'auth.login.heroBody': 'Plan, track and measure every sampling activation in real time. Branded surveys, instant insights, and the MENT score behind every event.',
  'auth.login.orContinueWith': 'or continue with',
  'auth.toast.devHint': 'Dev hint',
  'auth.toast.otpFailed': 'Could not send OTP',
  'auth.toast.invalidCredentials': 'Invalid credentials',
  'auth.toast.alreadySignedIn': 'Account already in use',
  'auth.toast.alreadySignedInBody':
    'You are already signed in from another device or browser. We notified that session of this attempt.',
  'auth.footer.copyrightPrefix': '© {year} Xamplin by ',
  'auth.footer.copyrightSuffix': '. All rights reserved.',

  // ── Auth · OTP ─────────────────────────────────────────────────────
  'otp.checkInbox': 'Check your inbox',
  'otp.subtitlePrefix': 'We sent a 6-digit code to',
  'otp.verifyCode': 'Verify code',
  'otp.didntReceive': "Didn't receive it?",
  'otp.resendIn': 'Resend in {seconds}s',
  'otp.resendNow': 'Resend now',
  'otp.toast.welcome': 'Welcome!',
  'otp.toast.signedIn': 'Signed in',
  'otp.toast.invalidExpired': 'Invalid or expired code',
  'otp.toast.newCodeSent': 'New code sent',
  'otp.toast.newCodeBody': 'It is still 123456.',

  // ── Auth · Register (3-step wizard) ────────────────────────────────
  'register.step.company': 'Brand',
  'register.step.account': 'Account',
  'register.step.verify': 'Verify',
  'register.orWithSocial': 'Sign up with',
  'register.orWithSocialHint': 'We skip the email code if you sign up with a social provider.',
  'register.orWithEmail': 'Or with email',
  'register.company.title': 'Create your workspace',
  'register.company.subtitle': "Tell us your company name — we'll set up a Free plan you can grow from.",
  'register.company.companyName': 'Brand name',
  'register.company.companyNamePlaceholder': 'Acme Inc.',
  'register.company.continue': 'Continue',
  'register.account.title': 'Your account',
  'register.account.subtitlePrefix': "You'll be the owner of",
  'register.account.firstname': 'First name',
  'register.account.lastname': 'Last name',
  'register.account.email': 'Work email',
  'register.account.password': 'Password',
  'register.account.passwordHint': 'At least 8 characters.',
  'register.account.confirmPassword': 'Confirm password',
  'register.account.continue': 'Send verification code',
  'register.verify.title': 'Verify your email',
  'register.verify.subtitlePrefix': 'We sent a 6-digit code to',
  'register.verify.verify': 'Create my account',
  'register.verify.didntReceive': "Didn't receive it?",
  'register.verify.resendIn': 'Resend in {seconds}s',
  'register.verify.resendNow': 'Resend now',
  'register.haveAccount': 'Already have an account?',
  'register.signIn': 'Sign in',
  'register.hero.title': 'Launch in minutes',
  'register.hero.body': 'Free plan: 1 brand, 2 journeys, 2 stations, 5 stocks. Upgrade when you are ready.',
  'register.toast.devHint': 'Dev hint',
  'register.toast.welcome': 'Welcome, {name}!',
  'register.toast.welcomeBody': 'Your workspace is ready.',
  'register.toast.initiateFailed': 'Could not start registration',
  'register.toast.emailTaken': 'That email is already registered',
  'register.toast.emailTakenBody': 'Try signing in instead.',
  'register.toast.invalidOtp': 'Invalid or expired code',

  // ── Auth · Pending access ──────────────────────────────────────────
  'pendingAccess.title': 'Waiting for access',
  'pendingAccess.body': "Your account is created but an admin still needs to grant you a role. We'll keep this page warm — sign back in once they're done.",
  'pendingAccess.signedInAs': 'Signed in as',

  // ── Onboarding shell ───────────────────────────────────────────────
  'onboarding.step.account': 'Account',
  'onboarding.step.brandProfile': 'Brand profile',
  'onboarding.step.brandKit': 'Brand kit',

  // ── Edit profile dialog ────────────────────────────────────────────
  'editProfile.eyebrow': 'Edit profile',
  'editProfile.title': 'Update your details',
  'editProfile.subtitle': "Visible across your workspace. Email and role can't change here.",
  'editProfile.section.visuals': 'Visuals',
  'editProfile.section.identity': 'Identity',
  'editProfile.section.professional': 'Professional',
  'editProfile.section.tags': 'Tags',
  'editProfile.field.avatar': 'Avatar',
  'editProfile.field.cover': 'Cover image',
  'editProfile.field.firstName': 'First name',
  'editProfile.field.lastName': 'Last name',
  'editProfile.field.username': 'Handle (@username)',
  'editProfile.field.phone': 'Phone',
  'editProfile.field.bio': 'Bio',
  'editProfile.field.bioPlaceholder': 'A short sentence on what you build.',
  'editProfile.field.title': 'Title',
  'editProfile.field.titlePlaceholder': 'Brand Manager',
  'editProfile.field.location': 'Location',
  'editProfile.field.locationPlaceholder': 'San Antonio, TX',
  'editProfile.field.studiedAt': 'Studied at',
  'editProfile.tag.placeholder': 'Add and press Enter…',
  'editProfile.tag.remove': 'Remove {tag}',
  'editProfile.save': 'Save changes',

  // ── Change password dialog ─────────────────────────────────────────
  'changePassword.eyebrow': 'Security',
  'changePassword.title': 'Change password',
  'changePassword.subtitle': "Pick at least 8 characters. You'll stay signed in.",
  'changePassword.field.current': 'Current password',
  'changePassword.field.new': 'New password',
  'changePassword.field.confirm': 'Confirm new password',
  'changePassword.show': 'Show password',
  'changePassword.hide': 'Hide password',
  'changePassword.hint.lengthOk': 'Length looks good.',
  'changePassword.hint.lengthShort': '{count}/8 characters — add {missing} more.',
  'changePassword.hint.match': 'Passwords match.',
  'changePassword.hint.noMatch': 'Passwords do not match yet.',
  'changePassword.error.length': 'New password must be at least 8 characters.',
  'changePassword.error.mismatch': 'New password and confirmation do not match.',
  'changePassword.submit': 'Update password',
  'changePassword.toast.updated': 'Password updated',

  // ── Pricing page ───────────────────────────────────────────────────
  'pricing.back': 'Back',
  'pricing.heroTitle': 'Plans built for every brand',
  'pricing.heroBody': 'Planning is free forever — operations free, methodology paid. Pick the tier that fits your roadmap.',
  'pricing.checkoutFailed': 'Checkout failed',
  'pricing.talkToUs': 'Talk to us',
  'pricing.talkToUsBody': 'We will reach out to design a custom plan for you.',
  'pricing.mostPopular': 'Most popular',
  'pricing.fromPrice': 'From {v}',
  'pricing.startFree': 'Start free',
  'pricing.choose': 'Choose {tier}',
  'pricing.cap.brands': 'Brands',
  'pricing.cap.events': 'Events / 12mo',
  'pricing.cap.seats': 'Paid seats',
  'pricing.cap.agencies': 'Agencies',
  'pricing.feat.operations': 'Free operations: planning, GPS clock-in, photo verification',
  'pricing.feat.methodology': 'Paid methodology: CS-1 + MENT score',
  'pricing.feat.atledi': 'ATLEDI AI assistant + recap',
  'pricing.feat.notifications': 'Notifications',
  'pricing.feat.activeLog': 'Activity log',
  'pricing.feat.team': 'Team & roles',
  'pricing.planUnavailable': 'Plan unavailable',
  'pricing.missingTier': 'Missing tier "{slug}"',

  // ── Price card ─────────────────────────────────────────────────────
  'priceCard.tier.pilot': 'Pilot',
  'priceCard.tier.scale': 'Scale ★',
  'priceCard.tier.program': 'Program',
  'priceCard.tier.enterprise': 'Enterprise',
  'priceCard.period.year': '/year',
  'priceCard.period.month': '/month',
  'priceCard.mostPopular': '★ Most popular',
  'priceCard.planAria': '{tier} plan',
  'priceCard.selected': 'Selected',
  'priceCard.talkToUs': 'Talk to us',
  'priceCard.select': 'Select {tier}',
  'priceCard.line.inBrands': 'In brands',
  'priceCard.line.inCapacity': 'In capacity',
  'priceCard.line.inEvents': 'In events',
  'priceCard.line.perUnit': 'Per unit',
  'priceCard.brandsOne': '{count} brand',
  'priceCard.brandsMany': '{count} brands',
  'priceCard.xotskiesValue': '{count} xotskies',
  'priceCard.editorSeatOne': '{count} editor seat (transferable)',
  'priceCard.editorSeatMany': '{count} editor seats (transferable)',
  'priceCard.editorSeatsUnlimited': '{count} editor seats (transferable)',
  'priceCard.unlimitedReporters': 'Unlimited reporters (free)',
  'priceCard.tier1Games': 'Tier 1 branded games',
  'priceCard.tier2Games': 'Tier 2 game library included',
  'priceCard.realtime': 'Real-time dashboard + Smart inventory',
  'priceCard.enterpriseExtras': 'SSO + Audit log + API + Dedicated CSM',
  'priceCard.feature.notifications': 'Push notifications',
  'priceCard.feature.activityLog': 'Activity log',
  'priceCard.feature.stockImport': 'Stock CSV import',
  'priceCard.feature.aiAssistant': 'AI assistant',
  'priceCard.feature.userManagement': 'User management',

  // ── Upgrade dialog ─────────────────────────────────────────────────
  'upgradeDialog.title': "You've hit your {resource} limit",
  'upgradeDialog.body': 'Your {plan} plan includes {limit} {resource}. Upgrade to keep building.',
  'upgradeDialog.maybeLater': 'Maybe later',
  'upgradeDialog.seePlans': 'See plans',
  'upgradeDialog.resource.projects': 'projects',
  'upgradeDialog.resource.groups': 'event groups',
  'upgradeDialog.resource.events': 'events',
  'upgradeDialog.resource.stations': 'stations',
  'upgradeDialog.resource.stocks': 'stock items',
  'upgradeDialog.resource.brands': 'brands',
  'upgradeDialog.resource.users': 'users',
  'upgradeDialog.resource.roles': 'roles',
  'upgradeDialog.resource.surveys': 'journeys',

  // ── Billing page ───────────────────────────────────────────────────
  'billingPage.title': 'Billing',
  'billingPage.subtitle': 'Track usage, manage your plan and download invoices.',
  'billingPage.currentBadge': 'Current plan',
  'billingPage.currentTitle': 'Current plan',
  'billingPage.currentDesc': 'Plan details endpoint not yet exposed by backend.',
  'billingPage.changePlan': 'Change plan',
  'billingPage.usageTitle': 'Usage',
  'billingPage.usageDesc': "How much of your plan you've consumed.",
  'billingPage.invoicesTitle': 'Invoices',
  'billingPage.invoicesDesc': 'Your plan purchase history.',
  'billingPage.noInvoices': 'No invoices yet.',
  'billingPage.col.date': 'Date',
  'billingPage.col.plan': 'Plan',
  'billingPage.col.amount': 'Amount',
  'billingPage.col.status': 'Status',
  'billingPage.invoice.paid': 'Paid',
  'billingPage.invoice.pending': 'Pending',
  'billingPage.invoice.status.active': 'Active',
  'billingPage.invoice.status.expired': 'Expired',
  'billingPage.invoice.status.refunded': 'Refunded',
  'billingPage.invoice.status.exhausted': 'Exhausted',
  'billingPage.resource.brands': 'Brands',
  'billingPage.resource.users': 'Users',
  'billingPage.resource.roles': 'Roles',
  'billingPage.resource.surveys': 'Journeys',
  'billingPage.resource.responsesPerSurvey': 'Interactions / journey',
  'billingPage.resource.projects': 'Brand projects',
  'billingPage.resource.eventGroups': 'Event groups',
  'billingPage.resource.events': 'Events',
  'billingPage.resource.stations': 'Stations',
  'billingPage.resource.stocks': 'Stock items',
  'billingPage.featuresTitle': 'Plan features',
  'billingPage.featuresDesc': "What's switched on for your workspace right now.",
  'billingPage.feature.notifications': 'Push notifications',
  'billingPage.feature.activeLog': 'Activity log',
  'billingPage.feature.aiAssistant': 'AI assistant',
  'billingPage.feature.stockImport': 'Stock CSV import',
  'billingPage.feature.userManagement': 'User management',
  'billingPage.gamesTitle': 'Allowed games',
  'billingPage.gamesDesc': 'Game mechanics included in your plan.',
  'billingPage.gamesEmpty': 'No game mechanics enabled on this plan.',

  // ── Checkout result ────────────────────────────────────────────────
  'checkoutResult.successTitle': "You're all set",
  'checkoutResult.successBody': 'Your plan upgrade is live and quotas have been refreshed.',
  'checkoutResult.seeBilling': 'See billing',
  'checkoutResult.backToDashboard': 'Back to dashboard',
  'checkoutResult.cancelTitle': 'Checkout cancelled',
  'checkoutResult.cancelBody': "No charges were made. You can pick another plan whenever you're ready.",
  'checkoutResult.seePlans': 'See plans',

  // ── Mock checkout page ─────────────────────────────────────────────
  'mockCheckout.paymentConfirmed': 'Payment confirmed',
  'mockCheckout.confirmationFailed': 'Confirmation failed',
  'mockCheckout.invalidSession': 'Invalid checkout session',
  'mockCheckout.backToPlans': 'Back to plans',
  'mockCheckout.badge': 'Mock checkout',
  'mockCheckout.planTitle': '{plan} plan',
  'mockCheckout.planDesc': "You'll be charged today and on the same day every month. Cancel anytime.",
  'mockCheckout.subtotal': 'Subtotal',
  'mockCheckout.tax': 'Tax',
  'mockCheckout.taxIncluded': 'Included',
  'mockCheckout.totalDue': 'Total due today',
  'mockCheckout.pay': 'Pay {amount}',
  'mockCheckout.cancelGoBack': 'Cancel and go back',
  'mockCheckout.mockEnv': 'Mock environment — no real money moves.',
  'mockCheckout.whatsIn': "What's in {plan}?",

  // ── Settings page (v2 list-style page) ─────────────────────────────
  'settings.comingSoon': 'Coming soon',
  'settings.section.accountDesc': 'Brand info, timezone, language',
  'settings.section.access': 'Access Manager',
  'settings.section.accessDesc': 'Invite members, assign roles, share access',
  'settings.section.brand': 'Brand',
  'settings.section.brandDesc': 'Brand-level settings and Brand Inventory opt-in',
  'settings.brand.title': 'Brand settings',
  'settings.brand.subtitle': 'Per-brand toggles. Affects the brand experience across Xotski.',
  'settings.brand.inventoryTitle': 'Brand Inventory',
  'settings.brand.inventoryDescription':
    'Two-tier inventory model (PRD §9): items live in a brand-level pool and pull into project warehouses. Off until you flip it on per brand.',
  'settings.brand.lockedBadge': 'Plan locked',
  'settings.brand.noBrands': 'No brands visible to your workspace yet.',
  'settings.brand.savedToast': 'Saved',
  'settings.brand.errorToast': 'Could not save change',
  'settings.section.billing': 'Billing',
  'settings.section.billingDesc': 'Plan, usage and invoices',
  'settings.section.followup': 'Follow-up emails',
  'settings.section.followupDesc': 'Templates, coupons and post-event nurture',
  'settings.section.integrations': 'CRM integrations',
  'settings.section.integrationsDesc': 'Connect HubSpot, Mailchimp or a webhook',
  'settings.section.operations': 'Operations',
  'settings.section.operationsDesc': 'Warehouses and asset protocols',
  'settings.section.crm': 'CRM integrations',
  'settings.section.crmDesc': 'Connect HubSpot, Salesforce or a webhook',
  'settings.section.api': 'API keys',
  'settings.section.apiDesc': 'Generate keys for programmatic access',
  'settings.section.notificationsDesc': 'Email alerts for inventory and events',
  'settings.section.activeLog': 'Active Log',
  'settings.section.activeLogDesc': 'Audit trail of every change in your workspace',

  // ── Insights page (v2 distribution overview) ───────────────────────
  'insights.resetFilters': 'Reset filters',
  'insights.filter.brand': 'Brand',
  'insights.filter.event': 'Event',
  'insights.filter.classification': 'Classification',
  'insights.filter.from': 'From',
  'insights.filter.to': 'To',
  'insights.filter.allBrands': 'All brands ({count})',
  'insights.filter.allEvents': 'All events ({count})',
  'insights.filter.all': 'All',
  'insights.filter.unclassified': 'Unclassified',
  'insights.kpi.distributed': 'Distributed',
  'insights.kpi.distributedHint': '{rate}% of allocated',
  'insights.kpi.remaining': 'Remaining on stations',
  'insights.kpi.remainingHint': '{total} allocated total',
  'insights.kpi.value': 'Distributed value',
  'insights.kpi.valueHint': 'Based on unit cost',
  'insights.kpi.activeEvents': 'Active events',
  'insights.kpi.activeEventsHintOne': '{brands} brand · {events} event in scope',
  'insights.kpi.activeEventsHintMany': '{brands} brands · {events} events in scope',
  'insights.restockAlertOne': '{count} station at or below restock threshold.',
  'insights.restockAlertMany': '{count} stations at or below restock threshold.',
  'insights.restockRefill': 'Refill before the next event window.',
  'insights.chart.byBrand': 'Distribution by brand',
  'insights.chart.topEvents': 'Top events by units distributed',
  'insights.chart.topStocks': 'Top 10 xotskies (units distributed)',
  'insights.chart.byClassification': 'Distributed units by classification',
  'insights.chart.byRarity': 'Distributed units by rarity',
  'insights.chart.usedAvailable': 'Stock used vs available (top 10)',
  'insights.chart.empty': 'No data for the current filters',
  'insights.rarity.common': 'Common',
  'insights.rarity.uncommon': 'Uncommon',
  'insights.rarity.rare': 'Rare',
  'insights.rarity.epic': 'Epic',
  'insights.rarity.legendary': 'Legendary',
  'insights.rarity.tier': 'Tier {n}',
  'insights.unclassifiedLabel': 'Unclassified',

  // ── Contacts page (v2 list) ────────────────────────────────────────
  'contacts.responsesCount': '{count} interactions',
  'contacts.col.when': 'When',
  'contacts.col.survey': 'Journey',
  'contacts.col.station': 'Station',
  'contacts.col.source': 'Source',
  'contacts.col.prize': 'Prize',
  'contacts.noun': 'interactions',
  // ── Caso 01 — Leads CRM (real /co-contacts integration) ────────────
  'contacts.export': 'Export CSV',
  'contacts.applySearch': 'Search',
  'contacts.filter.consentOnly': 'Only with consent',
  'contacts.totalCount': '{count} contacts',
  'contacts.col.name': 'Name',
  'contacts.col.contact': 'Contact',
  'contacts.col.interactions': 'Visits',
  'contacts.col.prizes': 'Prizes',
  'contacts.col.lastSeen': 'Last seen',
  'contacts.col.consent': 'Consent',
  'contacts.anonymousName': 'Anonymous participant',
  'contacts.anonymousSnippet': 'No identity captured',
  'contacts.consent.yes': 'Opted in',
  'contacts.consent.no': 'Not opted in',
  'contacts.detail.title': 'Contact detail',
  'contacts.detail.back': 'Back to contacts',
  'contacts.detail.invalidId': 'Invalid contact id.',
  'contacts.detail.delete': 'Delete (GDPR)',
  'contacts.detail.deleteConfirm':
    'Permanently scrub PII for {name}? This cannot be undone.',
  'contacts.detail.engagement.title': 'Engagement',
  'contacts.detail.engagement.interactions': 'Visits',
  'contacts.detail.engagement.prizes': 'Prizes won',
  'contacts.detail.engagement.firstSeen': 'First seen',
  'contacts.detail.engagement.lastSeen': 'Last seen',
  'contacts.detail.demographics.title': 'Demographics',
  'contacts.detail.demographics.age': 'Age',
  'contacts.detail.demographics.gender': 'Gender',
  'contacts.detail.demographics.city': 'City',
  'contacts.detail.demographics.state': 'State',
  'contacts.detail.demographics.zip': 'ZIP',
  'contacts.detail.demographics.language': 'Language',
  'contacts.detail.controls.title': 'Controls',
  'contacts.detail.controls.revokeConsent': 'Revoke marketing consent',
  'contacts.detail.controls.grantConsent': 'Grant marketing consent',
  'contacts.detail.controls.tags': 'Tags',
  'contacts.detail.controls.add': 'Add',
  'contacts.detail.controls.addTagPlaceholder': 'Add a tag…',
  'contacts.detail.controls.removeTag': 'Remove tag',
  'contacts.detail.timeline.title': 'Interactions ({count})',
  'contacts.detail.timeline.empty': 'No interactions captured yet.',
  'contacts.detail.timeline.atEvent': 'Event #{id}',
  'contacts.detail.timeline.unknownEvent': 'Unknown event',
  'contacts.detail.timeline.station': 'Station #{id}',
  'contacts.detail.timeline.prize': 'Prize #{id}',

  // ── Caso 02 — Event results report ─────────────────────────────────
  'eventReport.invalidId': 'Invalid event id.',
  'eventReport.backToEvent': 'Back to event',
  'eventReport.print': 'Print / Save PDF',
  'eventReport.createShare': 'Create share link',
  'eventReport.shareCopied': 'Link copied to clipboard',
  'eventReport.publicFooter':
    'Aggregated results — no individual participant data is shared.',
  'eventReport.publicInvalid': 'Invalid share link.',
  'eventReport.publicNotFound.title': 'This report is not available',
  'eventReport.publicNotFound.body':
    'The link may have expired or been revoked. Ask the host for a fresh one.',
  'eventReport.reach.interactions': 'Interactions',
  'eventReport.reach.last24h': 'Last 24 h',
  'eventReport.reach.distinctContacts': 'Unique contacts',
  'eventReport.reach.leads': 'Leads captured',
  'eventReport.prizes.title': 'Prizes',
  'eventReport.prizes.allocated': 'Allocated',
  'eventReport.prizes.distributed': 'Distributed',
  'eventReport.prizes.remaining': 'Remaining',
  'eventReport.prizes.value': 'Distributed value',
  'eventReport.costs.title': 'Cost per outcome',
  'eventReport.costs.perInteraction': 'Per interaction',
  'eventReport.costs.perLead': 'Per lead',
  'eventReport.topAnswers.title': 'Top answers',
  'eventReport.demographics.title': 'Demographics',

  // ── Caso 03 — Prize redemption (operator screen + stats) ───────────
  'redemption.title': 'Redeem a prize',
  'redemption.subtitle': 'Type or scan the consumer\'s code, then confirm delivery.',
  'redemption.codeLabel': 'Redemption code',
  'redemption.lookUp': 'Look up',
  'redemption.scan': 'Scan',
  // Caso 11 v2 — camera-based QR scanner overlay.
  'operator.scanner.title': 'Scan QR code',
  'operator.scanner.hint': 'Point the camera at the consumer QR.',
  'operator.scanner.unsupported':
    "This browser doesn't support camera-based scanning. Type the code manually instead.",
  'operator.scanner.cameraError':
    'Could not access the camera. Check the permission and try again.',
  'redemption.preview.title': 'Prize ready to deliver',
  'redemption.preview.code': 'Code',
  'redemption.preview.stockId': 'Prize',
  'redemption.preview.station': 'Station',
  'redemption.preview.won': 'Won at',
  'redemption.preview.expires': 'Expires',
  'redemption.preview.confirm': 'Confirm delivery',
  'redemption.preview.confirming': 'Confirming…',
  'redemption.preview.state.delivered': 'Already redeemed.',
  'redemption.preview.state.expired': 'This redemption has expired.',
  'redemption.preview.state.voided': 'This redemption has been voided.',
  'redemption.success.title': 'Delivered!',
  'redemption.success.body': 'Code {code} confirmed.',
  'redemption.error.generic': 'Could not redeem. Try again.',
  'redemption.error.tooShort': 'Codes are 6+ characters.',
  'redemption.stats.title': 'Prize redemption',
  'redemption.stats.won': 'Won',
  'redemption.stats.delivered': 'Delivered',
  'redemption.stats.expired': 'Expired',
  'redemption.stats.rate': 'Rate',

  // ── Caso 04 — Post-event follow-up settings ────────────────────────
  'followup.title': 'Follow-up email',
  'followup.subtitle':
    'Configure the email sent to opted-in participants after their event.',
  'followup.enabled': 'Active',
  'followup.disabled': 'Off',
  'followup.newTemplate': 'New template',
  'followup.confirmDelete': 'Delete this template?',
  'followup.templates.title': 'Templates',
  'followup.templates.description':
    'The most specific match wins per dispatch: event-scoped first, then brand-wide.',
  'followup.templates.empty':
    'No templates yet. Create one to start nurturing.',
  'followup.editor.newTitle': 'New template',
  'followup.editor.editTitle': 'Edit template',
  'followup.editor.name': 'Name',
  'followup.editor.nameRequired': 'Name is required — Save unlocks when you fill this in.',
  'followup.editor.scope': 'Apply to',
  'followup.editor.scopeHint': 'Event-specific templates win over brand-wide for that event.',
  'followup.scope.brandWide': 'All events (brand-wide)',
  'followup.scope.event': 'Event',
  'followup.toast.created': 'Template created',
  'followup.toast.updated': 'Template updated',
  'followup.toast.deleted': 'Template deleted',
  'followup.toast.saveError': 'Could not save template',
  'followup.toast.deleteError': 'Could not delete template',
  'followup.editor.subject': 'Subject',
  'followup.editor.body': 'Body (HTML)',
  'followup.editor.variables':
    'Variables: {{firstName}} {{lastName}} {{prizeName}} {{couponCode}} {{eventName}} {{unsubscribeUrl}}',
  'followup.editor.delay': 'Send delay (hours)',
  'followup.defaults.subject': 'Thanks for stopping by!',
  'followup.defaults.bodyHtml':
    '<p>Hi {{firstName}},</p><p>Thanks for visiting us at {{eventName}}. Use code <strong>{{couponCode}}</strong> next time you shop.</p><p><a href="{{unsubscribeUrl}}">Unsubscribe</a></p>',

  // ── Caso 14 — First-event onboarding checklist ────────────────────
  'firstSteps.title': 'Get to your first activation',
  'firstSteps.step.brand.title': 'Add your brand',
  'firstSteps.step.brand.description': 'Logo + colors so every screen carries your identity.',
  'firstSteps.step.brand.cta': 'Add brand',
  'firstSteps.step.event.title': 'Create your first event',
  'firstSteps.step.event.description': 'Dates, location, and which brand it belongs to.',
  'firstSteps.step.event.cta': 'Create event',
  'firstSteps.step.station.title': 'Add a station',
  'firstSteps.step.station.description': 'A booth or touchpoint at the event. Generates the QR.',
  'firstSteps.step.station.cta': 'Add station',
  'common.dismiss': 'Dismiss',

  // ── Caso 14 v1 — Modal guided wizard ───────────────────────────
  'firstEventWizard.title': "Let's launch your first Xotski",
  'firstEventWizard.subtitle':
    "Three steps and you're live: brand, event, station.",
  'firstEventWizard.progress': 'Setup progress',
  'firstEventWizard.dontShowAgain': "Don't show this again",
  'firstEventWizard.step.brand.title': 'Create your brand',
  'firstEventWizard.step.brand.description':
    'Logo, colors and tone — the look every journey inherits.',
  'firstEventWizard.step.brand.cta': 'Open brand studio',
  'firstEventWizard.step.event.title': 'Create your first event',
  'firstEventWizard.step.event.description':
    'A live activation: when and where it runs.',
  'firstEventWizard.step.event.cta': 'Create event',
  'firstEventWizard.step.station.title': 'Add a station and print the QR',
  'firstEventWizard.step.station.description':
    'A booth, kiosk, or touchpoint where guests scan to play.',
  'firstEventWizard.step.station.cta': 'Add station',
  // Caso 14 v2 — inline brand creation.
  'firstEventWizard.brand.nameLabel': 'Brand name',
  'firstEventWizard.brand.namePlaceholder': 'e.g. Atledi',
  'firstEventWizard.brand.createSubmit': 'Create brand',
  'firstEventWizard.brand.openStudio': 'Open the full Brand Studio →',
  'firstEventWizard.brand.created': 'Brand created.',
  'firstEventWizard.brand.createFailed': "Couldn't create the brand.",

  // ── Project detail page ────────────────────────────────────────────
  'project.status.planning': 'Planning',
  'project.status.live': 'Live',
  'project.status.wrapped': 'Wrapped',
  'project.backToBrand': 'Back to brand',
  'project.editProject': 'Edit project',
  'project.eventsCardTitle': 'Events',
  'project.newEvent': 'New event',
  'project.viewTrends': 'View trends',

  // ── Caso 12 parte 2 — Event-to-event trends ─────────────────────
  'trends.title': 'Event trends',
  'trends.subtitle':
    'Compare how each activation performed side-by-side.',
  'trends.empty': 'Not enough events to compare yet.',
  'trends.chart.title': 'Metrics per event',
  'trends.chart.subtitle':
    'Bars are grouped by event so you can spot week-over-week patterns at a glance.',
  'trends.table.title': 'Detail table',
  'trends.table.event': 'Event',
  'trends.table.date': 'Date',
  'trends.table.status': 'Status',
  'trends.metric.responses': 'Interactions',
  'trends.metric.leads': 'Leads',
  'trends.metric.prizes': 'Prizes delivered',
  'trends.metric.prizesDistributed': 'Prizes distributed',
  'project.field.eventName': 'Event name',
  'project.field.eventNamePlaceholder': 'Brooklyn Block Party',
  'project.field.address': 'Address',
  'project.field.addressPlaceholder': '123 Wythe Ave, Brooklyn NY',
  'project.field.starts': 'Starts',
  'project.field.ends': 'Ends',
  'project.field.sections': 'Stations',
  'project.field.codeRef': 'Code ref',
  'project.field.codeRefPlaceholder': 'LOLL26',
  'project.createEvent': 'Create event',
  'project.eventsEmpty': 'No events yet. Add one to start placing stations.',
  'project.noAddress': 'No address',
  'project.tbd': 'TBD',
  'project.noun.events': 'events',
  'project.notFound': 'Project not found',
  'project.eventCreatedToast': 'Event created',
  'project.eventCreateFailedToast': 'Create failed',
  'project.updatedToast': 'Project "{name}" updated',
  'project.updateFailedToast': 'Could not update project',
  'project.deletedToast': 'Project "{name}" deleted',
  'project.deleteFailedToast': 'Could not delete project',
  'project.edit.title': 'Edit project',
  'project.edit.description': 'Update the project name, code, status and dates. Events stay attached.',
  'project.edit.field.name': 'Project name',
  'project.edit.field.codeRef': 'Code reference',
  'project.edit.field.codeRefPlaceholder': 'JST26',
  'project.edit.field.status': 'Status',
  'project.edit.note': 'project dates are derived from the events below. Edit individual events to change the schedule.',
  'project.edit.noteLabel': 'Note:',
  'project.edit.field.startDate': 'Project starts',
  'project.edit.field.endDate': 'Project ends',
  'project.edit.rangeNoteLabel': 'Date range:',
  'project.edit.rangeNote': 'this window governs the dates allowed on every event in the project. Leave empty to derive it from the events.',
  'project.edit.rangeInvalid': 'Project end must be after the start.',
  // Project workspace tabs
  'project.tab.plan': 'Plan',
  'project.tab.track': 'Track',
  'project.tab.measure': 'Report',
  // PLAN tab — Planner
  'planner.xotskiCarousel.title': 'Xotskis',
  'planner.xotskiCarousel.empty': 'No xotskis in this brand yet.',
  'planner.xotskiCarousel.availableTip': '{n} available in the brand pool',
  'planner.xotskiCarousel.outOfStock': 'Out of stock — restock to allocate it',
  'planner.xotskiCarousel.out': 'Out of stock',
  'planner.stationSource.label': '+ Station',
  'planner.reporters.title': 'Reporters',
  'planner.reporters.emailPlaceholder': 'email@domain.com',
  'planner.reporters.namePlaceholder': 'Name (optional)',
  'planner.reporters.inviteToSelected': 'Invite to selected event',
  'planner.reporters.selectEventFirst': 'Select an event to invite',
  'planner.reporters.empty': 'No reporters yet. Invite one above or drag onto an event.',
  'planner.reporters.dragHint': 'Drag onto an event to (re)invite',
  'planner.reporters.invitedToast': 'Reporter invited',
  'planner.reporters.inviteFailedToast': 'Could not invite reporter',
  'planner.reporters.statusConfirmed': 'Confirmed',
  'planner.reporters.statusPending': 'Pending',
  'planner.reporters.statusRevoked': 'Revoked',
  'planner.reporters.statusExpired': 'Expired',
  'planner.reporters.remove': 'Remove reporter',
  'planner.reporters.removed': 'Reporter removed',
  'planner.reporters.removeFailed': 'Could not remove reporter',
  'planner.alloc.available': '{n} left',
  'planner.alloc.increase': 'Increase quantity',
  'planner.alloc.decrease': 'Decrease quantity',
  'planner.alloc.atCeiling': 'No more units available in the brand',
  'planner.alloc.remove': 'Remove from event',
  'planner.alloc.removed': 'Xotski removed from event',
  'planner.alloc.removeFailed': 'Could not remove xotski',
  'planner.alloc.qtyFailed': 'Could not update quantity',
  // Per-station inline allocation editor
  'planner.station.expand': 'Expand station',
  'planner.station.collapse': 'Collapse station',
  'planner.station.sliceCount': '{count} slices',
  'planner.station.empty': 'No xotskis here yet. Drop one on the event to stock every station.',
  'planner.station.drag': 'Drag to reorder',
  'planner.station.duplicate': 'Duplicate as another slice',
  'planner.station.duplicated': 'Slice duplicated',
  'planner.station.duplicateFailed': 'Could not duplicate slice',
  'planner.station.reorderFailed': 'Could not save the new order',
  'planner.station.preview': 'Preview the wheel',
  'planner.station.previewTitle': 'Preview · {name}',
  'planner.station.previewNoSurvey': 'No journey configured for this event yet — pick a template in the event\'s Journey tab.',
  'planner.alloc.groupedHint': '{n} allocations · {total} units total. Edit per-station quantities in the station detail.',
  'planner.alloc.removeGroupTitle': 'Remove “{name}” from this event?',
  'planner.alloc.removeGroupBody': 'This removes all {n} allocations of “{name}” across this event’s stations ({total} units total) and takes it off their wheels. The units return to the brand pool; past wins are kept. To remove it from a single station instead, use that station’s detail.',
  'planner.properties.title': 'Event properties',
  'planner.properties.noSelection': 'Select an event to edit its properties.',
  'planner.properties.savedToast': 'Event saved',
  'planner.properties.saveFailedToast': 'Could not save event',
  'planner.properties.endAfterStart': 'End must be after start.',
  'planner.properties.outOfRange': 'Dates must fall within the project range.',
  'planner.properties.save': 'Save',
  'planner.properties.sectionsReadonly': 'Read-only — equals the number of stations on this event',
  'planner.properties.sectionsHint': 'read-only',
  'planner.properties.surveyTemplate': 'Journey template',
  'planner.properties.pickTemplate': 'Pick a journey template…',
  'planner.properties.applyingTemplate': 'Applying…',
  'planner.properties.templateApplied': 'Journey template applied to the event',
  'planner.properties.templateFailed': 'Could not apply the journey template',
  'planner.events.title': 'Events in planning',
  'planner.events.empty': 'No events to plan. Use “New events” to create some.',
  'planner.events.collapse': 'Collapse',
  'planner.events.expand': 'Expand',
  'planner.events.openConfig': 'Open config',
  'planner.events.xotskis': 'Xotskis',
  'planner.events.reporters': 'Reporters',
  'planner.events.dropXotskiHint': 'Drag a xotski here to allocate it.',
  'planner.events.xotskiRollupEmpty': 'No xotskis yet — drop one on a station below to add it.',
  'planner.events.dropReporterHint': 'Drag a reporter here to invite them.',
  'planner.events.stationCount': '{count} station(s)',
  'planner.events.dropStationHint': 'Drag the “+ Station” chip here to add one.',
  'planner.events.openStationConfig': 'Open station configuration',
  'planner.events.removeStation': 'Remove station',
  'planner.events.lastStation': 'An event must keep at least one station',
  'planner.events.stationRemoved': 'Station removed',
  'planner.events.stationRemoveFailed': 'Could not remove station',
  'planner.phase.live': 'Live',
  'planner.phase.upcoming': 'Upcoming',
  'planner.phase.edit': 'In edit',
  'planner.phase.done': 'Completed',
  'planner.batch.open': 'New events',
  'planner.batch.title': 'Create multiple events',
  'planner.batch.description': 'Pick how many events to create. Names and dates are filled in automatically and each event gets one station.',
  'planner.batch.countLabel': 'How many events?',
  'planner.batch.noRange': 'no project range set',
  'planner.batch.previewNames': 'Names: {first} … {last}',
  'planner.batch.previewRange': 'Dates spread across {range}',
  'planner.batch.previewStation': 'Each event starts with one station.',
  'planner.batch.cancel': 'Cancel',
  'planner.batch.create': 'Create events',
  'planner.batch.createdToast': '{count} event(s) created',
  'planner.batch.failedToast': 'Could not create events',
  'planner.drop.xotskiDone': '{name} allocated to all stations',
  'planner.drop.xotskiToStation': '{name} allocated to this station',
  'planner.drop.xotskiUseStation': 'Drop the xotski onto a station to allocate it.',
  'planner.drop.xotskiOnStationExists': '{name} is already on this station — use the +/- or duplicate it',
  'planner.drop.reporterDone': '{name} invited',
  'planner.drop.stationDone': 'Station added',
  'planner.drop.failed': 'Drop failed',
  'planner.drop.noStations': 'This event has no stations yet.',
  'planner.drop.allStationsFailed': 'Could not allocate to any station.',
  'planner.drop.partial': 'Allocated, but {failed} of {total} stations failed.',
  'planner.drop.here': 'Drop here',
  'planner.drop.xotskiExists': '{name} is already in this event — use +/- to change quantity',
  'planner.drop.reporterExists': '{name} is already invited to this event',
  'sharedQr.pick.title': 'Where are you?',
  'sharedQr.pick.body': 'Pick your station to start.',
  'sharedQr.loading.title': 'Loading…',
  'sharedQr.loading.body': 'Fetching the stations for this QR.',
  'sharedQr.invalid.title': 'Invalid QR',
  'sharedQr.invalid.body': 'This shared QR is not valid or has been removed.',
  'sharedQr.empty.title': 'No stations',
  'sharedQr.empty.body': 'This QR has no stations assigned yet.',
  'sharedQr.manager.title': 'Shared QR',
  'sharedQr.manager.subtitle': 'One QR for several stations — the consumer picks their station first.',
  'sharedQr.manager.create': 'Create shared QR',
  'sharedQr.manager.empty': 'No shared QRs yet for this event.',
  'sharedQr.manager.copy': 'Copy link',
  'sharedQr.manager.copied': 'Link copied',
  'sharedQr.manager.delete': 'Delete',
  'sharedQr.manager.removed': 'Shared QR deleted',
  'sharedQr.manager.removeFailed': 'Could not delete the shared QR',
  'sharedQr.manager.dialogTitle': 'Create a shared QR',
  'sharedQr.manager.dialogBody': 'Pick 2+ stations to share one QR. Each keeps its own xotskis.',
  'sharedQr.manager.nameLabel': 'Name',
  'sharedQr.manager.defaultName': 'Shared QR',
  'sharedQr.manager.pickStations': 'Stations',
  'sharedQr.manager.needTwo': 'This event needs at least 2 stations to share a QR.',
  'sharedQr.manager.cancel': 'Cancel',
  'sharedQr.manager.createBtn': 'Create',
  'sharedQr.manager.createdToast': 'Shared QR created',
  'sharedQr.manager.createFailed': 'Could not create the shared QR',
  // TRACK tab
  'track.empty': 'No events in this project yet.',
  'track.noDate': 'No date set',
  'track.edit': 'Edit',
  'track.view': 'View',
  'track.group.live': 'Live now',
  'track.group.upcoming': 'Upcoming',
  'track.group.edit': 'In edit',
  'track.group.done': 'Completed',
  // MEASURE tab
  'measure.range': 'Date range',
  'measure.events': 'Events',
  'measure.live': 'Live now',
  'measure.stockChartTitle': 'Stock allocation across the project',
  'measure.noStockData': 'No stock has been allocated yet.',
  'measure.quantity': 'Allocated',
  'measure.available': 'Available',
  'project.edit.confirmTitle': 'Delete this project?',
  'project.edit.confirmBody': 'All of its events, stations and stock allocations will be removed permanently.',
  'project.edit.deleting': 'Deleting…',
  'project.edit.confirmDelete': 'Confirm delete',
  'project.edit.deleteProject': 'Delete project',
  'project.edit.cancel': 'Cancel',
  'project.edit.saving': 'Saving…',
  'project.edit.saveChanges': 'Save changes',

  // ── Access Manager ─────────────────────────────────────────────────
  'accessManager.title': 'Access Manager',
  'accessManager.scope': 'scope: {app}',
  'accessManager.subtitle': 'Manage members, roles, and external access grants for your workspace and child agencies. Listings and new entries are scoped to the {app} app — switch app to manage other modules.',
  'accessManager.tab.members': 'Members',
  'accessManager.tab.roles': 'Roles',
  'accessManager.tab.grants': 'External Sharing',
  'accessManager.currentApp': 'current',
  'accessManager.noAccess.title': 'No access',
  'accessManager.noAccess.body': 'You don’t have permission to view members, roles, or external sharing. Ask your administrator to grant you at least one.',

  // ── Members tab ────────────────────────────────────────────────────
  'members.title': 'Members',
  'members.addMember': 'Add member',
  'members.empty.title': 'No members yet',
  'members.empty.body': 'Add the first team member to your workspace.',
  'members.confirmDelete': 'Soft-delete {email}?',
  'members.deletedToast': 'Member removed',
  'members.col.name': 'Name',
  'members.col.email': 'Email',
  'members.col.company': 'Brand',
  'members.col.role': 'Role',
  'members.col.actions': 'Actions',
  'members.youBadge': 'you',
  'members.openRoleTitle': 'Open role "{role}" in the Roles tab',
  'members.cantDeleteSelf': "You can't delete your own account",
  'members.setPassword.action': 'Set password',
  'members.setPassword.title': 'Set password',
  'members.setPassword.description': "Set a new password for {name}. They'll use it on their next sign-in.",
  'members.setPassword.newPassword': 'New password',
  'members.setPassword.confirm': 'Confirm password',
  'members.setPassword.submit': 'Set password',
  'members.setPassword.show': 'Show password',
  'members.setPassword.hide': 'Hide password',
  'members.setPassword.minLength': 'Password must be at least {n} characters.',
  'members.setPassword.mismatch': "Passwords don't match.",
  'members.setPassword.successToast': 'Password set for {name}',
  'members.setPassword.errorToast': 'Could not set password',

  // ── Roles tab ──────────────────────────────────────────────────────
  'roles.title': 'Roles',
  'roles.createRole': 'Create role',
  'roles.empty.title': 'No roles yet',
  'roles.empty.body': 'Create roles to assign reusable permission bundles to your members.',
  'roles.confirmDelete': 'Delete role {name}?',
  'roles.deletedToast': 'Role deleted',
  'roles.templateBadge': 'template',
  'roles.permsCount': '{count} permission(s)',
  'roles.cloneTitle': 'Create a new role with these same permissions',
  'roles.clone': 'Clone',
  'roles.readOnlyTitle': 'System templates are read-only. Clone to customize.',
  'roles.readOnlyLabel': 'Read-only template',

  // ── Grants tab ─────────────────────────────────────────────────────
  'grants.title': 'External Sharing',
  'grants.comingSoonNotice': 'External Sharing is coming soon. This feature is not yet available — stay tuned!',
  'grants.shareAccess': 'Share access',
  'grants.confirmRevoke': 'Revoke access shared with {email}?',
  'grants.revokedToast': 'Grant revoked',
  'grants.tab.outgoing': 'Granted by me',
  'grants.tab.incoming': 'Granted to me',
  'grants.empty.outgoingTitle': 'No active grants',
  'grants.empty.incomingTitle': 'No incoming grants',
  'grants.empty.outgoingBody': 'Share access to one of your companies with an external user.',
  'grants.empty.incomingBody': 'External parties have not shared access with you yet.',
  'grants.user': 'user #{id}',
  'grants.expires': 'expires {date}',
  'grants.permsCount': '{count} permission(s)',
  'grants.grantedBy': 'granted by {email}',
  'grants.revoke': 'Revoke',

  // ── Brand Identity page (additional descriptive variants) ──────────
  'brandIdentity.subtitleLong': "Brand-level defaults for logo, colors, font and campaign name. Per-event customizations (data collection, age gate, game, prizes) live on each event's Design Journey.",
  'brandIdentity.saveChanges': 'Save changes',
  'brandIdentity.savedToast': 'Brand identity saved',
  'brandIdentity.saveFailedToast': 'Save failed',

  // ── Brand Studio (new) ─────────────────────────────────────────────
  'brandStudio.title': 'Brand Studio',
  'brandStudio.editing': 'Editing',
  'brandStudio.newBrandInherit': 'New brand · inherited from',
  'brandStudio.back': 'Back',
  'brandStudio.deleting': 'Deleting…',
  'brandStudio.confirmDelete': 'Confirm delete',
  'brandStudio.deleteBrand': 'Delete brand',
  'brandStudio.cancel': 'Cancel',
  'brandStudio.saving': 'Saving…',
  'brandStudio.saveChanges': 'Save changes',
  'brandStudio.savePublish': 'Save & Publish',
  'brandStudio.confirmDeleteTitle': 'Delete this brand?',
  'brandStudio.confirmDeleteBody': 'All of its projects, events, stations, stock and journeys will be removed permanently. Click',
  'brandStudio.confirmDeleteEmph': 'Confirm delete',
  'brandStudio.confirmDeleteEnd': 'again to proceed.',
  'brandStudio.brandCreated': 'Brand "{name}" created',
  'brandStudio.brandUpdated': 'Brand "{name}" updated',
  'brandStudio.brandDeleted': 'Brand "{name}" deleted',
  'brandStudio.createFailed': 'Could not create brand',
  'brandStudio.updateFailed': 'Could not update brand',
  'brandStudio.kitSaveFailed': 'Brand saved but the kit (colors / experience defaults) could not be persisted',
  'brandStudio.deleteFailed': 'Could not delete brand',
  'brandStudio.error.nameRequired': 'Brand name is required',
  'brandStudio.error.primaryHex': 'Primary color must be a hex value (#RGB or #RRGGBB)',
  'brandStudio.error.secondaryHex': 'Secondary color must be a hex value',
  'brandStudio.error.textHex': 'Text color must be a hex value',
  'brandStudio.fixOne': 'Fix {count} issue before saving:',
  'brandStudio.fixMany': 'Fix {count} issues before saving:',
  'brandStudio.tab.identity': 'Identity',
  'brandStudio.tab.experience': 'Experience',
  'brandStudio.tab.lookFeel': 'Look & Feel',
  'brandStudio.tab.game': 'Game',
  'brandStudio.tab.prizes': 'Prizes',
  'brandStudio.tab.output': 'Output',
  'brandStudio.livePreview': 'Live preview',
  'brandStudio.previewFooter': 'The preview updates as you change settings — journey welcome, game CTA, and prize reveal screens all share these styles.',
  // Caso 05 v1 — Game tab celebration simulation
  'brandStudio.game.celebration': 'Celebration animation',
  'brandStudio.game.test': 'Test celebration',
  'brandStudio.game.themeHint.confetti': 'Bursting paper rain — feels celebratory.',
  'brandStudio.game.themeHint.fireworks': 'Bigger payoff — radial explosions.',
  'brandStudio.game.themeHint.sparks': 'Subtle shimmer — minimal & elegant.',
  // Identity tab
  'brandStudio.identity.hint': 'Upload your logo first — in the future Atledi will auto-suggest brand colors from it. For now, set your 5-color palette below.',
  'brandStudio.identity.brandName': 'Brand name',
  'brandStudio.identity.brandNameHint': 'What this brand will appear as in Xotski.',
  'brandStudio.identity.brandNamePlaceholder': 'e.g. Joyly',
  'brandStudio.identity.industry': 'Industry',
  'brandStudio.identity.industryHint': 'Optional. Helps Atledi tailor templates.',
  'brandStudio.identity.industryPlaceholder': 'Food & Beverage',
  'brandStudio.identity.campaignName': 'Campaign name',
  'brandStudio.identity.campaignNameHint': 'Used in QR URLs, station labels and reports.',
  'brandStudio.identity.campaignNamePlaceholder': 'e.g. Joyly Spring Tour 2026',
  'brandStudio.identity.brandLogo': 'Brand logo',
  'brandStudio.identity.brandLogoHint': 'Inherited from the company until overridden.',
  'brandStudio.identity.noLogo': 'No logo',
  'brandStudio.identity.qrThumb': 'QR thumbnail',
  'brandStudio.identity.qrThumbHint': 'Square image inside QR codes. Falls back to Brand logo.',
  'brandStudio.identity.noThumb': 'No thumb',
  'brandStudio.identity.backgroundImage': 'Background image',
  'brandStudio.identity.backgroundImageHint': 'Full-bleed photo behind the journey. Empty = solid background color.',
  'brandStudio.identity.noImage': 'No image',
  'brandStudio.identity.palette': '5-color palette',
  'brandStudio.identity.color.primary': 'Primary',
  'brandStudio.identity.color.primaryDesc': 'Headers, QR background, main surfaces',
  'brandStudio.identity.color.secondary': 'Secondary',
  'brandStudio.identity.color.secondaryDesc': 'Supporting surfaces, borders, dividers',
  'brandStudio.identity.color.accent': 'Accent',
  'brandStudio.identity.color.accentDesc': 'Buttons, active states, QR foreground',
  'brandStudio.identity.color.background': 'Background',
  'brandStudio.identity.color.backgroundDesc': 'Card and container backgrounds',
  'brandStudio.identity.color.text': 'Text',
  'brandStudio.identity.color.textDesc': 'Primary text on brand surfaces',
  'brandStudio.identity.brandFont': 'Brand font',
  // Experience tab
  'brandStudio.experience.hint': 'Bilingual journeys see up to 34% higher completion at multicultural events.',
  'brandStudio.experience.surveyLanguage': 'Journey language',
  'brandStudio.experience.english': 'English',
  'brandStudio.experience.spanish': 'Español',
  'brandStudio.experience.bilingualHint': 'Bilingual mode — users pick their language at start',
  'brandStudio.experience.contactInfo': 'Contact info',
  'brandStudio.experience.collectContact': 'Collect contact data from attendees',
  'brandStudio.experience.fullName': 'Full name',
  'brandStudio.experience.email': 'Email',
  'brandStudio.experience.phone': 'Phone',
  'brandStudio.experience.ageGate': 'Age gate',
  'brandStudio.experience.disabled': 'Disabled',
  'brandStudio.experience.demographics': 'Demographics',
  'brandStudio.experience.ageRange': 'Age range',
  'brandStudio.experience.gender': 'Gender',
  'brandStudio.experience.ethnicity': 'Ethnicity',
  'brandStudio.experience.nationality': 'Nationality',
  'brandStudio.experience.pronouns': 'Pronouns',
  'brandStudio.experience.customQuestions': 'Custom questions',
  'brandStudio.experience.customQuestionsLabel': '{count} custom question(s) added',
  'brandStudio.experience.customQuestionsAccessOn': 'Plan allows custom questions. Add them in Phase 4 question editor.',
  'brandStudio.experience.customQuestionsAccessOff': 'Upgrade to add custom questions to the consumer flow.',
  // Look & Feel tab
  'brandStudio.look.hint': 'Consistent button styles and celebration themes build brand recognition.',
  'brandStudio.look.buttonStyle': 'Button style',
  'brandStudio.look.next': 'Next',
  'brandStudio.look.celebration': 'Celebration theme',
  'brandStudio.look.noAnimation': 'No animation',
  'brandStudio.look.consent': 'Consent wording',
  'brandStudio.look.consentHint': 'Shown before data collection begins. Must comply with your privacy policy.',
  'brandStudio.look.gameCTAEn': 'Game CTA (EN)',
  'brandStudio.look.gameCTAEnPlaceholder': 'Spin to win!',
  'brandStudio.look.gameCTAEs': 'Game CTA (ES)',
  'brandStudio.look.gameCTAEsPlaceholder': '¡Gira para ganar!',
  // Game tab
  'brandStudio.game.hint': 'Each game showcases your brand differently. Wheel ships with all plans; premium games unlock with upgrades.',
  'brandStudio.game.chooseYourGame': 'Choose your game',
  'brandStudio.game.brandIntegration': 'Brand integration',
  'brandStudio.brandIntegration': 'Brand integration',
  'brandStudio.game.wheel.name': 'Prize Wheel',
  'brandStudio.game.wheel.desc': 'Spin to win — the classic. Logo in the hub, brand colors on segments.',
  'brandStudio.game.wheel.s1': 'Logo appears in the wheel hub',
  'brandStudio.game.wheel.s2': 'Brand color on wheel segments',
  'brandStudio.game.wheel.s3': 'Prize photo fills the reveal screen',
  'brandStudio.game.scratch.name': 'Scratch Card',
  'brandStudio.game.scratch.desc': 'Scratch to reveal — tactile and satisfying.',
  'brandStudio.game.scratch.s1': 'Logo watermark under scratch surface',
  'brandStudio.game.scratch.s2': 'Brand color gradient on card',
  'brandStudio.game.scratch.s3': 'Prize photo revealed underneath',
  'brandStudio.game.pirinola.name': 'Pirinola',
  'brandStudio.game.pirinola.desc': 'Spin the top — a cultural classic with a digital twist.',
  'brandStudio.game.pirinola.s1': 'Brand colors on pirinola faces',
  'brandStudio.game.pirinola.s2': 'Logo on the top face',
  'brandStudio.game.pirinola.s3': 'Prize name on the winning face',
  'brandStudio.game.random.name': 'Random Draw',
  'brandStudio.game.random.desc': 'Lottery-style reveal — suspense and surprise.',
  'brandStudio.game.random.s1': 'Brand-colored draw animation',
  'brandStudio.game.random.s2': 'Logo in the header',
  'brandStudio.game.random.s3': 'Prize card with product photo',
  'brandStudio.game.plinko.name': 'Plinko',
  'brandStudio.game.plinko.desc': 'Drop the ball — physics-based fun with branded pegs.',
  'brandStudio.game.plinko.s1': 'Brand color on pegs and board',
  'brandStudio.game.plinko.s2': 'Logo on the ball',
  'brandStudio.game.plinko.s3': 'Prize slots at the bottom',
  'brandStudio.game.soccer.name': 'Soccer Ball',
  'brandStudio.game.soccer.desc': 'Tap to kick a ball into the goal — slots inside reveal the prize.',
  'brandStudio.game.soccer.s1': 'Brand color on the ball and goal frame',
  'brandStudio.game.soccer.s2': 'Logo on prize slots',
  'brandStudio.game.soccer.s3': 'Best for sports brands and beverage activations',
  // PRD §18 — Lotería catalog entry
  'brandStudio.game.loteria.name': 'Lotería',
  'brandStudio.game.loteria.desc':
    'Mexican folk-art board game. 4×3 of cards. Match a row to win. Highest cultural resonance for Latino events.',
  'brandStudio.game.loteria.s1': 'Brand colors on row-win highlight + Jackpot banner',
  'brandStudio.game.loteria.s2': 'Auto-marked board · longest dwell of all games',
  'brandStudio.game.loteria.s3': 'Festival anchor; best for Hispanic-market activations',
  'brandStudio.game.slots.name': 'Slot Machine',
  'brandStudio.game.slots.desc':
    'Three reels of prize icons. Tap to spin; line up the middle row to win.',
  'brandStudio.game.slots.s1': 'Brand colors on the cabinet and reel frame',
  'brandStudio.game.slots.s2': 'Prize icons on every reel',
  'brandStudio.game.slots.s3': 'High-energy mechanic; great for arcade-style booths',
  // Game picker UI copy
  'brandStudio.gamePicker.selectedGame': 'Selected game',
  'brandStudio.gamePicker.tierRequired': 'Tier required',
  'publicSurvey.soccer.kick': 'Kick!',
  'publicSurvey.soccer.kicking': 'Kicking…',
  'publicSurvey.soccer.goal': 'GOAL!',
  'publicSurvey.soccer.fieldAlt': 'Soccer field with goal',
  // PRD §18 — Lotería (loteria game UI)
  'publicSurvey.loteria.label': 'Lotería',
  'publicSurvey.loteria.cardCalled': 'Card called',
  'publicSurvey.loteria.getReady': 'Get ready…',
  'publicSurvey.loteria.start': '¡Lotería!',
  'publicSurvey.loteria.hint': 'Cards match automatically · Complete a row to win',
  // PRD §18 — Unified game UX (shared action/state labels; no emojis; EN+ES)
  'publicSurvey.games.youWon': 'You won',
  'publicSurvey.games.tapToPlay': 'Tap to play',
  'publicSurvey.games.sound': 'Sound',
  'publicSurvey.games.soundOn': 'Sound on',
  'publicSurvey.games.soundOff': 'Sound off',
  'publicSurvey.games.wheel.action': 'Spin the wheel',
  'publicSurvey.games.wheel.busy': 'Spinning…',
  'publicSurvey.games.wheel.hub': 'SPIN',
  'publicSurvey.games.scratch.action': 'Scratch to reveal',
  'publicSurvey.games.scratch.hint': 'Scratch with your finger',
  'publicSurvey.games.scratch.revealed': 'Revealed!',
  'publicSurvey.games.slots.action': 'Pull',
  'publicSurvey.games.slots.busy': 'Spinning…',
  'publicSurvey.games.plinko.action': 'Drop the ball',
  'publicSurvey.games.plinko.busy': 'Dropping…',
  'publicSurvey.games.plinko.tagline': 'Drop · Bounce · Win',
  'publicSurvey.games.pirinola.action': 'Spin the top',
  'publicSurvey.games.pirinola.busy': 'Spinning…',
  'publicSurvey.games.randomDraw.action': 'Draw a card',
  'publicSurvey.games.randomDraw.busy': 'Shuffling…',
  'publicSurvey.games.randomDraw.done': 'Revealed!',
  'publicSurvey.games.loteria.waiting': 'Waiting…',
  'brandStudio.game.wheelTaglineEn': 'Wheel tagline (EN)',
  'brandStudio.game.wheelTaglineEs': 'Wheel tagline (ES)',
  'brandStudio.game.wheelTaglineEnPlaceholder': 'Spin to win!',
  'brandStudio.game.wheelTaglineEsPlaceholder': '¡Gira para ganar!',
  'brandStudio.game.heroImage': 'Wheel hero image',
  'brandStudio.game.heroImageHint': 'Optional. Product shot or event banner shown behind the wheel.',
  'brandStudio.game.noHero': 'No hero',
  'brandStudio.game.tier.free': 'Free',
  'brandStudio.game.tier.premium': 'Premium',
  'brandStudio.game.tier.pro': 'Pro',
  // Prizes tab
  'brandStudio.prizes.hint': '3–5 prizes with varied rarity perform best. Rare (1–2 stars) builds excitement; common (4–5 stars) keeps everyone winning.',
  'brandStudio.prizes.inventory': 'Prize inventory',
  'brandStudio.prizes.selected': '{count} selected',
  'brandStudio.prizes.units': '{count} units',
  'brandStudio.prizes.revealPreview': 'Prize reveal preview',
  'brandStudio.prizes.youWon': 'You Won!',
  'brandStudio.prizes.fallback': 'Prize',
  'brandStudio.prizes.claim': 'Show this screen to event staff to claim your prize',
  'brandStudio.prizes.rare': 'Rare',
  'brandStudio.prizes.medium': 'Medium',
  'brandStudio.prizes.common': 'Common',
  'brandStudio.prizes.inv.tshirt': 'Branded T-Shirt',
  'brandStudio.prizes.inv.hat': 'Snapback Hat',
  'brandStudio.prizes.inv.sticker': 'Sticker Pack',
  'brandStudio.prizes.inv.tote': 'Canvas Tote Bag',
  'brandStudio.prizes.inv.bottle': 'Water Bottle',
  'brandStudio.prizes.inv.hoodie': 'Premium Hoodie',
  // Output tab
  'brandStudio.output.hint': 'Each station gets its own unique QR — brand colors and logo configured here apply automatically.',
  'brandStudio.output.universalQr': 'Universal QR code',
  'brandStudio.output.embedLogo': 'QR includes your embedded logo',
  'brandStudio.output.uploadLogo': 'Upload a logo to embed it in the QR',
  'brandStudio.output.shareableLink': 'Shareable link',
  'brandStudio.output.uniquePerStation': 'Each station gets its own unique link & QR',
  'brandStudio.output.uniquePerStationBody': 'Public links are generated per station — not per brand. Go to {path} to download individual QR codes. The brand settings configured here apply automatically.',
  'brandStudio.output.path': 'Projects → Event → Station',
  'brandStudio.output.downloadPng': 'Download PNG',
  'brandStudio.output.downloadPngHint': 'Available after saving + wiring to a station',
  'brandStudio.output.printDisplay': 'Print display',
  'brandStudio.output.upload': 'Upload',
  'brandStudio.output.clear': 'Clear',
  // Preview
  'brandStudio.preview.yourBrand': 'Your brand',
  'brandStudio.preview.brandWorkspace': 'Brand workspace',
  'brandStudio.preview.fallbackCampaign': '{brand} sampling',
  'brandStudio.preview.continue': 'Continue',
  'brandStudio.preview.spinToWin': 'Spin to win',

  // ── Surveys list / templates / preview / builder ───────────────────
  'surveys.browseTemplates': 'Browse templates',
  'surveys.emptyTitle': 'No journeys yet',
  'surveys.emptyBody': 'Start from a template or build a journey from scratch.',
  'surveys.allSurveys': 'All journeys',
  'surveys.locked': 'Locked',
  'surveys.template': 'Template',
  'surveys.preview': 'Preview',
  'surveys.templates.emptyTitle': 'No templates yet',
  'surveys.templates.emptyBody': 'System-provided templates will show up here.',
  'surveys.templates.stepsCount': '{n} steps',
  'surveys.toast.cloned': 'Template cloned',
  'surveys.toast.cloneFailed': 'Clone failed',
  'surveys.preview.badge': 'Preview',
  'surveys.preview.editInBuilder': 'Edit in builder',
  'surveys.preview.stepsAndComponents': '{steps} steps · {components} components',
  'surveys.preview.openBuilder': 'Open builder',
  'surveys.preview.stepsTitle': 'Steps',
  'surveys.preview.noSteps': 'No steps yet.',
  'surveys.preview.stepN': 'Step {n}',
  'surveys.preview.noLabel': 'no label',
  'surveys.preview.publicLink': 'Public link',
  'surveys.preview.publicLinkBody':
    'This is what consumers see when they scan the station QR. Attach the journey to a station first to render it with full context (branding, stocks, wheel).',
  'surveys.preview.openConsumer': 'Open consumer preview',
  'surveys.builder.backToSurveys': 'Back to journeys',
  'surveys.builder.settings': 'Journey settings',
  'surveys.builder.field.name': 'Name',
  'surveys.builder.field.description': 'Description',
  'surveys.builder.field.webhookUrl': 'Webhook URL',
  'surveys.builder.translatable': 'Translatable',
  'surveys.builder.translatableHelp': 'Show language toggle to the consumer (EN/ES).',
  'surveys.builder.stepsComponents': 'Steps & components',
  'surveys.builder.toast.saved': 'Journey saved',
  'surveys.builder.toast.saveFailed': 'Save failed',

  // ── Public-survey end-user surface ─────────────────────────────────
  'publicSurvey.finishPage.title': 'Thanks for playing!',
  'publicSurvey.finishPage.body': "Come back anytime — there's always more to unlock.",
  'publicSurvey.missingSurveyOrStation': 'Missing journey or station.',
  // Caso 15 — Plan-cap / lock terminal screens (consumed by SurveyLockedScreen).
  'publicSurvey.locked.limitExhausted':
    'This experience has reached its limit for now. Thanks for stopping by!',
  'publicSurvey.locked.planExpired':
    'This experience is no longer available. Please contact the host for more info.',
  'publicSurvey.locked.manualDisable':
    'This experience is temporarily disabled by its host.',
  'publicSurvey.ageGate.eyebrow': 'Age verification',
  'publicSurvey.ageGate.question': 'Are you {age} or older?',
  'publicSurvey.ageGate.body': 'You need to confirm to continue.',
  'publicSurvey.ageGate.yes': 'Yes, I am',
  'publicSurvey.ageGate.no': 'No',
  'publicSurvey.continueArrow': 'Continue →',
  'publicSurvey.prizeReveal.claimHint': 'Show this screen at the bar / station to claim your prize.',
  // PRD §11 — deliveryMode='off' hint (BA hands prize directly).
  'publicSurvey.prizeReveal.directDelivery': 'Your prize will be handed to you by the staff. No code needed.',
  'publicSurvey.photoFilm.eyebrow': 'Notice',
  'publicSurvey.photoFilm.title': 'Photography & filming',
  'publicSurvey.photoFilm.body': 'By participating in this event you acknowledge you may be photographed or filmed for marketing use.',
  'publicSurvey.photoFilm.bodyWithBrand': 'By participating in {brand} you acknowledge you may be photographed or filmed for marketing use.',
  'publicSurvey.photoFilm.notACheckbox': 'This is a notice, not a consent — no checkbox required.',
  'publicSurvey.photoFilm.acknowledge': 'I understand',
  'publicSurvey.prizeReveal.continueExternal': 'Continue to brand site →',
  'publicSurvey.prizeReveal.brandedThankYou': 'Thanks for playing — keep this screen ready when the brand ambassador comes to deliver your prize.',
  'calendar.eyebrow': 'Activity',
  'calendar.title': 'Calendar',
  'calendar.searchPlaceholder': 'Search events…',
  'calendar.statusAll': 'All statuses',
  'calendar.statusActive': 'Active',
  'calendar.statusScheduled': 'Scheduled',
  'calendar.statusArchived': 'Archived',
  'calendar.loading': 'Loading events…',
  'calendar.empty': 'No events match the filters.',
  'calendar.view.list': 'List',
  'calendar.view.month': 'Month',
  'insights.tabs.recap': 'Atledi Recap',
  'insights.tabs.throughput': 'Throughput',
  'insights.tabs.distribution': 'Distribution',
  'insights.tabs.demographics': 'Demographics',
  'insights.tabs.leads': 'Leads',
  'insights.leads.eyebrow': 'Locked CSV export',
  'insights.leads.title': 'Lead capture & follow-up',
  'insights.leads.body': 'Lead identities — names, contact, demographics and marketing consent — live in the Contacts surface alongside the locked 22-column CSV export (PRD §10.1).',
  'insights.leads.cta': 'Open Contacts',
  'insights.exportPdf': 'Export PDF',
  'insights.pdfPaid': 'PDF · Paid',
  'insights.recap.eyebrow': 'Atledi Recap',
  'insights.recap.narrative': 'Narrative',
  'insights.recap.narrativePlaceholder':
    'Live narrative ships with the Atledi/Sonnet endpoint in Phase 4. Each event will get a 2–3 sentence story of what just happened, in the brand voice configured in Brand Studio.',
  'insights.recap.kpis': 'KPI summary',
  'insights.recap.recommendation': 'Recommendation',
  'insights.recap.recommendationPlaceholder':
    'One concrete next action with confidence level appears here once the recap endpoint is wired.',
  'insights.recap.confidence.low': 'Low',
  'insights.recap.confidence.medium': 'Medium',
  'insights.recap.confidence.high': 'High',
  'insights.kpi.moments': 'Moments',
  'insights.kpi.throughput': 'Throughput',
  'insights.kpi.inventory': 'Inventory',
  'legal.eyebrow': 'Legal · One-time acknowledgement',
  'legal.brandObligations': 'Brand obligations',
  'legal.xotskiObligations': 'Xotski obligations',
  'legal.xotskiObligationsBody':
    'Xotski processes personal data on your documented instructions only, keeps it inside the regions you select, and notifies you of any breach within 24 hours of detection.',
  'legal.signerName': 'Signer name',
  'legal.signerTitle': 'Signer title',
  'legal.later': 'Remind me later',
  'legal.submitting': 'Saving…',
  'legal.accept': 'I accept',
  'reporterAdmin.eyebrow': 'Reporters',
  'reporterAdmin.title': 'Field reporters for this event',
  'reporterAdmin.invite': 'Invite reporter',
  'reporterAdmin.freeUnlimited': 'Free · Unlimited',
  'reporterAdmin.paidOnly': 'Paid',
  'reporterAdmin.paidHint': 'Upgrade to invite reporters with 24h magic links — free unlimited reporters in every paid tier.',
  'reporterAdmin.loading': 'Loading reporters…',
  'reporterAdmin.empty': 'No reporters yet. Send a magic link to your first field staff.',
  'reporterAdmin.expiresAt': 'expires {date}',
  'reporterAdmin.state.active': 'Active',
  'reporterAdmin.state.revoked': 'Revoked',
  'reporterAdmin.state.expired': 'Expired',
  'reporterAdmin.reissueHint': 'Reissue link (copies new URL)',
  'reporterAdmin.revokeHint': 'Revoke active link',
  'reporterAdmin.revoked': 'Reporter link revoked',
  'reporterAdmin.reissuedCopied': 'New link copied to clipboard',
  'reporterAdmin.invitedCopied': 'Magic URL copied to clipboard',
  'reporterAdmin.inviteError': 'Could not send invitation',
  'reporterAdmin.copied': 'Copied',
  'reporterAdmin.copy': 'Copy URL',
  'reporterAdmin.modal.eyebrow': 'PRD §12 · Reporter View',
  'reporterAdmin.modal.title': 'Invite a field reporter',
  'reporterAdmin.modal.body': 'They receive a 24h magic link to a 4-tab field surface — no account, no password.',
  'reporterAdmin.modal.email': 'Email',
  'reporterAdmin.modal.nameOptional': 'Name (optional)',
  'reporterAdmin.modal.namePlaceholder': 'Brand ambassador name',
  'reporterAdmin.modal.send': 'Send invite',
  'reporterAdmin.modal.sending': 'Sending…',
  'reporterAdmin.modal.successTo': 'Magic link sent to {email}',
  'reporterAdmin.modal.magicUrl': 'Magic URL',
  'reporterAdmin.modal.expiresHint': 'Link expires {date}',
  'reporter.tabs.today': 'Today',
  'reporter.tabs.inventory': 'Inventory',
  'reporter.tabs.notes': 'Notes',
  'reporter.tabs.claims': 'Claims',
  'reporter.header.eyebrow': 'Reporter View',
  'reporter.header.expiresAt': 'Link expires {date}',
  'reporter.verifying.title': 'Verifying your link…',
  'reporter.verifying.body': 'One moment while we open your event.',
  'reporter.invalidLink.title': 'This link is invalid',
  'reporter.invalidLink.body': 'Ask the brand to send you a fresh link.',
  'reporter.expired.title': 'Link expired',
  'reporter.expired.body': 'Reporter links are valid for 24 hours. Ask the brand for a new one.',
  'reporter.terms.eyebrow': 'Reporter terms',
  'reporter.terms.title': 'Before you start at {event}',
  'reporter.terms.body': 'You are signing in as a brand reporter. Quick acknowledgements before you can log notes:',
  'reporter.terms.point1': 'Notes and photos you log are shared with the organizing brand only.',
  'reporter.terms.point2': 'You will not share the magic link or screenshots that expose claim codes.',
  'reporter.terms.point3': 'You may stop reporting at any time by closing this tab.',
  'reporter.terms.checkbox': 'I understand and want to start reporting.',
  'reporter.terms.accept': 'Start reporting',
  'reporter.terms.submitting': 'Saving…',
  'reporter.today.scheduleTitle': 'Schedule',
  'reporter.today.scheduleBody': 'Stations + start/end times for {event}. Live view ships with the offline queue in Phase 3.',
  'reporter.today.atlediEyebrow': 'Atledi briefing',
  'reporter.today.atlediTitle': 'Your focus right now',
  'reporter.today.atlediBody': 'Capacity tip + first-hour focus from the Atledi assistant. Coming in Phase 4.',
  'reporter.today.atlediBriefing.morning': 'Good morning at {event}. Open posters by every entrance and warm the QR scanners — the first hour drives 30–40% of the day’s captures.',
  'reporter.today.atlediBriefing.afternoon': 'Afternoon push at {event}. Refresh any picked-clean stations, walk the bar line, and re-engage groups that haven’t scanned yet.',
  'reporter.today.atlediBriefing.evening': 'Evening flow at {event}. Watch claim-code timers and flag any near-expiry before the bar runs out of inventory.',
  'reporter.today.atlediBriefing.night': 'Late shift at {event}. Tally what’s left, capture closing photos, and queue notes for the manager review tomorrow.',
  'reporter.inventory.title': 'Brand inventory',
  'reporter.inventory.body': 'Items allocated to {event} with quick adjusters for damaged or written-off units.',
  'reporter.notes.title': 'Field notes',
  'reporter.notes.body': 'Photo + text notes for the brand manager. Tap "+" on the bottom right to add one.',
  'reporter.notes.offlineTitle': 'Offline-first',
  'reporter.notes.offlineBody': 'Notes typed without signal queue in IndexedDB and sync once the connection comes back.',
  'reporter.claims.title': 'Claim verification',
  'reporter.claims.body': 'Scan a guest QR or type a claim code to mark a prize delivered for {event}.',
  'brandStudio.look.atlediTone': 'Atledi voice',
  'brandStudio.look.atlediToneHint': 'Shapes the recap + welcome narrative Atledi writes for this brand.',
  'brandStudio.look.atlediTone.playful': 'Playful',
  'brandStudio.look.atlediTone.professional': 'Professional',
  'brandStudio.look.atlediTone.warm': 'Warm',
  'brandStudio.look.atlediTone.bold': 'Bold',
  'brandStudio.look.atlediTone.custom': 'Custom',
  'brandStudio.look.atlediToneCustomPlaceholder': 'Describe the voice — e.g. "concise, data-first, no exclamation marks".',
  'brandStudio.look.consentVariant': 'Consent variant',
  'brandStudio.look.consentVariantHint': 'Anonymous (A) skips PII and marketing checkboxes. PII (B) collects contact info and shows the unbundled marketing opt-in.',
  'brandStudio.look.consentVariantA': 'A · Anonymous',
  'brandStudio.look.consentVariantADesc': 'No PII, no marketing. Just T&C acceptance.',
  'brandStudio.look.consentVariantB': 'B · PII + Marketing opt-in',
  'brandStudio.look.consentVariantBDesc': 'Collects contact + separate optional marketing checkbox.',
  'publicSurvey.claim.preparing': 'Generating your claim code…',
  'publicSurvey.claim.codeLabel': 'Claim code',
  'publicSurvey.claim.showToBa':
    'Show this code to the brand ambassador before the timer runs out.',
  'publicSurvey.claim.expiredAskBa':
    'The timer expired. Flag a brand ambassador and they can extend it.',
  'publicSurvey.wheel.emptyTitle': 'Stock is empty',
  'publicSurvey.wheel.emptyBody': 'All prizes have been claimed for this station.',

  // ── Output tab (event QR + short URLs) ─────────────────────────────
  'output.noClonedSurvey': 'This BrandStudioEvent has no cloned journey yet — pick a template first.',
  'output.noStations':
    "No stations in this event yet. Create stations on the Stations tab and they'll appear here for QR distribution.",
  'output.intro': 'Each station has its own short URL + QR. Print or share with the on-site team.',
  'output.toast.copied': 'Short URL copied',
  'output.toast.copyFailed': 'Copy failed',
  'output.copied': 'Copied',
  'output.copy': 'Copy',
  'output.saveSvg': 'Save SVG',
  'output.open': 'Open',

  // ── Active Log (audit trail) ───────────────────────────────────────
  'activeLog.title': 'Active log',
  'activeLog.subtitle': 'Every mutation across your workspace — who, when, where.',
  'sidebar.activeLog': 'Active log',
  'activeLog.noAccessTitle': 'No audit access',
  'activeLog.noAccessBody': "Your role doesn't grant access to the audit log.",
  'activeLog.empty.title': 'No activity yet',
  'activeLog.empty.body': 'Once people start working, every meaningful action shows up here.',
  'activeLog.filter.search': 'Search',
  'activeLog.filter.searchPlaceholder': 'Description, URL, user…',
  'activeLog.filter.action': 'Action',
  'activeLog.filter.actionAll': 'All actions',
  'activeLog.filter.entity': 'Entity',
  'activeLog.filter.userId': 'User ID',
  'activeLog.filter.eventId': 'Event ID',
  'activeLog.filter.from': 'From',
  'activeLog.filter.to': 'To',
  'activeLog.filter.apply': 'Apply',
  'activeLog.filter.reset': 'Reset',
  'activeLog.table.when': 'When',
  'activeLog.table.user': 'User',
  'activeLog.table.action': 'Action',
  'activeLog.table.entity': 'Entity',
  'activeLog.table.description': 'Description',
  'activeLog.pager.summary': 'Showing {from}–{to} of {total}',
  'activeLog.pager.prev': 'Previous page',
  'activeLog.pager.next': 'Next page',
  'activeLog.detail.id': 'ID',
  'activeLog.detail.companyId': 'Brand',
  'activeLog.detail.user': 'User',
  'activeLog.detail.email': 'Email',
  'activeLog.detail.method': 'HTTP method',
  'activeLog.detail.appType': 'App',
  'activeLog.detail.eventId': 'Event ID',
  'activeLog.detail.ip': 'IP address',
  'activeLog.detail.url': 'URL',
  'activeLog.detail.userAgent': 'User agent',
  'activeLog.detail.description': 'Description',
  'activeLog.detail.oldData': 'Old data',
  'activeLog.detail.newData': 'New data',

  // ── Paywall (premium feature gate) ─────────────────────────────────
  'paywall.badge.premium': 'Premium',
  'paywall.badge.locked': 'Locked',
  'paywall.cta.viewPlans': 'View plans',
  'paywall.activeLog.title': 'Active Log is a premium service',
  'paywall.activeLog.description':
    "Audit every action your team takes: who created, edited or deleted what, from where and when. Activate this service in your plan to start seeing the full history.",
  'paywall.activeLog.perk1': 'Granular history of every change (before/after)',
  'paywall.activeLog.perk2': 'Filter by user, entity, date and IP',
  'paywall.activeLog.perk3': 'Export and extended retention',
  'paywall.notifications.title': 'Notifications is a premium service',
  'paywall.notifications.description':
    'Send real-time messages (push, in-app and email) to your team and station operators. Activate this service in your plan to start communicating.',
  'paywall.notifications.perk1': 'Instant push to operator devices',
  'paywall.notifications.perk2': 'Optional email to the same recipient',
  'paywall.notifications.perk3': 'Segmentation by users or groups',
  'paywall.aiAssistant.title': 'AI Assistant is a premium service',
  'paywall.aiAssistant.description':
    'Ask questions about your projects, events, journeys and interactions in natural language. The assistant streams answers grounded in your live data.',
  'paywall.aiAssistant.perk1': 'Instant Q&A about your events, stations and interactions',
  'paywall.aiAssistant.perk2': 'Surfacing insights without exporting to a spreadsheet',
  'paywall.aiAssistant.perk3': 'Always grounded in the data your plan unlocks',
};

const ES: Dict = {
  // ── Common ─────────────────────────────────────────────────────────
  'common.edit': 'Editar',
  'common.comingSoon': 'Próximamente',
  'common.save': 'Guardar',
  'common.cancel': 'Cancelar',
  'common.done': 'Listo',
  'profile.privacy.eyebrow': 'Privacidad',
  'profile.privacy.profileLabel': 'Tarjeta de perfil',
  'profile.privacy.profileHint': 'Quién puede ver tu nombre, bio y tema.',
  'profile.privacy.activityLabel': 'Cronología de actividad',
  'profile.privacy.activityHint': 'Si tu calendario y actividad aparecen entre workspaces.',
  'profile.privacy.public': 'Público',
  'profile.privacy.private': 'Privado',
  'profile.privacy.saved': 'Preferencia de privacidad guardada',
  'profile.privacy.saveError': 'No se pudo guardar la preferencia',
  'common.close': 'Cerrar',
  'common.delete': 'Eliminar',
  'common.create': 'Crear',
  'common.signOut': 'Cerrar sesión',
  'datePicker.placeholder': 'Selecciona una fecha',
  'datePicker.clear': 'Borrar fecha',
  'datePicker.time': 'Hora',
  'tour.start': 'Hacer recorrido',
  'tour.next': 'Siguiente',
  'tour.prev': 'Atrás',
  'tour.done': 'Listo',
  'tour.progress': '{{current}} de {{total}}',
  'tour.restart': 'Reiniciar los recorridos',
  'tour.restartHint': 'Vuelve a ver las guías al visitar cada página.',
  'tour.restartDone': 'Recorridos reiniciados — se mostrarán de nuevo al navegar.',
  'journeyPreview.title': 'Vista previa en vivo',
  'brandTree.label': 'Explorar proyectos, eventos y estaciones',
  'brandTree.loadError': 'No se pudo cargar',
  'brandTree.empty.projects': 'Sin proyectos aún',
  'brandTree.empty.events': 'Sin eventos',
  'brandTree.empty.stations': 'Sin estaciones',
  'common.unassigned': 'Sin asignar',
  'common.upcoming': 'Próximos',
  'common.live': 'En vivo',
  'common.idle': 'Inactivo',
  'common.export': 'Exportar',
  'common.search': 'Buscar…',
  'common.loading': 'Cargando…',

  // ── TopBar ─────────────────────────────────────────────────────────
  'topbar.workspace': 'ESPACIO',
  'topbar.agency': 'AGENCIA',
  'topbar.atledi': 'Atledi',
  'topbar.atlediSoon': 'Atledi llegará pronto',
  'topbar.resetDemo': 'Reiniciar datos de demo',
  'topbar.theme': 'Tema',
  'topbar.language': 'Idioma',
  'topbar.openMenu': 'Abrir menú',
  'topbar.closeMenu': 'Cerrar menú',
  'topbar.help': 'Ayuda y documentación',

  // ── Selector de workspace (pill de brand alineado a la izquierda) ──
  'topbar.workspaceSwitcher.searchPlaceholder': 'Buscar marcas…',
  'topbar.workspaceSwitcher.empty': 'Sin marcas coincidentes',
  'topbar.workspaceSwitcher.clear': 'Limpiar búsqueda',
  'topbar.workspaceSwitcher.noBrand': 'Elegir marca',

  // ── Árbol de brand del sidebar (marca seleccionada + proyectos/eventos)
  'sidebar.brandTree.projects': 'Proyectos',
  'sidebar.brandTree.events': 'Eventos',
  'sidebar.brandTree.noProjects': 'Sin proyectos aún',
  'sidebar.brandTree.noEvents': 'Sin eventos aún',
  'sidebar.brandTree.noStations': 'Sin estaciones aún',
  'sidebar.brandTree.expand': 'Expandir',
  'sidebar.brandTree.collapse': 'Colapsar',

  // ── Pestañas del perfil de marca (Proyectos · Xotskies · Sobrantes)
  'brand.tabs.projects': 'Proyectos',
  'brand.tabs.xotskies': 'Xotskies',
  'brand.tabs.inventory': 'Inventario',
  'brand.tabs.leftovers': 'Sobrantes',
  'brand.leftovers.title': 'Inventario sobrante de eventos pasados',
  'brand.leftovers.subtitle': 'Eventos pasados con xotskies sin usar todavía asignados a sus estaciones.',
  'brand.leftovers.emptyTitle': 'Sin sobrantes',
  'brand.leftovers.emptyBody': 'Ningún evento pasado terminó con unidades disponibles. Cuando un evento cierre con sobrantes aparecerá aquí.',
  'brand.leftovers.noBrand': 'Sin marca seleccionada',
  'brand.leftovers.noBrandBody': 'Elige una marca en el selector del topbar para ver su inventario sobrante.',
  'brand.leftovers.totalLeftover': 'Total sobrante',
  'brand.leftovers.units': 'unidades',
  // Legacy — para deep-links antiguos.
  'brand.tabs.prizes': 'Inventario',
  'brand.xotskies.title': 'Xotskies de la marca',
  'brand.xotskies.subtitle': 'Catálogo de xotskies de esta marca. Añade o importa en bloque desde un ZIP.',
  'brand.xotskies.add': 'Añadir xotski',
  'brand.xotskies.import': 'Importar en bloque',
  'brand.xotskies.emptyTitle': 'Sin xotskies aún',
  'brand.xotskies.emptyBody': 'Añade el primer xotski para esta marca o importa un ZIP.',
  'brand.xotskies.noBrand': 'Sin marca seleccionada',
  'brand.xotskies.noBrandBody': 'Elige una marca en el selector del topbar para gestionar sus xotskies.',
  'brand.xotskies.agencyCatalog': 'Disponibles en el catálogo del agency',
  'brand.xotskies.agencyCatalogEmpty': 'Ya adoptaste todos los xotskies del agency, o el catálogo está vacío.',
  'brand.xotskies.deleteAria': 'Eliminar xotski',
  'brand.xotskies.deleteBlocked': 'No se puede eliminar el xotski',
  'brand.xotskies.deleteBlockedHint': 'Tiene asignaciones activas en alguna estación — elimina las asignaciones antes de borrarlo.',
  'brand.xotskies.writeOffBlocked': 'No se puede descontar más de {available} unidad(es) disponibles.',

  // ── Theme menu ─────────────────────────────────────────────────────
  'theme.dark': 'Oscuro',
  'theme.light': 'Claro',

  // ── Dashboard / Home ───────────────────────────────────────────────
  'dashboard.portfolio': 'Portafolio de marcas',
  'dashboard.brandHub': 'Brand Hub',
  'dashboard.brandsLower': 'marcas',
  'dashboard.kpi.liveActivations': 'Activaciones en vivo',
  'dashboard.kpi.spend': 'Gasto',
  'dashboard.kpi.peopleReached': 'Personas alcanzadas (YTD)',
  'dashboard.subBrands': 'Sub-marcas',
  'dashboard.brandManagers': 'Gestores de marca',
  'dashboard.newBrand': 'Nueva marca',
  'dashboard.noBrandsTitle': 'Aún no hay marcas',
  'dashboard.noBrandsBody':
    'Agrega una marca para empezar a correr proyectos en tu espacio.',
  'dashboard.noManagers':
    'Aún no hay gestores asignados. Invita a tu equipo desde Ajustes.',

  // ── SubBrand card ──────────────────────────────────────────────────
  'subbrand.projects': 'Proyectos',
  'subbrand.events': 'Eventos',
  'subbrand.active': 'Activos',
  'subbrand.ownedBy': 'Gestionado por',
  'subbrand.ytd': 'YTD',

  // ── Brand profile page ─────────────────────────────────────────────
  'brand.workspace': 'Espacio de marca',
  'brand.editInStudio': 'Editar en Brand Studio',
  'brand.new.cta': 'Nuevo brand',
  'brand.new.title': 'Crear un nuevo brand',
  'brand.new.subtitle': 'Solo lo básico — el resto lo personalizás en Brand Studio a continuación.',
  'brand.new.nameLabel': 'Nombre del brand',
  'brand.new.namePlaceholder': 'ej. Acme Soda',
  'brand.new.industryLabel': 'Industria',
  'brand.new.industryPlaceholder': 'ej. Bebidas (opcional)',
  'brand.new.studioHint': 'Al crearlo te llevamos directo a Brand Studio para sumar el logo, los colores y las tipografías.',
  'brand.new.submit': 'Crear brand',
  'brand.new.creating': 'Creando…',
  'brand.new.created': 'Brand “{name}” creado',
  'brand.new.failed': 'No se pudo crear el brand',
  'brand.viewTrends': 'Ver tendencias',
  'brand.recentEvents': 'Eventos recientes',
  'brand.recentEvents.count': 'Mostrando {shown} de {total}',
  'common.open': 'Abrir',
  'brand.activeProjects': 'Proyectos activos',
  'brand.newProject': 'Nuevo proyecto',
  'brand.noProjectsTitle': 'Aún no hay proyectos',
  'brand.noProjectsBody':
    'Lanza tu primera campaña para configurar eventos, estaciones y xotskies.',
  'brand.createProject': 'Crear proyecto',
  'brand.kpi.activeXotskies': 'Xotskies activos',
  'brand.kpi.distributed': 'Distribuidos (YTD)',
  'brand.kpi.engagement': 'Tasa de interacción',
  'brand.kpi.health': 'Salud de marca',

  // ── Profile page ───────────────────────────────────────────────────
  'profile.tabs.events': 'Eventos',
  'profile.tabs.brands': 'Marcas',
  'profile.tabs.projects': 'Proyectos',
  'profile.tabs.stations': 'Estaciones',
  'profile.tabs.team': 'Equipo',
  'profile.events.upcoming': 'Próximos eventos',
  'profile.events.past': 'Eventos pasados',
  'profile.events.searchPlaceholder': 'Buscar eventos…',
  'profile.events.emptyUpcomingTitle': 'No hay eventos próximos',
  'profile.events.emptyUpcomingSubtitle': 'Crea un proyecto para empezar',
  'profile.events.emptyPastTitle': 'Aún no hay eventos pasados.',
  'profile.brands.emptyTitle': 'Marcas que gestionas',
  'profile.brands.emptySubtitle': 'Aquí verás las marcas asociadas a tu cuenta.',
  'profile.brands.nextEvent': 'Próximo evento',
  'profile.brands.noUpcoming': 'Sin eventos próximos',
  'profile.team.emptyTitle': 'Miembros del equipo',
  'profile.team.emptySubtitle': 'Invita colaboradores para verlos aquí.',
  'profile.activity.emptyTitle': 'Aún no hay actividad',
  'profile.activity.emptySubtitle':
    'Los recursos que crees, edites o elimines aparecerán aquí.',
  'profile.activity.loadErrorTitle': 'No se pudo cargar la actividad',
  'profile.activity.loadErrorSubtitle': 'Inténtalo de nuevo en un momento.',
  'profile.activity.lastActivity': 'Última actividad {date}',
  'profile.activity.historyToggle': 'Historial ({count})',
  'profile.activity.badgeDeleted': 'Eliminado',
  'profile.activity.noun': 'recursos',
  'profile.activity.history.created': 'Creó este {type}',
  'profile.activity.history.updated': 'Editó {fields}',
  'profile.activity.history.updatedGeneric': 'Editó este {type}',
  'profile.activity.history.deleted': 'Eliminó este {type}',
  'profile.activity.history.restored': 'Restauró este {type}',
  'profile.activity.type.brand': 'marca',
  'profile.activity.type.event': 'evento',
  'profile.activity.type.project': 'proyecto',
  'profile.activity.type.station': 'estación',
  'profile.sidebar.experience': 'Mi experiencia',
  'profile.sidebar.contact': 'Información de contacto',
  'profile.sidebar.professional': 'Profesional',
  'profile.sidebar.additional': 'Información adicional',
  'profile.sidebar.preferences': 'Preferencias',
  'profile.sidebar.languages': 'Idiomas',
  'profile.sidebar.interests': 'Intereses',
  'profile.sidebar.editProfile': 'Editar perfil',
  'profile.sidebar.changePassword': 'Cambiar contraseña',
  'profile.sidebar.theme': 'Tema',
  'profile.sidebar.language': 'Idioma',
  'profile.sidebar.noBio':
    'Aún no hay biografía. Haz clic en "Editar perfil" para agregar tu experiencia.',

  // ── Pagination ─────────────────────────────────────────────────────
  'pagination.previous': 'Anterior',
  'pagination.next': 'Siguiente',
  'pagination.previousPage': 'Página anterior',
  'pagination.nextPage': 'Página siguiente',
  'pagination.showing': 'Mostrando',
  'pagination.of': 'de',
  'pagination.items': 'elementos',

  // ── Global search ──────────────────────────────────────────────────
  'globalSearch.placeholder': 'Buscar lo que sea — eventos, marcas, estaciones, stock',
  'globalSearch.clear': 'Limpiar búsqueda',
  'globalSearch.noMatches': 'Sin resultados para',
  'globalSearch.kind.brand': 'Marcas',
  'globalSearch.kind.event': 'Eventos',
  'globalSearch.kind.station': 'Estaciones',
  'globalSearch.kind.stock': 'Stock',
  'globalSearch.subBrand': 'Marca',
  'globalSearch.subEvent': 'Evento',
  'globalSearch.subStation': 'Estación',
  'globalSearch.subStockItem': 'Artículo de stock',

  // ── Right sidebar / AI Assistant ───────────────────────────────────
  'assistant.title': 'Atledi',
  'assistant.collapse': 'Contraer asistente',
  'assistant.expand': 'Abrir asistente',
  // Caso 07 — Etiquetas del launcher + drawer.
  'assistant.launcher.aria': 'Abrir asistente IA',
  'assistant.drawer.aria': 'Asistente IA',
  'assistant.kickoffPending': 'Leyendo contexto de la página…',
  'assistant.placeholder': 'Pregunta lo que quieras sobre esta página…',
  'assistant.send': 'Enviar',
  'assistant.suggestions': 'Sugerencias',
  'assistant.error': 'No pude conectar con el asistente. Intenta de nuevo.',
  'assistant.error.unavailable': 'El asistente no está disponible por el momento. Inténtalo en un momento.',
  'assistant.retry': 'Reintentar',

  // ── Export XLSX ────────────────────────────────────────────────────
  'export.button': 'Exportar XLSX',
  'export.exporting': 'Exportando…',
  'export.nothingTitle': 'Nada para exportar',
  'export.nothingBody': 'La lista está vacía.',
  'export.readyTitle': 'Exportación lista',
  'export.readyBodyOne': 'fila descargada.',
  'export.readyBodyMany': 'filas descargadas.',
  'export.failedTitle': 'Error al exportar',

  // ── Forms / file uploader ──────────────────────────────────────────
  'forms.colorField.pickColor': 'Elegir color',
  'forms.uploader.formats': 'PNG, JPG, WEBP o SVG',
  'forms.uploader.uploadFailed': 'Error al subir',
  'forms.uploader.cancel': 'Cancelar',
  'forms.uploader.edit': 'Editar',
  'forms.uploader.remove': 'Quitar',
  'forms.uploader.retry': 'Reintentar',
  'forms.imageEditor.title': 'Editar imagen',
  'forms.imageEditor.zoomOut': 'Alejar',
  'forms.imageEditor.zoomIn': 'Acercar',
  'forms.imageEditor.rotateLeft': 'Girar izquierda',
  'forms.imageEditor.rotateRight': 'Girar derecha',
  'forms.imageEditor.zoom': 'Zoom',
  'forms.imageEditor.brightness': 'Brillo',
  'forms.imageEditor.contrast': 'Contraste',
  'forms.imageEditor.saturation': 'Saturación',
  'forms.imageEditor.reset': 'Restablecer',
  'forms.imageEditor.cancel': 'Cancelar',
  'forms.imageEditor.apply': 'Aplicar recorte',
  'forms.imageEditor.hint': 'Arrastra para reubicar · Rueda para zoom · Botones ± para control fino',
  'forms.rating.starOne': '1 estrella',
  'forms.rating.starMany': 'estrellas',

  // ── Common buttons / states ────────────────────────────────────────
  'common.saving': 'Guardando…',
  'common.next': 'Siguiente',
  'common.back': 'Atrás',
  'common.continue': 'Continuar',
  'common.skip': 'Omitir',
  'common.submit': 'Enviar',
  'common.confirm': 'Confirmar',
  'common.add': 'Agregar',
  'common.remove': 'Quitar',
  'common.update': 'Actualizar',
  'common.optional': 'Opcional',
  'common.required': 'Obligatorio',
  'common.yes': 'Sí',
  'common.no': 'No',
  'common.retry': 'Reintentar',
  'common.viewAll': 'Ver todo',
  'common.error': 'Algo salió mal',
  'common.tryAgain': 'Inténtalo de nuevo.',
  'common.empty': 'Aún no hay nada aquí',
  'common.copy': 'Copiar',
  'common.copied': 'Copiado',
  'common.draft': 'Borrador',
  'common.active': 'Activo',
  'common.disabled': 'Deshabilitado',
  'common.enabled': 'Habilitado',
  'common.preview': 'Vista previa',
  'common.print': 'Imprimir',
  'common.download': 'Descargar',
  'common.duplicate': 'Duplicar',
  'common.archive': 'Archivar',
  'common.locked': 'Bloqueado',
  'common.unlocked': 'Desbloqueado',

  // ── Onboarding ─────────────────────────────────────────────────────
  'onboarding.brandProfile.title': 'Cuéntanos sobre tu marca',
  'onboarding.brandProfile.subtitle': 'Algunos datos para personalizar tu espacio.',
  'onboarding.brandProfile.brandName': 'Nombre de la marca',
  'onboarding.brandProfile.brandNamePlaceholder': 'p. ej. Atledi',
  'onboarding.brandProfile.industry': 'Industria',
  'onboarding.brandProfile.industryPlaceholder': 'Selecciona una industria',
  'onboarding.brandProfile.website': 'Sitio web',
  'onboarding.brandProfile.websitePlaceholder': 'https://tumarca.com',
  'onboarding.brandProfile.about': 'Acerca de',
  'onboarding.brandProfile.aboutPlaceholder': 'Breve descripción de lo que hacen.',
  'onboarding.brandProfile.continue': 'Continuar',
  'onboarding.brandKit.title': 'Agrega tu kit de marca',
  'onboarding.brandKit.subtitle': 'Sube tu logo y elige los colores de tu marca.',
  'onboarding.brandKit.logo': 'Logo',
  'onboarding.brandKit.primaryColor': 'Color primario',
  'onboarding.brandKit.accentColor': 'Color de acento',
  'onboarding.brandKit.finish': 'Finalizar configuración',
  'onboarding.brandKit.skip': 'Omitir por ahora',
  'onboarding.profile.title': 'Cuéntanos sobre tu marca',
  'onboarding.profile.subtitle': 'Activaremos tu espacio gratuito enseguida.',
  'onboarding.profile.companyName': 'Nombre de la empresa',
  'onboarding.profile.companyNamePlaceholder': 'Refresco Lima S.A.',
  'onboarding.profile.yourName': 'Tu nombre',
  'onboarding.profile.yourNamePlaceholder': 'Juana Fundadora',
  'onboarding.profile.industry': 'Industria',
  'onboarding.profile.pickOne': 'Elige una',
  'onboarding.profile.continue': 'Continuar',
  'onboarding.profile.savedTitle': 'Perfil guardado',
  'onboarding.profile.savedBody': 'Tu plan gratuito está activo. Un paso más.',
  'onboarding.profile.errorTitle': 'No se pudo guardar',
  'onboarding.kit.title': 'Arma tu BrandKit',
  'onboarding.kit.subtitle': 'Usaremos estos valores predeterminados en journeys, ruleta e impresos.',
  'onboarding.kit.campaignName': 'Nombre de la campaña',
  'onboarding.kit.campaignNamePlaceholder': 'Tour de Verano 2026',
  'onboarding.kit.primary': 'Primario',
  'onboarding.kit.secondary': 'Secundario',
  'onboarding.kit.font': 'Fuente',
  'onboarding.kit.clientLogo': 'Logo del cliente',
  'onboarding.kit.finish': 'Finalizar configuración',
  'onboarding.kit.savedTitle': '¡BrandKit guardado!',
  'onboarding.kit.savedBody': 'Bienvenido a Xotski.',
  'onboarding.kit.errorTitle': 'No se pudo guardar el BrandKit',

  // ── Stock ──────────────────────────────────────────────────────────
  'stock.title': 'Xotskies',
  'stock.subtitle': 'Gestiona los artículos que entregas en tus activaciones.',
  'stock.addButton': 'Agregar xotski',
  'stock.empty.title': 'Aún no hay xotskies',
  'stock.empty.body': 'Agrega tu primer xotski para empezar a activar.',
  'stock.empty.cta': 'Agregar xotski',
  'stock.column.name': 'Nombre',
  'stock.column.rating': 'Probabilidad',
  'stock.column.classification': 'Clasificación',
  'stock.column.status': 'Estado',
  'stock.column.actions': 'Acciones',
  'stock.searchPlaceholder': 'Buscar xotskies…',
  'stock.confirmDelete': '¿Eliminar este xotski?',
  'stock.deletedTitle': 'Xotski eliminado',
  'stock.savedTitle': 'Xotski guardado',
  'stock.errorTitle': 'No se pudo guardar el xotski',
  'stock.dialog.addTitle': 'Agregar xotski',
  'stock.dialog.editTitle': 'Editar xotski',
  'stock.dialog.name': 'Nombre',
  'stock.dialog.namePlaceholder': 'Botella muestra, postal, sticker…',
  'stock.dialog.translate': 'Nombre en español',
  'stock.dialog.translatePlaceholder': 'Traducción opcional',
  'stock.dialog.image': 'Imagen',
  'stock.dialog.logo': 'Logo',
  'stock.dialog.rating': 'Probabilidad en la ruleta',
  'stock.dialog.classification': 'Clasificación',
  'stock.dialog.classificationPlaceholder': 'Bebida, swag, cupón…',
  'stock.dialog.status': 'Estado',
  'stock.list.title': 'Mi Merch',
  'stock.list.subtitle': 'Los artículos de stock que aparecen en tus ruletas e impresos.',
  'stock.list.addButton': 'Agregar xotski',
  'stock.list.filter': 'Filtro',
  'stock.list.allClassifications': 'Todas las clasificaciones',
  'stock.list.unclassified': 'Sin clasificar',
  'stock.list.itemOne': 'artículo',
  'stock.list.itemMany': 'artículos',
  'stock.list.emptyTitle': 'Sin artículos de stock',
  'stock.list.emptyBody': 'Agrega los productos y merch que tus invitados pueden ganar.',
  'stock.list.allClassified': 'Todo está clasificado',
  'stock.list.allClassifiedBody': 'Cada artículo tiene una etiqueta SEE / ENGAGE / TRY / ACT.',
  'stock.list.noneInClass': 'No hay xotskies clasificados como',
  'stock.list.editToSetClass': 'Edita un xotski y asigna su clasificación a',
  'stock.list.editToSetClassSuffix': 'para verlo aquí.',
  'stock.list.createdTitle': 'Xotski agregado al inventario',
  'stock.list.updatedTitle': 'Xotski actualizado',
  'stock.list.createFailed': 'Error al crear',
  'stock.list.updateFailed': 'Error al actualizar',
  'stock.card.edit': 'Editar',
  'stock.card.delete': 'Eliminar',
  'stock.card.rarityTier': 'Nivel de rareza',
  'stock.card.rarityLabel.1': 'Común',
  'stock.card.rarityLabel.2': 'Poco común',
  'stock.card.rarityLabel.3': 'Raro',
  'stock.card.rarityLabel.4': 'Épico',
  'stock.card.rarityLabel.5': 'Legendario',
  'stock.card.qty': 'Cant.',
  'stock.card.ratingOf': 'Valoración',
  'stock.dialog.editingTitle': 'Editar xotski',
  'stock.dialog.addingTitle': 'Agregar xotski',
  'stock.dialog.updateHeading': 'Actualizar',
  'stock.dialog.updateHeadingFallback': 'merch',
  'stock.dialog.newHeading': '¿Cuál es el nuevo merch?',
  'stock.dialog.editLead': 'Ajusta lo básico — los cambios se aplican donde sea que aparezca este xotski.',
  'stock.dialog.newLead': 'Define lo básico. Asignarás cantidades a estaciones específicas desde Mis Xotskies más tarde.',
  'stock.dialog.section.identity': 'Identidad',
  'stock.dialog.section.visuals': 'Visuales',
  'stock.dialog.section.inventory': 'Inventario',
  'stock.dialog.section.funnel': 'Etapa del funnel',
  'stock.dialog.section.rarity': 'Rareza',
  'stock.dialog.field.name': 'Nombre',
  'stock.dialog.field.namePlaceholder': 'p. ej. Pin Joyly esmaltado',
  'stock.dialog.field.translate': 'Nombre en español (traducción)',
  'stock.dialog.field.translatePlaceholder': 'Se muestra cuando la journey corre en en-es',
  'stock.dialog.field.description': 'Descripción',
  'stock.dialog.field.descriptionPlaceholder': 'Una descripción breve mostrada al ganar el premio…',
  'stock.dialog.field.productImage': 'Imagen del producto',
  'stock.dialog.field.wheelLogo': 'Logo en la ruleta',
  'stock.dialog.field.initialQty': 'Cantidad inicial',
  'stock.dialog.field.unitCost': 'Costo unitario (USD)',
  'stock.dialog.field.wheelLogoLibrary': 'O elige de la biblioteca',
  'stock.dialog.field.wheelLogoLibraryEmpty':
    'Sin logos curados aún. Coloca archivos en ox-platform/uploads/wheel-logos/ para poblar la biblioteca.',
  'stock.dialog.field.restockThreshold': 'Umbral de reabastecimiento',
  'stock.dialog.field.restockPlaceholder': 'Alertar por debajo de…',
  'stock.dialog.field.classification': 'Clasificación (SEE · ENGAGE · TRY · ACT)',
  'stock.dialog.field.classUnset': 'Sin asignar',
  'stock.dialog.field.rarityTier': 'Nivel de rareza (define la probabilidad en la ruleta)',
  'stock.dialog.weight.weightOn': 'Peso en la ruleta:',
  'stock.dialog.weight.hint': '1 = común (ganancia frecuente), 5 = legendario (drop raro).',
  'stock.dialog.fixFieldsOne': 'Por favor arregla 1 campo antes de guardar:',
  'stock.dialog.fixFieldsMany': 'Por favor arregla {count} campos antes de guardar:',
  'stock.dialog.cancel': 'Cancelar',
  'stock.dialog.saving': 'Guardando…',
  'stock.dialog.saveChanges': 'Guardar cambios',
  'stock.dialog.addToInventory': 'Agregar al inventario',
  'stock.dialog.error.nameLen': 'El nombre debe tener al menos 2 caracteres',
  'stock.dialog.error.qtyGt0': 'La cantidad inicial debe ser mayor a 0',
  'stock.dialog.error.qtyInt': 'Debe ser un número entero',
  'stock.dialog.error.costNeg': 'El costo unitario no puede ser negativo',
  'stock.dialog.error.threshNeg': 'El umbral no puede ser negativo',
  'stock.dialog.error.threshExceeds': 'El umbral no puede exceder la cantidad inicial',
  'stock.dialog.error.ratingRange': 'La valoración debe estar entre 1 y 5',
  'stock.dialog.classHint.SEE': 'Awareness — solo visibilidad',
  'stock.dialog.classHint.ENGAGE': 'Engagement — pequeño touchpoint',
  'stock.dialog.classHint.TRY': 'Trial — degustación / muestreo',
  'stock.dialog.classHint.ACT': 'Action — conversión de alta intención',

  // ── Stations ───────────────────────────────────────────────────────
  'stations.title': 'Estaciones',
  'stations.subtitle': 'Donde tu equipo entrega xotskies el día del evento.',
  'stations.addButton': 'Nueva estación',
  'stations.empty.title': 'Aún no hay estaciones',
  'stations.empty.body': 'Agrega una estación para asignar xotskies y capturar interactions.',
  'stations.empty.cta': 'Crear estación',
  'stations.searchPlaceholder': 'Buscar estaciones…',
  'stations.column.name': 'Nombre',
  'stations.column.event': 'Evento',
  'stations.column.surveys': 'Journeys',
  'stations.column.status': 'Estado',
  'stations.confirmDelete': '¿Eliminar esta estación?',
  'stations.deletedTitle': 'Estación eliminada',
  'stations.savedTitle': 'Estación guardada',
  'stations.dialog.addTitle': 'Nueva estación',
  'stations.dialog.editTitle': 'Editar estación',
  'stations.dialog.name': 'Nombre de la estación',
  'stations.dialog.event': 'Evento',
  'stations.dialog.surveys': 'Journeys',
  'stations.dialog.status': 'Estado',
  'stations.detail.qrCode': 'Código QR',
  'stations.detail.printQr': 'Imprimir QR',
  'stations.detail.copyLink': 'Copiar enlace',
  'stations.detail.linkCopied': 'Enlace copiado al portapapeles',
  'stations.detail.stockAssigned': 'Xotskies asignados',
  'stations.detail.noStock': 'Sin xotskies asignados a esta estación.',
  'stations.detail.responses': 'Interactions',
  'stations.detail.noResponses': 'Aún no se han capturado interactions.',
  'stations.print.title': 'Imprimir códigos QR de estaciones',
  'stations.print.instructions': 'Usa el diálogo de impresión de tu navegador para enviar estos QR a una impresora o guardarlos como PDF.',
  'stations.list.subtitleLong': 'Cada QR vive en una estación. Cada estación pertenece a un evento.',
  'stations.list.emptyDesc': 'Crea estaciones dentro de los eventos de un proyecto.',
  'stations.list.goToProjects': 'Ir a proyectos',
  'stations.list.allStations': 'Todas las estaciones',
  'stations.list.column.survey': 'Journey',
  'stations.list.column.actions': 'Acciones',
  'stations.list.allocate': 'Asignar',
  'stations.list.qrShort': 'QR',
  'stations.detail.notFound': 'Estación no encontrada',
  'stations.detail.back': 'Atrás',
  'stations.detail.eventLabel': 'Evento',
  'stations.detail.surveyLabel': 'Journey',
  'stations.detail.edit': 'Editar',
  'stations.detail.viewQr': 'Ver código QR',
  'stations.detail.allocationsTitle': 'Asignaciones de stock',
  'stations.detail.stock': 'Stock',
  'stations.detail.loadingStocks': 'Cargando catálogo de stock…',
  'stations.detail.allAllocated': 'Todo el stock de la marca ya está asignado a esta estación',
  'stations.detail.noStocksForBrand': 'Esta marca aún no tiene stock',
  'stations.detail.createStocksCta': 'Crear stock',
  'stations.detail.available': 'disponibles',
  'stations.detail.pickStock': 'Elige un artículo de stock',
  'stations.detail.quantity': 'Cantidad',
  'stations.detail.qtyError': 'Debe ser ≥ 1',
  'stations.detail.overrideRating': 'Sobrescribir probabilidad',
  'stations.detail.usesDefault': 'Usa el valor por defecto',
  'stations.detail.wheelWeight': 'Peso en la ruleta:',
  'stations.detail.allocate': 'Asignar',
  'stations.detail.noAllocations': 'Aún no hay asignaciones.',
  'stations.detail.stockCol': 'Stock',
  'stations.detail.ratingCol': 'Probabilidad',
  'stations.detail.availTotalCol': 'Disponible / total',
  'stations.detail.out': 'Agotado',
  'stations.detail.removeAria': 'Quitar',
  'stations.detail.allocatedToast': 'Stock asignado',
  'stations.detail.allocateFailed': 'Error al asignar',
  'stations.detail.missingData': 'Datos faltantes',
  'stations.detail.brandPool': 'Inventario de marca',
  'stations.detail.exceedsBrandPool': 'Excede el inventario de marca',
  // PRD §9 v2 — Event Prize Pool picker (ES).
  'eventPrizePool.title': 'Premios elegibles para este evento',
  'eventPrizePool.subtitle':
    'Selecciona qué premios del brand pueden asignarse a las stations de este evento. Vacío = se permiten todos los premios del brand (retro-compatibilidad).',
  'eventPrizePool.brandEmpty':
    'El brand aún no tiene premios adoptados. Abre Brand Studio → Premios para adoptar del catálogo de la agency.',
  'eventPrizePool.noPoolHint':
    'No hay premios seleccionados — las stations verán el catálogo completo del brand.',
  'eventPrizePool.unitsRemain': 'unidades',
  'eventPrizePool.savedToast': 'Pool de premios actualizado',
  'eventPrizePool.saveFailed': 'No se pudo actualizar el pool de premios',
  // ── Station Detail v2 (header + tabs) ────────────────────────────
  'stations.detail.inventoryTab': 'Inventario',
  'stations.detail.responsesTab': 'Interactions',
  'stations.detail.dashboardTab': 'Dashboard',
  'stations.detail.usedCol': 'Usado',
  'stations.detail.totalAllocated': 'Total',
  'stations.detail.totalUsed': 'Usado',
  'stations.detail.totalRemaining': 'Restante',
  'stations.detail.noSurveyAssigned': 'Aún no hay journey asignado a esta station.',
  'stations.detail.responsesOverTime': 'Interactions en el tiempo',
  'stations.detail.stockDistribution': 'Stock entregado por item',
  'stations.detail.inventoryDonut': 'Inventario usado vs restante',
  'stations.detail.topAnswers': 'Interactions más comunes',
  'stations.detail.dashboardEmpty': 'Aún no hay datos suficientes para esta gráfica — vuelve cuando empiecen a llegar interactions.',
  'stations.detail.responseTimestamp': 'Cuándo',
  'stations.detail.responseSummary': 'Vista previa',
  'stations.detail.prevPage': 'Página anterior',
  'stations.detail.nextPage': 'Página siguiente',
  'stations.editDialog.title': 'Editar estación',
  'stations.editDialog.description': 'Renombra o reasigna la journey. Las asignaciones se mantienen.',
  'stations.editDialog.stationName': 'Nombre de la estación',
  'stations.editDialog.nameRequired': 'El nombre de la estación es obligatorio',
  'stations.editDialog.survey': 'Journey',
  'stations.editDialog.surveyDisabledTitle': 'La journey se configura en la pestaña Design Journey del evento. Abre el evento para cambiarla.',
  'stations.editDialog.noSurvey': '— sin journey —',
  'stations.editDialog.template': 'plantilla',
  'stations.editDialog.global': 'global',
  'stations.editDialog.surveyHintPre': 'Configurado en la pestaña',
  'stations.editDialog.surveyHintLink': 'Design Journey',
  'stations.editDialog.surveyHintPost': 'del evento — aplica a todas las estaciones de este evento.',
  'stations.editDialog.confirmHeader': 'Eliminar estación',
  'stations.editDialog.confirmBody': 'La estación y sus asignaciones de stock se ocultarán de los listados.',
  'stations.editDialog.confirmInstruction': 'Haz clic en',
  'stations.editDialog.confirmInstructionEmph': 'Confirmar eliminación',
  'stations.editDialog.confirmInstructionEnd': 'para proceder.',
  'stations.editDialog.deleting': 'Eliminando…',
  'stations.editDialog.confirmDelete': 'Confirmar eliminación',
  'stations.editDialog.delete': 'Eliminar',
  'stations.editDialog.cancel': 'Cancelar',
  'stations.editDialog.save': 'Guardar',
  'stations.editDialog.updatedToast': 'Estación actualizada',
  'stations.editDialog.updateFailed': 'Error al actualizar',
  'stations.editDialog.deletedToast': 'Estación eliminada',
  'stations.editDialog.deleteFailed': 'Error al eliminar',
  'stations.qrPrint.noSurvey': 'Esta estación aún no tiene journey. Asigna una en Journeys → Editor.',
  'stations.qrPrint.back': 'Atrás',
  'stations.qrPrint.title': 'Código QR',
  'stations.qrPrint.shortUrl': 'URL corta',
  'stations.qrPrint.sharedNote': 'Esta estación comparte un QR con otras — el código de abajo abre el selector de estación compartido. Al escanearlo, el consumidor elige primero su estación.',
  'stations.qrPrint.fullUrl': 'URL completa',
  'stations.qrPrint.downloadSvg': 'Descargar SVG',
  'stations.qrPrint.print': 'Imprimir',
  'stations.qrPrint.openNewTab': 'Abrir en nueva pestaña',

  // ── Events ─────────────────────────────────────────────────────────
  'events.detail.startDate': 'Inicio',
  'events.detail.endDate': 'Fin',
  'events.detail.address': 'Dirección',
  'events.detail.codeRef': 'Código de referencia',
  'events.detail.sectionCount': 'Secciones',
  'events.detail.stations': 'Estaciones',
  'events.detail.noStations': 'Aún no hay estaciones en este evento.',
  'events.detail.addStation': 'Agregar estación',
  'events.detail.surveyConfig': 'Design Journey',
  'events.detail.savedTitle': 'Evento guardado',
  'events.detail.deletedTitle': 'Evento eliminado',
  'events.detail.confirmDelete': '¿Eliminar este evento?',
  'events.dialog.editTitle': 'Editar evento',
  'events.dialog.name': 'Nombre del evento',
  'events.dialog.address': 'Dirección',
  'events.dialog.startDate': 'Fecha de inicio',
  'events.dialog.endDate': 'Fecha de fin',
  'events.dialog.sectionCount': 'Número de secciones',
  'events.dialog.codeRef': 'Código de referencia',
  'events.dialog.status': 'Estado',
  'events.survey.title': 'Design Journey',
  'events.survey.subtitle': 'Elige una plantilla o arma un flujo a medida para este evento.',
  'events.survey.outputs': 'Salidas',
  'events.survey.preview': 'Vista previa',
  'events.survey.savedTitle': 'Configuración guardada',
  'events.survey.endpoint': 'URL del endpoint',
  'events.survey.endpointPlaceholder': 'https://hooks.example.com/onxite',
  'events.survey.translatable': 'Traducible',
  'events.survey.translatableHint': 'Muestra el selector de idioma a los respondientes.',
  'events.survey.locked': 'Bloquear plantilla',
  'events.survey.lockedHint': 'Evita ediciones accidentales al cuerpo de la journey.',
  'events.survey.preview.empty': 'Agrega pasos y componentes para ver la vista previa.',
  'events.detail.notFound': 'Evento no encontrado',
  'events.detail.backToProject': 'Volver al proyecto',
  'events.detail.unknown': 'Desconocido',
  'events.detail.tbd': 'Por definir',
  'events.detail.sectionOne': 'sección',
  'events.detail.sectionMany': 'secciones',
  'events.detail.editEvent': 'Editar evento',
  'events.detail.viewReport': 'Ver reporte',
  // Caso 10 — Atajo al monitor en vivo desde el header del evento.
  'events.detail.openLiveMonitor': 'Monitor en vivo',
  'events.detail.liveTitle': 'Monitoreo en vivo',
  'events.detail.reportTitle': 'Reporte del evento',
  // Caso 12 — Card de metas del evento.
  'goals.title': 'Metas y avance',
  'goals.description':
    'Define metas por evento. El avance se calcula con las mismas métricas del reporte.',
  'goals.empty': 'Aún no hay metas. Elige una métrica abajo para empezar.',
  'goals.targetPlaceholder': 'Meta',
  'goals.metric.samples': 'Muestras',
  'goals.metric.responses': 'Interactions',
  'goals.metric.leads': 'Leads',
  'goals.metric.prizesDelivered': 'Premios entregados',
  'goals.metric.completionRate': 'Tasa de finalización',
  'goals.status.on-track': 'En camino',
  'goals.status.at-risk': 'En riesgo',
  'goals.status.met': 'Cumplida',
  // Caso 11 — Vista móvil del operador del booth.
  'operator.title': 'Modo operador',
  'operator.back': 'Volver a estaciones',
  'operator.invalidId': 'Identificador de estación inválido.',
  'operator.unknownEvent': 'Evento desconocido',
  'operator.surveyActive': 'Journey activa: {name}',
  'operator.noSurvey': 'Sin journey asignada aún.',
  'operator.cta.redeem': 'Canjear',
  'operator.cta.showQr': 'Mostrar QR',
  'operator.stock.title': 'Premios restantes',
  'operator.stock.totalRemaining': '{count} en total',
  'operator.stock.left': 'restan',
  'operator.stock.empty': 'No hay premios asignados a esta estación.',
  'operator.stock.lowAlert':
    '{count} premio(s) por agotarse — avisa al coordinador.',
  'stations.detail.operatorMode': 'Modo operador',
  // Caso 11 v3 — Tokens de acceso del operador.
  'stations.detail.generateOperatorLink': 'Enlace de operador',
  'stations.detail.tokenReady': 'Enlace de operador listo',
  'stations.detail.tokenWarning':
    'Esta URL da acceso al modo operador. Cópiala ahora — no la mostraremos otra vez.',
  'stations.detail.tokenExpiresAt': 'Expira {when}',
  'stations.detail.tokenCopy': 'Copiar URL',
  'stations.detail.tokenCopied': 'Copiado al portapapeles.',
  'stations.detail.tokenCopyFailed':
    'No se pudo copiar. Selecciona la URL manualmente.',
  'stations.detail.tokenMintFailed':
    'No se pudo generar el enlace de operador.',
  // Caso 11 v4 — login del operador (intercambio de token).
  'operatorLogin.title': 'Iniciando tu sesión…',
  'operatorLogin.subtitle': 'Intercambiando tu enlace por una sesión de operador.',
  'operatorLogin.errorTitle': 'No se pudo iniciar tu sesión',
  'operatorLogin.missingToken':
    'Este enlace no tiene el token de acceso.',
  'operatorLogin.exchangeFailed':
    'El enlace es inválido, fue revocado, o expiró. Pide uno nuevo al manager.',
  // Caso 16 — Etiquetas de migas de pan.
  'breadcrumbs.brand': 'Marca',
  'breadcrumbs.project': 'Proyecto',
  'breadcrumbs.event': 'Evento',
  'breadcrumbs.settings': 'Ajustes',

  // ── Caso 09 — Panel de demografía ───────────────────────────────
  'demographics.title': 'Demografía',
  'demographics.subtitle':
    '{count} contactos con datos demográficos registrados.',
  'demographics.ageRange': 'Rango de edad',
  'demographics.gender': 'Género',
  'demographics.ethnicity': 'Etnia',
  'demographics.city': 'Ciudades principales',
  'demographics.state': 'Estados principales',
  'demographics.language': 'Idiomas principales',
  'demographics.genderValue.female': 'Femenino',
  'demographics.genderValue.male': 'Masculino',
  'demographics.genderValue.non-binary': 'No binario',
  'demographics.genderValue.other': 'Otro',
  'demographics.genderValue.undisclosed': 'No declarado',
  'demographics.brand.noEvents':
    'Esta marca aún no tiene eventos — la demografía aparecerá cuando un evento corra con una journey que recolecte edad, género o ubicación.',
  'demographics.brand.noData':
    'Se revisaron {count} evento(s); ninguno tiene interactions demográficas todavía. Asegúrate de que las journeys de la marca recolecten edad, género o ubicación.',
  // Caso 10 — Monitor en vivo.
  'liveMonitor.invalidId': 'Identificador de evento inválido.',
  'liveMonitor.back': 'Volver al reporte',
  'liveMonitor.live': 'EN VIVO',
  'liveMonitor.refreshing': 'Actualizando…',
  // Caso 10 v2 — Indicador de transporte (WS push vs polling).
  'liveMonitor.transport.live': 'Tiempo real',
  'liveMonitor.transport.polling': 'Sondeo',
  'liveMonitor.unknown': 'Evento',
  'liveMonitor.kpi.interactions': 'Interacciones',
  'liveMonitor.kpi.last24h': 'Últimas 24 h',
  'liveMonitor.kpi.leads': 'Leads',
  'liveMonitor.kpi.prizesDistributed': 'Premios entregados',
  'liveMonitor.alert.lowStock':
    'Solo quedan {count} premio(s) en todas las estaciones.',
  'eventReport.openLiveMonitor': 'Abrir monitor en vivo',
  'events.detail.stationsTab': 'Estaciones',
  'events.detail.surveyTab': 'Design Journey',
  'events.detail.touchpoints': 'Estaciones / Touchpoints',
  'events.detail.eventSurvey': 'Journey del evento:',
  'events.detail.sharedByStations': '· compartida por todas las estaciones',
  'events.detail.noEventSurveyPre': 'Aún no hay journey del evento — elige una plantilla en la pestaña',
  'events.detail.noEventSurveyTab': 'Design Journey',
  'events.detail.noEventSurveyPost': '.',
  'events.detail.newStation': 'Nueva estación',
  'events.detail.stationName': 'Nombre de la estación',
  'events.detail.stationNamePlaceholder': 'Barra principal',
  'events.detail.surveyAutoPre': 'asignada automáticamente desde la configuración de journey de este evento (definida en la pestaña',
  'events.detail.surveyAutoTab': 'Design Journey',
  'events.detail.surveyAutoPost': '→ elige una plantilla). Todas las estaciones del evento comparten la misma journey.',
  'events.detail.surveyLabel': 'Journey:',
  'events.detail.createStation': 'Crear estación',
  'events.detail.stationCreated': 'Estación creada',
  'events.detail.createFailed': 'Error al crear',
  'events.detail.surveyNone': 'ninguna',
  'events.detail.outOfSync': 'desincronizada',
  'events.detail.noSurvey': 'sin journey',
  'events.detail.outOfSyncTitle': 'Desincronizada — la journey del evento es',
  'events.detail.outOfSyncBody': 'Usa "Re-sincronizar estaciones" en Design Journey.',
  'events.detail.noSurveyTitle': 'Sin journey asignada. Usa "Re-sincronizar estaciones" en Design Journey.',
  'events.detail.editStationAria': 'Editar estación',
  'events.detail.editBtn': 'Editar',
  'events.detail.allocateStock': 'Ver',
  'events.detail.qrShort': 'QR',
  'events.editDialog.title': 'Editar evento',
  'events.editDialog.description': 'Ajusta el horario, dirección, estado y capacidad de este evento.',
  'events.editDialog.name': 'Nombre del evento',
  'events.editDialog.address': 'Dirección',
  'events.editDialog.starts': 'Inicia',
  'events.editDialog.ends': 'Termina',
  'events.editDialog.sections': 'Secciones',
  'events.editDialog.codeRef': 'Código',
  'events.editDialog.statusField': 'Estado',
  'events.editDialog.confirmHeader': 'Eliminar',
  'events.editDialog.confirmBody': 'El evento, sus estaciones y asignaciones de stock se ocultarán de todos los listados.',
  'events.editDialog.confirmInstruction': 'Haz clic en',
  'events.editDialog.confirmInstructionEmph': 'Confirmar eliminación',
  'events.editDialog.confirmInstructionEnd': 'para proceder.',
  'events.editDialog.deleting': 'Eliminando…',
  'events.editDialog.confirmDelete': 'Confirmar eliminación',
  'events.editDialog.delete': 'Eliminar',
  'events.editDialog.cancel': 'Cancelar',
  'events.editDialog.save': 'Guardar',
  'events.editDialog.updatedToast': 'Evento actualizado',
  'events.editDialog.updateFailed': 'Error al actualizar',
  'events.editDialog.deletedToast': 'Evento eliminado',
  'events.editDialog.deleteFailed': 'Error al eliminar',
  'events.editDialog.error.nameRequired': 'El nombre del evento es obligatorio',
  'events.editDialog.error.startRequired': 'La fecha y hora de inicio es obligatoria',
  'events.editDialog.error.startInvalid': 'Fecha de inicio inválida',
  'events.editDialog.error.endRequired': 'La fecha y hora de fin es obligatoria',
  'events.editDialog.error.endInvalid': 'Fecha de fin inválida',
  'events.editDialog.error.endAfterStart': 'El fin debe ser posterior al inicio',
  'events.editDialog.error.outOfRange': 'Las fechas deben estar dentro del rango del proyecto',
  'surveyConfig.title': 'Design Journey',
  'surveyConfig.pickTemplateSubtitle': 'Elige una plantilla de xurvey para empezar a configurar este evento. La clonaremos y haremos una snapshot de la identidad de marca en el evento para que los cambios no afecten a la marca.',
  'surveyConfig.noTemplates': 'Aún no hay plantillas de xotski disponibles. Pide a tu admin publicar una en la app ox-journey.',
  'surveyConfig.stepsLabel': 'paso(s)',
  'surveyConfig.useTemplate': 'Usar plantilla',
  'surveyConfig.cloned': 'Plantilla clonada — empieza a configurar',
  'surveyConfig.based': 'Basado en la plantilla',
  'surveyConfig.resyncStations': 'Re-sincronizar estaciones',
  'surveyConfig.resyncStationsTitle': 'Apunta cada estación activa de este evento a la journey del evento',
  'surveyConfig.resetIdentity': 'Restablecer identidad',
  'surveyConfig.resetIdentityTitle': 'Re-snapshot de identidad desde la marca',
  'surveyConfig.changeTemplate': 'Cambiar plantilla',
  'surveyConfig.changeTemplateTitle': 'Cambia la plantilla de xurvey que respalda este evento',
  'surveyConfig.changeTemplateDialogTitle': 'Cambiar plantilla de journey',
  'surveyConfig.changeTemplateDialogBody': 'Elegí una nueva plantilla. La journey clonada será reemplazada y todas las estaciones de este evento serán re-apuntadas. Los overrides de identidad, experiencia y juego se mantienen.',
  'surveyConfig.changeTemplateConfirm': 'Reemplazar plantilla',
  'surveyConfig.changeTemplateCancel': 'Cancelar',
  'surveyConfig.changeTemplateSuccess': 'Plantilla reemplazada',
  'surveyConfig.currentTemplateLabel': 'Actual:',
  'surveyConfig.saveChanges': 'Guardar cambios',
  'surveyConfig.saved': 'Design Journey guardada',
  'surveyConfig.tab.identity': 'Identidad',
  'surveyConfig.tab.experience': 'Experiencia',
  'surveyConfig.tab.lookfeel': 'Look & Feel',
  'surveyConfig.tab.game': 'Juego',
  'surveyConfig.tab.prizes': 'Premios',
  'surveyConfig.tab.output': 'Salida',
  'surveyConfig.confirmResetIdentity': '¿Re-sincronizar identidad desde la marca? Se perderán las personalizaciones por evento.',
  'surveyConfig.identitySynced': 'Identidad re-sincronizada desde la marca',
  'surveyConfig.confirmSyncStations': '¿Apuntar TODAS las estaciones activas de este evento a la journey del evento? Cualquier estación que use otra journey será sobreescrita.',
  'surveyConfig.stationsInSync': 'Estaciones ya sincronizadas',
  'surveyConfig.resyncedPrefix': 'Re-sincronizadas',
  'surveyConfig.stationOne': 'estación',
  'surveyConfig.stationMany': 'estaciones',
  'surveyConfig.identity.campaignName': 'Nombre de campaña',
  'surveyConfig.identity.campaignPlaceholder': 'Felix Pago 2026',
  'surveyConfig.identity.industry': 'Industria',
  'surveyConfig.identity.industryPlaceholder': 'Bebidas, deportes, telecomunicaciones…',
  'surveyConfig.identity.font': 'Fuente',
  'surveyConfig.identity.primaryColor': 'Color primario',
  'surveyConfig.identity.secondaryColor': 'Color secundario',
  'surveyConfig.identity.accentColor': 'Color de acento',
  'surveyConfig.identity.backgroundColor': 'Color de fondo',
  'surveyConfig.identity.textColor': 'Color de texto',
  'surveyConfig.identity.brandLogo': 'URL del logo de marca',
  'surveyConfig.identity.clientLogo': 'URL del logo del cliente',
  'surveyConfig.identity.qrThumbnail': 'URL de miniatura del QR',
  'surveyConfig.identity.qrPlaceholder': 'https://… (cuadrada)',
  'surveyConfig.identity.backgroundImage': 'URL de imagen de fondo',
  'surveyConfig.exp.language': 'Idioma',
  'surveyConfig.exp.english': 'Inglés',
  'surveyConfig.exp.englishSpanish': 'Inglés + Español',
  'surveyConfig.exp.ageGate': 'Verificación de edad',
  'surveyConfig.exp.ageNone': 'Ninguna',
  'surveyConfig.exp.collectName': 'Capturar nombre',
  'surveyConfig.exp.collectEmail': 'Capturar correo',
  'surveyConfig.exp.collectPhone': 'Capturar teléfono',
  'surveyConfig.exp.demographics': 'Demografía',
  'surveyConfig.exp.age': 'Edad',
  'surveyConfig.exp.gender': 'Género',
  'surveyConfig.exp.ethnicity': 'Etnicidad',
  'surveyConfig.exp.nationality': 'Nacionalidad',
  'surveyConfig.look.buttonStyle': 'Estilo de botón',
  'surveyConfig.look.celebration': 'Tema de celebración',
  'surveyConfig.look.gameCtaEn': 'CTA del juego (EN)',
  'surveyConfig.look.gameCtaEnPlaceholder': 'Spin to win!',
  'surveyConfig.look.gameCtaEs': 'CTA del juego (ES)',
  'surveyConfig.look.gameCtaEsPlaceholder': '¡Gira para ganar!',
  'surveyConfig.look.consent': 'Texto de consentimiento',
  'surveyConfig.look.consentPlaceholder': 'Al continuar aceptas nuestros Términos…',
  'surveyConfig.game.mechanic': 'Mecánica del juego',
  'surveyConfig.game.tier': 'Nivel',
  'surveyConfig.game.taglineEn': 'Tagline de la ruleta (EN)',
  'surveyConfig.game.taglineEs': 'Tagline de la ruleta (ES)',
  'surveyConfig.game.heroImage': 'URL imagen hero de la ruleta',
  'surveyConfig.game.heroPlaceholder': 'https://… (se muestra en la pantalla de la ruleta)',
  'surveyConfig.game.none': 'Ninguno',
  'surveyConfig.prizes.empty': 'Aún no hay stock asignado como premios. Abre una Estación → "Asignar stock" para agregar artículos, luego vuelve aquí para habilitarlos / valorarlos.',
  'surveyConfig.prizes.lead': 'Stocks asignados a las estaciones de este evento. Activa/desactiva en la ruleta; haz clic en las estrellas para definir la rareza (probabilidad). La cantidad se define en la página de detalle de la estación.',
  'surveyConfig.prizes.noImg': 'sin img',
  'surveyConfig.prizes.stationLabel': 'Estación',
  'surveyConfig.prizes.qty': 'cant.',
  'surveyConfig.prizes.wheel': 'ruleta',
  'surveyConfig.prizes.hidden': 'oculto',
  'surveyConfig.prizes.ratingTitle': 'Probabilidad:',
  'surveyConfig.prizes.setRating': 'Definir probabilidad a',

  // ── Surveys ────────────────────────────────────────────────────────
  'surveys.title': 'Journeys',
  'surveys.subtitle': 'Plantillas reutilizables para tus activaciones.',
  'surveys.newButton': 'Nueva journey',
  'surveys.empty.title': 'Aún no hay journeys',
  'surveys.empty.body': 'Construye una journey para capturar interactions en tus activaciones.',
  'surveys.empty.cta': 'Crear journey',
  'surveys.searchPlaceholder': 'Buscar journeys…',
  'surveys.column.name': 'Nombre',
  'surveys.column.steps': 'Pasos',
  'surveys.column.product': 'Producto',
  'surveys.column.locked': 'Bloqueada',
  'surveys.column.status': 'Estado',
  'surveys.confirmDelete': '¿Eliminar esta journey?',
  'surveys.deletedTitle': 'Journey eliminada',
  'surveys.duplicatedTitle': 'Journey duplicada',
  'surveys.savedTitle': 'Journey guardada',
  'surveys.builder.title': 'Editor de journeys',
  'surveys.builder.addStep': 'Agregar paso',
  'surveys.builder.addComponent': 'Agregar componente',
  'surveys.builder.removeStep': 'Quitar paso',
  'surveys.builder.removeComponent': 'Quitar componente',
  'surveys.builder.stepLabel': 'Paso',
  'surveys.builder.componentLabel': 'Componente',
  'surveys.builder.componentType': 'Tipo',
  'surveys.builder.componentLabelField': 'Etiqueta',
  'surveys.builder.componentOptions': 'Opciones (una por línea)',
  'surveys.builder.namePlaceholder': 'Nombre de la journey',
  'surveys.builder.descriptionPlaceholder': 'Descripción breve',
  'surveys.builder.savedTitle': 'Journey guardada',
  'surveys.builder.preview': 'Vista previa',
  'surveys.builder.empty': 'Aún no hay pasos — agrega el primer paso para empezar.',
  'surveys.preview.title': 'Vista previa de journey',
  'surveys.preview.exit': 'Salir de vista previa',
  'surveys.preview.notFound': 'Journey no encontrada.',
  'surveys.templates.title': 'Plantillas de journey',
  'surveys.templates.subtitle': 'Empieza desde una plantilla probada y personalízala.',
  'surveys.templates.useTemplate': 'Usar plantilla',
  'surveys.templates.preview': 'Vista previa',
  'surveys.templates.empty': 'Aún no hay plantillas disponibles.',

  // ── Projects ───────────────────────────────────────────────────────
  'project.detail.kpi.events': 'Eventos',
  'project.detail.kpi.stations': 'Estaciones',
  'project.detail.kpi.responses': 'Interactions',
  'project.detail.events': 'Eventos',
  'project.detail.noEvents': 'Aún no hay eventos en este proyecto.',
  'project.detail.newEvent': 'Nuevo evento',
  'project.detail.editProject': 'Editar proyecto',
  'project.detail.deleteProject': 'Eliminar proyecto',
  'project.detail.confirmDelete': '¿Eliminar este proyecto?',
  'project.detail.deletedTitle': 'Proyecto eliminado',
  'project.detail.savedTitle': 'Proyecto guardado',
  'project.detail.notFound': 'Proyecto no encontrado.',
  'project.detail.backToBrand': 'Volver a la marca',

  // ── Contacts ───────────────────────────────────────────────────────
  'contacts.title': 'Contactos',
  'contacts.subtitle': 'Interactions de journeys capturadas en tus estaciones.',
  'contacts.empty.title': 'Aún no hay interactions',
  'contacts.empty.body': 'Lanza una journey en una estación y verás interactions aquí.',
  'contacts.searchPlaceholder': 'Buscar contactos…',
  'contacts.column.name': 'Nombre',
  'contacts.column.email': 'Correo',
  'contacts.column.phone': 'Teléfono',
  'contacts.column.event': 'Evento',
  'contacts.column.date': 'Fecha',
  'contacts.column.source': 'Fuente',
  'contacts.exportFilename': 'contactos',

  // ── Insights ───────────────────────────────────────────────────────
  'insights.title': 'Insights',
  'insights.subtitle': 'Desempeño de distribución por marca, evento e inventario.',
  'insights.kpi.totalResponses': 'Interactions totales',
  'insights.kpi.uniqueContacts': 'Contactos únicos',
  'insights.kpi.activeStations': 'Estaciones activas',
  'insights.kpi.completionRate': 'Tasa de finalización',
  'insights.responsesByDay': 'Interactions por día',
  'insights.responsesByEvent': 'Interactions por evento',
  'insights.responsesByStation': 'Interactions por estación',
  'insights.topStock': 'Xotskies principales',
  'insights.empty.title': 'Aún no hay suficientes datos',
  'insights.empty.body': 'Una vez que empiecen a llegar interactions, verás tus insights aquí.',

  // ── Settings ───────────────────────────────────────────────────────
  'settings.title': 'Espacio de trabajo',
  'settings.subtitle': 'Preferencias del espacio y cuenta.',
  'settings.section.account': 'Cuenta',
  'settings.section.workspace': 'Espacio',
  'settings.section.notifications': 'Notificaciones',
  'settings.section.danger': 'Zona de peligro',
  'settings.workspace.name': 'Nombre del espacio',
  'settings.workspace.timezone': 'Zona horaria',
  'settings.workspace.language': 'Idioma predeterminado',
  'settings.notifications.email': 'Notificaciones por correo',
  'settings.notifications.product': 'Actualizaciones del producto',
  'settings.danger.deleteWorkspace': 'Eliminar espacio',
  'settings.danger.deleteWorkspaceHint': 'Esta acción no se puede deshacer.',
  'settings.savedTitle': 'Ajustes guardados',

  // ── Access manager ─────────────────────────────────────────────────
  'access.title': 'Administrador de accesos',
  'access.subtitle': 'Invita a tu equipo y controla quién puede hacer qué.',
  'access.tabs.members': 'Miembros',
  'access.tabs.roles': 'Roles',
  'access.tabs.grants': 'Concesiones',
  'access.members.invite': 'Invitar miembro',
  'access.members.empty.title': 'Aún no hay miembros',
  'access.members.empty.body': 'Invita a tu equipo a colaborar en tus activaciones.',
  'access.members.column.name': 'Nombre',
  'access.members.column.email': 'Correo',
  'access.members.column.role': 'Rol',
  'access.members.column.status': 'Estado',
  'access.members.dialog.inviteTitle': 'Invitar miembro',
  'access.members.dialog.editTitle': 'Editar miembro',
  'access.members.dialog.email': 'Correo',
  'access.members.dialog.role': 'Rol',
  'access.members.dialog.firstName': 'Nombre',
  'access.members.dialog.lastName': 'Apellido',
  'access.members.savedTitle': 'Miembro guardado',
  'access.members.removedTitle': 'Miembro eliminado',
  'access.members.confirmRemove': '¿Quitar a este miembro?',
  'access.roles.new': 'Nuevo rol',
  'access.roles.empty.title': 'Aún no hay roles',
  'access.roles.empty.body': 'Crea un rol para agrupar permisos para tu equipo.',
  'access.roles.column.name': 'Nombre',
  'access.roles.column.members': 'Miembros',
  'access.roles.column.permissions': 'Permisos',
  'access.roles.dialog.newTitle': 'Nuevo rol',
  'access.roles.dialog.editTitle': 'Editar rol',
  'access.roles.dialog.name': 'Nombre del rol',
  'access.roles.dialog.description': 'Descripción',
  'access.roles.dialog.permissions': 'Permisos',
  'access.roles.savedTitle': 'Rol guardado',
  'access.roles.deletedTitle': 'Rol eliminado',
  'access.roles.confirmDelete': '¿Eliminar este rol?',
  'access.grants.new': 'Nueva concesión',
  'access.grants.empty.title': 'Aún no hay concesiones',
  'access.grants.empty.body': 'Otorga a un miembro acceso a una marca, proyecto o evento específico.',
  'access.grants.column.member': 'Miembro',
  'access.grants.column.scope': 'Alcance',
  'access.grants.column.role': 'Rol',
  'access.grants.dialog.newTitle': 'Nueva concesión',
  'access.grants.dialog.editTitle': 'Editar concesión',
  'access.grants.dialog.member': 'Miembro',
  'access.grants.dialog.scope': 'Alcance',
  'access.grants.dialog.role': 'Rol',
  'access.grants.savedTitle': 'Concesión guardada',
  'access.grants.deletedTitle': 'Concesión eliminada',
  'access.grants.confirmDelete': '¿Quitar esta concesión?',
  'access.permissions.available': 'Disponibles',
  'access.permissions.selected': 'Seleccionados',
  'access.permissions.searchPlaceholder': 'Buscar permisos…',

  // ── Brand identity ─────────────────────────────────────────────────
  'brandIdentity.title': 'Identidad de marca',
  'brandIdentity.subtitle': 'Logo, colores y voz que definen tu marca.',
  'brandIdentity.logo': 'Logo',
  'brandIdentity.primaryColor': 'Color primario',
  'brandIdentity.accentColor': 'Color de acento',
  'brandIdentity.voice': 'Voz de la marca',
  'brandIdentity.voicePlaceholder': 'Algunos adjetivos que describen cómo habla tu marca.',
  'brandIdentity.savedTitle': 'Identidad de marca guardada',

  // ── Brand studio (new page) ────────────────────────────────────────
  'brandStudio.new.title': 'Crear una nueva marca',
  'brandStudio.new.subtitle': 'Configura lo esencial. Podrás refinar todo después.',
  'brandStudio.new.brandName': 'Nombre de la marca',
  'brandStudio.new.brandNamePlaceholder': 'p. ej. Atledi',
  'brandStudio.new.logo': 'Logo',
  'brandStudio.new.primaryColor': 'Color primario',
  'brandStudio.new.accentColor': 'Color de acento',
  'brandStudio.new.create': 'Crear marca',
  'brandStudio.new.createdTitle': 'Marca creada',

  // ── Agency ─────────────────────────────────────────────────────────
  'agency.title': 'Perfil de la agencia',
  'agency.subtitle': 'Cómo se ve tu agencia ante marcas y socios.',
  'agency.section.basics': 'Básicos',
  'agency.section.team': 'Equipo',
  'agency.section.brands': 'Marcas',
  'agency.name': 'Nombre de la agencia',
  'agency.tagline': 'Eslogan',
  'agency.about': 'Acerca de',
  'agency.aboutPlaceholder': 'Una descripción breve de tu agencia.',
  'agency.logo': 'Logo',
  'agency.website': 'Sitio web',
  'agency.savedTitle': 'Agencia guardada',
  'agency.team.empty': 'Invita a tu equipo desde el Administrador de accesos.',
  'agency.brands.empty': 'Aún no hay marcas vinculadas a esta agencia.',
  'agency.home.title': 'Panel de agencia',
  'agency.home.subtitle': 'Rastrea la actividad en todas las marcas que gestionas.',
  'agency.home.activeBrands': 'Marcas activas',
  'agency.home.liveEvents': 'Eventos en vivo',
  'agency.home.totalResponses': 'Interactions totales',
  'agency.home.teammates': 'Miembros del equipo',

  // ── Billing ────────────────────────────────────────────────────────
  'billing.title': 'Facturación',
  'billing.subtitle': 'Planes, uso y facturas.',
  'billing.currentPlan': 'Plan actual',
  'billing.changePlan': 'Cambiar plan',
  'billing.upgrade': 'Mejorar',
  'billing.downgrade': 'Bajar plan',
  'billing.cancelPlan': 'Cancelar plan',
  'billing.invoices': 'Facturas',
  'billing.noInvoices': 'Aún no hay facturas.',
  'billing.usageTitle': 'Uso',
  'billing.usage.responses': 'Interactions',
  'billing.usage.stations': 'Estaciones',
  'billing.usage.events': 'Eventos',
  'billing.usage.teammates': 'Miembros',
  'billing.usage.usedOf': 'usados de',
  'billing.pricing.title': 'Planes',
  'billing.pricing.subtitle': 'Elige el plan que se adapta a tus activaciones.',
  'billing.pricing.month': '/ mes',
  'billing.pricing.year': '/ año',
  'billing.pricing.choosePlan': 'Elegir plan',
  'billing.pricing.currentPlan': 'Plan actual',
  'billing.pricing.contactSales': 'Contactar ventas',
  'billing.upgradeDialog.title': 'Mejorar plan',
  'billing.upgradeDialog.confirm': 'Confirmar mejora',
  'billing.upgradeDialog.cancel': 'Cancelar',
  'billing.checkout.successTitle': 'Pago exitoso',
  'billing.checkout.successBody': 'Tu plan se actualizó. ¡Bienvenido a bordo!',
  'billing.checkout.failedTitle': 'Pago fallido',
  'billing.checkout.failedBody': 'Algo salió mal al procesar tu pago.',
  'billing.checkout.returnToBilling': 'Volver a facturación',
  'billing.mock.title': 'Checkout de prueba',
  'billing.mock.subtitle': 'Este es un flujo de prueba — no se cobrará ningún pago real.',
  'billing.mock.pay': 'Pagar ahora',
  'billing.mock.cancel': 'Cancelar',

  // ── Public survey ──────────────────────────────────────────────────
  'publicSurvey.continue': 'Continuar',
  'publicSurvey.back': 'Atrás',
  'publicSurvey.submit': 'Enviar',
  'publicSurvey.finish': 'Finalizar',
  'publicSurvey.thanks.title': '¡Gracias por tu interaction!',
  'publicSurvey.thanks.body': 'Tus interactions han sido registradas.',
  'publicSurvey.thanks.replay': 'Girar de nuevo',
  'publicSurvey.notFound.title': 'Journey no encontrada',
  'publicSurvey.notFound.body': 'Verifica el enlace o el QR e intenta de nuevo.',
  'publicSurvey.required': 'Este campo es obligatorio.',
  'publicSurvey.invalidEmail': 'Ingresa un correo válido.',
  'publicSurvey.invalidPhone': 'Ingresa un teléfono válido.',
  'publicSurvey.wheel.spin': 'Girar la ruleta',
  'publicSurvey.wheel.spinning': 'Girando…',
  'publicSurvey.wheel.youWon': 'Ganaste',
  'publicSurvey.wheel.tryAgain': 'Intentar de nuevo',
  'publicSurvey.wheel.claim': 'Reclama tu premio',
  'publicSurvey.loading': 'Cargando journey…',

  // ── Sidebar ────────────────────────────────────────────────────────
  'sidebar.home': 'Inicio',
  'sidebar.brands': 'Marcas',
  'sidebar.assets': 'Mis assets',
  'sidebar.photos': 'Fotos',
  'sidebar.schedule': 'Mi agenda',
  'sidebar.myXotskies': 'My Merch',
  'sidebar.brandInventory': 'Inventario de marca',
  'sidebar.brandStudio': 'Estudio de marca',
  'sidebar.projects': 'Proyectos',
  'sidebar.insights': 'Métricas',
  'sidebar.contacts': 'Contactos',
  'sidebar.redeem': 'Canjear premio',
  'sidebar.profile': 'Perfil',
  'sidebar.accessManager': 'Gestor de acceso',
  'sidebar.settings': 'Configuración',
  'sidebar.admin': 'Admin',
  'sidebar.collapse': 'Colapsar menú',
  'sidebar.expand': 'Expandir menú',
  'sidebar.profileMenu.workspace': 'Espacio',
  'sidebar.profileMenu.workspaceSublabel': 'Plan · cupos · marcas · facturación',
  'sidebar.profileMenu.plan': 'Plan',
  'sidebar.profileMenu.planSublabel': 'Cupos · marcas · facturación',
  'sidebar.profileMenu.language': 'Idioma',
  'sidebar.profileMenu.languagePickerSoon': 'Selector de idioma próximamente',
  'sidebar.profileMenu.openMenu': 'abrir menú',
  'sidebar.favorites': 'Favoritos',
  'sidebar.workspace': 'Espacio de trabajo',
  'sidebar.notifications': 'Notificaciones',
  'sidebar.billing': 'Facturación',
  'sidebar.followup': 'Seguimiento',
  'sidebar.integrations': 'Integraciones',
  'sidebar.surveyBuilder': 'Design Journey',

  // ── Inventario de marca (PRD §9, modelo de inventario en dos niveles) ──
  'brandInventory.title': 'Inventario de marca',
  'brandInventory.subtitle':
    'Pool de artículos a nivel de marca. Asígnalos a proyectos, regrésalos al cerrar un evento o márcalos como baja.',
  'brandInventory.noun': 'artículos',
  'brandInventory.filterByState': 'Filtrar por estado',
  'brandInventory.allStates': 'Todos',
  'brandInventory.empty': 'Ningún artículo coincide con estos filtros.',
  'brandInventory.emptyHint': 'Captura uno desde el campo o créalo desde el admin.',
  'brandInventory.emptyNotEnabled': 'Inventario de marca no está habilitado',
  'brandInventory.emptyNotEnabledHint':
    'Habilítalo en Ajustes › Marca › Inventario para empezar a registrar artículos.',
  'brandInventory.estimated': 'estimado',
  'brandInventory.col.name': 'Nombre',
  'brandInventory.col.quantity': 'Cant.',
  'brandInventory.col.state': 'Estado',
  'brandInventory.col.rating': 'Rareza',
  'brandInventory.col.tags': 'Etiquetas',
  'brandInventory.state.unassigned': 'Sin asignar',
  'brandInventory.state.allocated': 'Asignado',
  'brandInventory.state.distributed': 'Distribuido',
  'brandInventory.state.claimed': 'Reclamado',
  'brandInventory.state.returned': 'Devuelto',
  'brandInventory.state.written_off': 'Dado de baja',

  // ── Entity Home (PRD §7.1) ─────────────────────────────────────
  'entityHome.atledi.label': 'Atledi',
  'entityHome.atledi.youFallback': 'equipo',
  'entityHome.atledi.greeting.morning':
    'Buen día, {name}. Tres días en la semana — ¿qué activación sigue en la lista?',
  'entityHome.atledi.greeting.afternoon':
    'Buenas tardes, {name}. A media tarde sube la tasa de captura. ¿Reviso tus estaciones en vivo?',
  'entityHome.atledi.greeting.evening':
    'Buenas noches, {name}. La ventana de preparación para mañana empieza ahora. ¿Armamos el brief del próximo evento?',
  // Brand home (Entity Home simplificado)
  'brandHome.cooking': '¿Qué se cocina hoy, {name}?',
  'brandHome.addXotskies': 'Agregar Merch',
  'brandHome.addXotskiesHint': 'Premios y merch al inventario',
  'brandHome.addEvents': 'Agregar eventos',
  'brandHome.addEventsHint': 'Programa una activación',
  'brandHome.addProject': 'Agregar un proyecto',
  'brandHome.addProjectHint': 'Agrupa eventos en una campaña',
  'brandHome.jumpBack': 'Retoma tus proyectos',
  'brandHome.askAtledi': 'Pregúntale algo más a Atledi…',
  'brandHome.status.planning': 'Planeación',
  'brandHome.status.wrapped': 'Cerrado',
  // Formulario de creación de evento (panel derecho)
  'eventForm.project': 'Proyecto',
  'eventForm.noProjects': 'Crea un proyecto primero — los eventos viven dentro de un proyecto.',
  'eventForm.name': 'Nombre del evento',
  'eventForm.namePlaceholder': 'p. ej. Noche de apertura',
  'eventForm.address': 'Dirección',
  'eventForm.addressPlaceholder': 'Sede o ubicación',
  'eventForm.starts': 'Inicia',
  'eventForm.ends': 'Termina',
  'eventForm.create': 'Crear evento',
  'eventForm.outOfRange': 'Las fechas del evento deben estar dentro del rango del proyecto',
  'entityHome.kpi.moments': 'Momentos capturados',
  'entityHome.kpi.throughput': 'Flujo (throughput)',
  'entityHome.kpi.inventoryHealth': 'Salud de inventario',
  'entityHome.kpi.contacts': 'Contactos capturados',
  'entityHome.kpi.comingSoon': 'Métricas en vivo, próximamente',
  'entityHome.kpi.aboveThreshold': 'Artículos arriba del umbral de stock bajo',
  'entityHome.kpi.totalDeduped': 'Contactos únicos totales',
  'entityHome.nextEvent.label': 'Mi próximo evento',
  'entityHome.nextEvent.openCta': 'Abrir evento',
  'entityHome.nextEvent.noneTitle': 'No tienes eventos próximos agendados',
  'entityHome.nextEvent.noneHint':
    'Crea un proyecto para agendar tu primera activación.',

  // ── Project Environment (PRD §7.2) ─────────────────────────────
  'project.atledi.label': 'Atledi',
  'project.atledi.emptyProject':
    '{name} aún no tiene eventos. ¿Te armo el primero a partir del brief de marca?',
  'project.atledi.liveActivity':
    '{name} está en vivo en {live} evento(s). Vigila la tasa de captura contra el flujo esperado.',
  'project.atledi.scheduled':
    '{name} tiene {total} evento(s) agendado(s). Cierra el brief 6 días antes para pegar la ventana de temporada.',
  'project.panels.nextEvent.label': 'Próximo evento',
  'project.panels.nextEvent.tbd': 'Fecha por definir',
  'project.panels.nextEvent.empty': 'No hay eventos próximos agendados.',
  'project.panels.nextEvent.open': 'Abrir evento',
  'project.panels.myXotskies.label': 'My Merch',
  'project.panels.myXotskies.itemsTotal': 'artículos en total',
  'project.panels.myXotskies.lowStock': 'bajos',
  'project.panels.myXotskies.open': 'Abrir stock',
  'project.panels.reporters.label': 'Reporters',
  'project.panels.reporters.comingSoon':
    'La vista de Reporter llega en la Fase 2 — onboarding por magic-link para personal de campo.',
  'project.capacity.label': 'Capacidad',
  'project.capacity.green': 'En curso',
  'project.capacity.yellow': 'Ajustado',
  'project.capacity.red': 'Insuficiente',
  'project.capacity.pendingShort': 'Pendiente',
  'project.capacity.pending':
    'Aún no tenemos tasa histórica de captura para este proyecto. Corre una revisión manual o espera a que Atledi aprenda tras el primer evento.',
  'project.capacity.summary.green':
    'Inventario {inventory} ≥ esperado {expected} × buffer 15%. Estás bien provisto.',
  'project.capacity.summary.yellow':
    'Inventario {inventory} entre el esperado {expected} y el buffer del 15%. Considera reforzar.',
  'project.capacity.summary.red':
    'Inventario {inventory} está por debajo del esperado {expected}. Refuerza o suma una BA.',

  // ── Caso 15 v2 — Banner de plan en la cabecera del shell ───────
  'plan.banner.viewPlans': 'Ver planes',
  'plan.banner.upgradeNow': 'Actualizar plan',
  'plan.banner.expiringSoon': 'Tu plan está por vencer.',
  'plan.banner.limitNear':
    'Te estás acercando a uno de los límites de tu plan.',
  'plan.banner.limitCritical':
    'Estás por quedarte sin cupo — actualiza ahora para que tu evento siga corriendo.',
  'plan.banner.expired':
    'Tu plan ha vencido — tu cuenta volvió a Free.',
  'plan.banner.day': 'día',
  'plan.banner.days': 'días',

  // ── Caso 13 — Integraciones CRM salientes ────────────────────────
  'integrations.title': 'Integraciones',
  'integrations.subtitle':
    'Envía cada contacto nuevo a tu CRM o cualquier webhook en tiempo real.',
  'integrations.newWebhook': 'Nuevo webhook',
  'integrations.empty':
    'Aún no hay integraciones. Agrega un webhook para sincronizar contactos.',
  'integrations.row.enabled': 'Activo',
  'integrations.row.disabled': 'Inactivo',

  'integrations.stats.title': 'Estado de sincronización',
  'integrations.stats.subtitle':
    'Conteos de la cola saliente para este espacio de trabajo.',
  'integrations.state.PENDING': 'Pendientes',
  'integrations.state.SENT': 'Enviados',
  'integrations.state.FAILED': 'Fallidos',
  'integrations.state.SKIPPED': 'Omitidos',
  'integrations.dispatch.run': 'Despachar ahora',
  'integrations.dispatch.running': 'Despachando…',
  'integrations.dispatch.lastRun': 'Última corrida',

  'integrations.oauth.title': 'HubSpot · Mailchimp',
  'integrations.oauth.subtitle':
    'Los conectores OAuth se habilitarán cuando las apps de Onxite estén registradas con cada proveedor.',
  'integrations.oauth.comingSoon': 'Próximamente',

  'integrations.list.title': 'Integraciones configuradas',
  'integrations.list.subtitle':
    'Cada fila dispara un POST firmado al capturar un nuevo contacto.',

  'integrations.editor.newTitle': 'Nuevo webhook',
  'integrations.editor.editTitle': 'Editar webhook',
  'integrations.editor.subtitle':
    'Onxite firma cada payload con HMAC-SHA256. Verifica el header X-Onxite-Signature en tu endpoint.',
  'integrations.editor.name': 'Nombre',
  'integrations.editor.namePlaceholder': 'ej. Webhook del CRM de Ventas',
  'integrations.editor.url': 'URL del webhook',
  'integrations.editor.secret': 'Secreto compartido',
  'integrations.editor.secretPlaceholder':
    'Opcional — se usa para firmar los payloads',
  'integrations.editor.secretHelp':
    'Cuando está presente, cada request agrega X-Onxite-Signature: sha256=<hex>.',
  'integrations.editor.consentOnly':
    'Sólo enviar contactos con consentimiento de marketing',
  'integrations.editor.enabled': 'Activo',
  // Caso 13 v2 — editor multi-proveedor.
  'integrations.new.webhook': 'Nuevo webhook',
  'integrations.new.hubspot': 'Nuevo HubSpot',
  'integrations.new.mailchimp': 'Nuevo Mailchimp',
  'integrations.editor.webhookHelp':
    'Onxite firma cada payload con HMAC-SHA256. Verifica el header X-Onxite-Signature en tu endpoint.',
  'integrations.editor.hubspotHelp':
    'Crea una Private App en HubSpot con scope crm.objects.contacts.write y pega su access token abajo.',
  'integrations.editor.mailchimpHelp':
    'Pega una API key de Mailchimp y el Audience ID destino. Los contactos se sincronizan como miembros de la audiencia.',
  'integrations.editor.secretSaved': '•••••••• (guardado — deja vacío para conservar)',
  'integrations.editor.hubspotAccessToken': 'Access token de Private App',
  'integrations.editor.hubspotAccessTokenHelp':
    'HubSpot → Settings → Integrations → Private Apps. Se guarda cifrado.',
  'integrations.editor.mailchimpApiKey': 'API key',
  'integrations.editor.mailchimpApiKeyHelp':
    'Mailchimp → Account → Extras → API keys. El sufijo de datacenter (-us21) es obligatorio. Se guarda cifrado.',
  'integrations.editor.mailchimpAudienceId': 'Audience ID',
  'integrations.editor.mailchimpAudienceIdHelp':
    'Mailchimp → Audience → Settings → Audience name and defaults.',

  // ── Caso 08 — Constructor de encuestas embebible (página host) ────
  'surveyBuilder.title': 'Design Journey',
  'surveyBuilder.subtitle':
    'Diseña journeys dentro de la app. Potenciado por @onxite/ox-journey-builder.',
  'surveyBuilder.seed.step1Title': 'Bienvenida',
  'surveyBuilder.seed.q1Label': '¿Cuál es tu nombre completo?',
  'surveyBuilder.subtitle.persisted':
    'Editando una journey guardada. Los cambios se escriben transaccionalmente al servidor.',
  'surveyBuilder.toast.saved': 'Journey guardada.',
  'surveyBuilder.toast.saveFailed': 'No se pudo guardar la journey.',
  'surveyBuilder.toast.draftOnly':
    'Modo borrador — abre una journey existente en /surveys/builder/:id para guardar.',
  'surveyBuilder.toast.created': 'Journey creada.',
  'surveyBuilder.toast.createFailed': 'No se pudo crear la journey.',
  'surveyBuilder.create.title': 'Crea una nueva journey',
  'surveyBuilder.create.subtitle':
    'Dale un nombre para empezar — todo lo demás lo refinas después.',
  'surveyBuilder.create.nameLabel': 'Nombre de la journey',
  'surveyBuilder.create.namePlaceholder': 'ej. Journey del Brooklyn Block Party',
  'surveyBuilder.create.submit': 'Crear y abrir el editor',
  // Caso 08 — workspace: lista / importar / exportar / clonar / template.
  'surveyBuilder.action.export': 'Exportar JSON',
  'surveyBuilder.action.import': 'Importar JSON',
  'surveyBuilder.action.clone': 'Clonar',
  'surveyBuilder.action.markTemplate': 'Guardar como template',
  'surveyBuilder.action.unmarkTemplate': 'Quitar de templates',
  'surveyBuilder.templateBadge': 'Template',
  'surveyBuilder.list.title': 'Tus journeys',
  'surveyBuilder.list.subtitle':
    'Abre una para editarla, o clónala para ramificar sin tocar la original.',
  'surveyBuilder.list.empty':
    'Aún no hay journeys — crea o importa una arriba.',
  'surveyBuilder.toast.cloned': 'Journey clonada.',
  'surveyBuilder.toast.cloneFailed': 'No se pudo clonar la journey.',
  'surveyBuilder.action.disable': 'Deshabilitar',
  'surveyBuilder.action.enable': 'Habilitar',
  'surveyBuilder.toast.disabled': 'Journey deshabilitada.',
  'surveyBuilder.toast.enabled': 'Journey habilitada.',
  'surveyBuilder.toast.statusFailed':
    'No se pudo actualizar el estado de la journey.',
  'surveyBuilder.toast.deleted': 'Journey eliminada.',
  'surveyBuilder.toast.deleteFailed': 'No se pudo eliminar la journey.',
  'surveyBuilder.confirm.deleteTitle': '¿Eliminar esta journey?',
  'surveyBuilder.confirm.delete':
    '¿Eliminar "{name}"? Es un soft-delete; un admin puede restaurarla desde la DB.',
  'surveyBuilder.toast.exported': 'Journey exportada como JSON.',
  'surveyBuilder.toast.imported': 'Journey importada.',
  'surveyBuilder.toast.importInvalid':
    'Ese archivo no es un JSON de journey válido.',
  'surveyBuilder.toast.markedTemplate':
    'Guardada como template — tu workspace ya puede reutilizarla en eventos.',
  'surveyBuilder.toast.unmarkedTemplate': 'Ya no es un template.',
  'surveyBuilder.toast.templateFailed':
    'No se pudo actualizar el estado de template.',
  'sidebar.copyright.product': 'Un producto de',

  // ── Navegación inferior móvil ──────────────────────────────────────
  'mobileNav.label': 'Navegación principal',
  'mobileNav.more': 'Más',
  'mobileNav.close': 'Cerrar menú',

  // ── Centro de Ayuda (solo chrome — el contenido vive en helpContent.ts) ─
  'help.title': 'Centro de Ayuda',
  'help.subtitle': 'Explora qué hace cada pantalla, qué datos lleva y para qué sirve. Usa el buscador o salta a una categoría.',
  'help.searchPlaceholder': 'Buscar temas, palabras clave…',
  'help.whatItDoes': 'Qué hace',
  'help.whatData': 'Qué datos lleva',
  'help.whatFor': 'Para qué sirve',
  'help.related': 'Relacionado',
  'help.goToPage': 'Ir a la página',
  'help.noResults': 'Ningún tema coincide con tu búsqueda.',
  'help.resultsCount': '{count} resultados',
  'help.hintAria': 'Abrir ayuda de esta página',

  // ── Notificaciones ─────────────────────────────────────────────────
  'notifications.banner.markRead': 'Marcar como leída',
  'notifications.banner.moreCount': 'más',
  'notifications.banner.systemSender': 'Sistema',
  'notifications.bell.title': 'Notificaciones',
  'notifications.bell.empty': 'Aún no tienes notificaciones.',
  'notifications.bell.allCaughtUp': 'Al día',
  'notifications.bell.unreadCount': '{{count}} sin leer',
  'notifications.compose.pageTitle': 'Enviar una notificación',
  'notifications.compose.pageSubtitle': 'Escribe un mensaje y elige a quién enviárselo. También recibirán un push.',
  'notifications.compose.messageTitle': 'Mensaje',
  'notifications.compose.messageDescription': 'El título se muestra en el push y como encabezado del banner.',
  'notifications.compose.titleLabel': 'Título',
  'notifications.compose.titlePlaceholder': 'Un título corto…',
  'notifications.compose.bodyLabel': 'Cuerpo',
  'notifications.compose.bodyPlaceholder': 'Añade detalle. Acepta emoji y saltos de línea 🎉',
  'notifications.compose.emailCopy': 'Enviar también por email',
  'notifications.compose.send': 'Enviar ({{count}})',
  'notifications.compose.recipientsTitle': 'Destinatarios',
  'notifications.compose.recipientsDescription': 'Personas dentro de tu árbol de compañía.',
  'notifications.compose.searchPlaceholder': 'Buscar por nombre o email…',
  'notifications.compose.available': 'disponibles',
  'notifications.compose.noCandidates': 'No hay personas disponibles para notificar.',
  'notifications.compose.titleRequired': 'El título es obligatorio',
  'notifications.compose.bodyRequired': 'El cuerpo es obligatorio',
  'notifications.compose.recipientsRequired': 'Selecciona al menos un destinatario',
  'notifications.toast.sent': 'Notificación enviada a {{count}} persona(s)',
  'notifications.toast.sendFailed': 'No se pudo enviar la notificación',
  'notifications.push.disabledTitle': 'Notificaciones push desactivadas',
  'notifications.push.insecureContextBody':
    'Este URL de desarrollo no es HTTPS ni localhost, así que el navegador bloquea el service worker que recibe los push. Abre la app por HTTPS o vía http://localhost para activarlo.',
  'notifications.push.permissionDeniedBody':
    'Bloqueaste las notificaciones para este sitio. Reactívalas desde la configuración del navegador (candado → Notificaciones) y recarga.',

  // ── Importación masiva de stock ────────────────────────────────────
  'stock.list.importButton': 'Importar',
  'stock.import.title': 'Importar stock masivo',
  'stock.import.subtitle': 'Sube un ZIP con stock.xlsx y una carpeta images/.',
  'stock.import.dropzone': 'Suelta un archivo ZIP aquí o haz click para seleccionar',
  'stock.import.dropzoneHint': 'Acepta .zip (máx 50 MB)',
  'stock.import.downloadTemplate': 'Descargar plantilla',
  'stock.import.parsing': 'Leyendo ZIP…',
  'stock.import.parseFailed': 'No se pudo leer este ZIP',
  'stock.import.preview': 'Vista previa',
  'stock.import.previewCount': '{count} filas · {errors} errores',
  'stock.import.col.image': 'Imagen',
  'stock.import.col.name': 'Nombre',
  'stock.import.col.classification': 'Clase',
  'stock.import.col.rating': 'Rating',
  'stock.import.col.qty': 'Cantidad',
  'stock.import.col.status': 'Estado',
  'stock.import.status.new': 'Nuevo',
  'stock.import.status.duplicate': 'Ya existe',
  'stock.import.status.error': 'Error',
  'stock.import.action.replace': 'Reemplazar existente',
  'stock.import.action.createNew': 'Crear como nuevo',
  'stock.import.execute': 'Importar {count} items',
  'stock.import.cancel': 'Cancelar',
  'stock.import.progress.upload': 'Subiendo imágenes',
  'stock.import.progress.create': 'Creando stock',
  'stock.import.progress.replace': 'Actualizando existentes',
  'stock.import.summarySuccess': '{created} creados · {updated} actualizados',
  'stock.import.summaryMixed': '{created} creados · {updated} actualizados · {failed} fallidos',
  'stock.import.summaryAllFailed': 'La importación falló para las {count} filas',
  'stock.import.done': 'Listo',
  'stock.import.error.noXlsx': 'No se encontró archivo .xlsx en el ZIP',
  'stock.import.error.multipleXlsx': 'Más de un archivo .xlsx — deja solo stock.xlsx',
  'stock.import.error.missingNameCol': 'Falta la columna requerida: Name',
  'stock.import.error.emptySheet': 'El xlsx no tiene filas de datos',
  'stock.import.error.nameRequired': 'El nombre es obligatorio',
  'stock.import.error.nameTooShort': 'El nombre debe tener al menos 2 caracteres',
  'stock.import.error.ratingRange': 'El rating debe estar entre 1 y 5',
  'stock.import.error.classificationInvalid': 'La clasificación debe ser SEE, ENGAGE, TRY o ACT',
  'stock.import.error.imageNotFound': 'Imagen "{file}" no encontrada en images/',
  'stock.import.error.logoNotFound': 'Logo "{file}" no encontrado en images/',
  'stock.import.error.qtyNotInt': 'La cantidad inicial debe ser un entero',
  'stock.import.error.zipTooLarge': 'El ZIP supera los 50 MB',
  'stock.import.error.notAZip': 'El archivo debe ser un .zip',

  // ── Favoritos ──────────────────────────────────────────────────────
  'favorites.added': 'Añadido a favoritos',
  'favorites.removed': 'Quitado de favoritos',
  'favorites.addTitle': 'Añadir a favoritos',
  'favorites.removeTitle': 'Quitar de favoritos',

  // ── Auth · Login ───────────────────────────────────────────────────
  'auth.login.welcomeBack': 'Bienvenido de nuevo',
  'auth.login.subtitle': '¿Eres nuevo? Crearemos tu cuenta automáticamente al ingresar.',
  'auth.login.modeOtp': 'Código por correo',
  'auth.login.modePassword': 'Contraseña',
  'auth.login.emailLabel': 'Correo electrónico',
  'auth.login.email': 'Correo',
  'auth.login.password': 'Contraseña',
  'auth.login.continueWithEmail': 'Continuar con correo',
  'auth.login.signIn': 'Iniciar sesión',
  'auth.login.forgotPassword': '¿Olvidaste tu contraseña?',
  'auth.forgot.title': 'Recupera tu contraseña',
  'auth.forgot.subtitle': 'Ingresa tu correo y te enviaremos un código de 6 dígitos a tu correo de recuperación.',
  'auth.forgot.submit': 'Enviar código',
  'auth.forgot.sentTitle': 'Código enviado',
  'auth.forgot.sentBody': 'Revisa tu bandeja para el código de 6 dígitos.',
  'auth.forgot.failed': 'No se pudo enviar el código',
  'auth.forgot.backToLogin': 'Volver a iniciar sesión',
  'auth.reset.title': 'Define una nueva contraseña',
  'auth.reset.subtitleFor': 'Ingresa el código de 6 dígitos enviado a {email} y tu nueva contraseña.',
  'auth.reset.subtitleNoEmail': 'Falta el correo. Solicita un nuevo código.',
  'auth.reset.requestNew': 'Solicitar un nuevo código',
  'auth.reset.otpLabel': 'Código de recuperación',
  'auth.reset.newPasswordLabel': 'Nueva contraseña',
  'auth.reset.confirmPasswordLabel': 'Confirmar nueva contraseña',
  'auth.reset.submit': 'Actualizar contraseña',
  'auth.reset.successTitle': 'Contraseña actualizada',
  'auth.reset.successBody': 'Ya puedes iniciar sesión con tu nueva contraseña.',
  'auth.reset.failed': 'No se pudo restablecer la contraseña',
  'auth.reset.errOtpLength': 'El código debe tener 6 dígitos.',
  'auth.reset.errPasswordShort': 'La contraseña debe tener al menos 8 caracteres.',
  'auth.reset.errMismatch': 'Las contraseñas no coinciden.',
  'auth.login.demoTipLabel': 'Tip demo:',
  'auth.login.demoTipBody': 'cualquier correo sirve. El OTP siempre es',
  'auth.login.curious': '¿Solo curioso?',
  'auth.login.seePlans': 'Ver planes',
  'auth.login.newHere': '¿Nuevo por aquí?',
  'auth.login.createAccount': 'Crea tu cuenta',
  'auth.login.heroTitle': 'Planeación de eventos lista para medir',
  'auth.login.heroBody': 'Planifica, monitorea y mide cada activación de sampling en tiempo real. Encuestas con tu marca, métricas al instante y el score MENT detrás de cada evento.',
  'auth.login.orContinueWith': 'o continúa con',
  'auth.toast.devHint': 'Pista dev',
  'auth.toast.otpFailed': 'No se pudo enviar el OTP',
  'auth.toast.invalidCredentials': 'Credenciales inválidas',
  'auth.toast.alreadySignedIn': 'Cuenta en uso',
  'auth.toast.alreadySignedInBody':
    'Ya tienes sesión iniciada en otro dispositivo o navegador. Notificamos a esa sesión de este intento.',
  'auth.footer.copyrightPrefix': '© {year} Xamplin by ',
  'auth.footer.copyrightSuffix': '. Todos los derechos reservados.',

  // ── Auth · OTP ─────────────────────────────────────────────────────
  'otp.checkInbox': 'Revisa tu bandeja',
  'otp.subtitlePrefix': 'Enviamos un código de 6 dígitos a',
  'otp.verifyCode': 'Verificar código',
  'otp.didntReceive': '¿No lo recibiste?',
  'otp.resendIn': 'Reenviar en {seconds}s',
  'otp.resendNow': 'Reenviar ahora',
  'otp.toast.welcome': '¡Bienvenido!',
  'otp.toast.signedIn': 'Sesión iniciada',
  'otp.toast.invalidExpired': 'Código inválido o expirado',
  'otp.toast.newCodeSent': 'Nuevo código enviado',
  'otp.toast.newCodeBody': 'Sigue siendo 123456.',

  // ── Auth · Register (wizard 3 pasos) ───────────────────────────────
  'register.step.company': 'Marca',
  'register.step.account': 'Cuenta',
  'register.step.verify': 'Verificar',
  'register.orWithSocial': 'Regístrate con',
  'register.orWithSocialHint': 'Al registrarte con una red social no pediremos código por correo.',
  'register.orWithEmail': 'O con correo',
  'register.company.title': 'Crea tu workspace',
  'register.company.subtitle': 'Cuéntanos el nombre de tu empresa — te activamos un plan Free para empezar.',
  'register.company.companyName': 'Nombre de la empresa',
  'register.company.companyNamePlaceholder': 'Acme S.A.',
  'register.company.continue': 'Continuar',
  'register.account.title': 'Tu cuenta',
  'register.account.subtitlePrefix': 'Serás el propietario de',
  'register.account.firstname': 'Nombre',
  'register.account.lastname': 'Apellido',
  'register.account.email': 'Correo de trabajo',
  'register.account.password': 'Contraseña',
  'register.account.passwordHint': 'Mínimo 8 caracteres.',
  'register.account.confirmPassword': 'Confirma la contraseña',
  'register.account.continue': 'Enviar código de verificación',
  'register.verify.title': 'Verifica tu correo',
  'register.verify.subtitlePrefix': 'Enviamos un código de 6 dígitos a',
  'register.verify.verify': 'Crear mi cuenta',
  'register.verify.didntReceive': '¿No lo recibiste?',
  'register.verify.resendIn': 'Reenviar en {seconds}s',
  'register.verify.resendNow': 'Reenviar ahora',
  'register.haveAccount': '¿Ya tienes cuenta?',
  'register.signIn': 'Inicia sesión',
  'register.hero.title': 'Listo en minutos',
  'register.hero.body': 'Plan Free: 1 marca, 2 journeys, 2 estaciones, 5 stocks. Upgrade cuando quieras.',
  'register.toast.devHint': 'Pista dev',
  'register.toast.welcome': '¡Bienvenido, {name}!',
  'register.toast.welcomeBody': 'Tu workspace está listo.',
  'register.toast.initiateFailed': 'No pudimos iniciar el registro',
  'register.toast.emailTaken': 'Ese correo ya está registrado',
  'register.toast.emailTakenBody': 'Mejor inicia sesión.',
  'register.toast.invalidOtp': 'Código inválido o expirado',

  // ── Auth · Pending access ──────────────────────────────────────────
  'pendingAccess.title': 'Esperando acceso',
  'pendingAccess.body': 'Tu cuenta ya está creada, pero un admin debe asignarte un rol. Mantén esta página abierta — vuelve a entrar cuando lo haga.',
  'pendingAccess.signedInAs': 'Iniciaste sesión como',

  // ── Onboarding shell ───────────────────────────────────────────────
  'onboarding.step.account': 'Cuenta',
  'onboarding.step.brandProfile': 'Perfil de marca',
  'onboarding.step.brandKit': 'Kit de marca',

  // ── Edit profile dialog ────────────────────────────────────────────
  'editProfile.eyebrow': 'Editar perfil',
  'editProfile.title': 'Actualiza tus datos',
  'editProfile.subtitle': 'Visible en todo tu espacio. El correo y el rol no se pueden cambiar aquí.',
  'editProfile.section.visuals': 'Visuales',
  'editProfile.section.identity': 'Identidad',
  'editProfile.section.professional': 'Profesional',
  'editProfile.section.tags': 'Etiquetas',
  'editProfile.field.avatar': 'Avatar',
  'editProfile.field.cover': 'Imagen de portada',
  'editProfile.field.firstName': 'Nombre',
  'editProfile.field.lastName': 'Apellido',
  'editProfile.field.username': 'Usuario (@handle)',
  'editProfile.field.phone': 'Teléfono',
  'editProfile.field.bio': 'Biografía',
  'editProfile.field.bioPlaceholder': 'Una frase corta sobre lo que construyes.',
  'editProfile.field.title': 'Cargo',
  'editProfile.field.titlePlaceholder': 'Gestor de marca',
  'editProfile.field.location': 'Ubicación',
  'editProfile.field.locationPlaceholder': 'CDMX, México',
  'editProfile.field.studiedAt': 'Estudiaste en',
  'editProfile.tag.placeholder': 'Escribe y presiona Enter…',
  'editProfile.tag.remove': 'Quitar {tag}',
  'editProfile.save': 'Guardar cambios',

  // ── Change password dialog ─────────────────────────────────────────
  'changePassword.eyebrow': 'Seguridad',
  'changePassword.title': 'Cambiar contraseña',
  'changePassword.subtitle': 'Mínimo 8 caracteres. Tu sesión seguirá activa.',
  'changePassword.field.current': 'Contraseña actual',
  'changePassword.field.new': 'Nueva contraseña',
  'changePassword.field.confirm': 'Confirmar nueva contraseña',
  'changePassword.show': 'Mostrar contraseña',
  'changePassword.hide': 'Ocultar contraseña',
  'changePassword.hint.lengthOk': 'Largo correcto.',
  'changePassword.hint.lengthShort': '{count}/8 caracteres — faltan {missing}.',
  'changePassword.hint.match': 'Las contraseñas coinciden.',
  'changePassword.hint.noMatch': 'Las contraseñas aún no coinciden.',
  'changePassword.error.length': 'La nueva contraseña debe tener al menos 8 caracteres.',
  'changePassword.error.mismatch': 'La nueva contraseña y la confirmación no coinciden.',
  'changePassword.submit': 'Actualizar contraseña',
  'changePassword.toast.updated': 'Contraseña actualizada',

  // ── Pricing page ───────────────────────────────────────────────────
  'pricing.back': 'Volver',
  'pricing.heroTitle': 'Planes para cada marca',
  'pricing.heroBody': 'Planear es gratis para siempre — operación gratis, metodología paga. Elige el plan que se ajusta a tu hoja de ruta.',
  'pricing.checkoutFailed': 'Falló el checkout',
  'pricing.talkToUs': 'Hablemos',
  'pricing.talkToUsBody': 'Nos pondremos en contacto para diseñar un plan a tu medida.',
  'pricing.mostPopular': 'Más popular',
  'pricing.fromPrice': 'Desde {v}',
  'pricing.startFree': 'Empezar gratis',
  'pricing.choose': 'Elegir {tier}',
  'pricing.cap.brands': 'Marcas',
  'pricing.cap.events': 'Eventos / 12m',
  'pricing.cap.seats': 'Asientos pagos',
  'pricing.cap.agencies': 'Agencias',
  'pricing.feat.operations': 'Operación gratis: planeación, clock-in GPS, foto-verificación',
  'pricing.feat.methodology': 'Metodología paga: CS-1 + score MENT',
  'pricing.feat.atledi': 'Asistente IA ATLEDI + recap',
  'pricing.feat.notifications': 'Notificaciones',
  'pricing.feat.activeLog': 'Registro de actividad',
  'pricing.feat.team': 'Equipo y roles',
  'pricing.planUnavailable': 'Plan no disponible',
  'pricing.missingTier': 'Falta el plan "{slug}"',

  // ── Price card ─────────────────────────────────────────────────────
  'priceCard.tier.pilot': 'Pilot',
  'priceCard.tier.scale': 'Scale ★',
  'priceCard.tier.program': 'Program',
  'priceCard.tier.enterprise': 'Enterprise',
  'priceCard.period.year': '/año',
  'priceCard.period.month': '/mes',
  'priceCard.mostPopular': '★ Más popular',
  'priceCard.planAria': 'Plan {tier}',
  'priceCard.selected': 'Seleccionado',
  'priceCard.talkToUs': 'Hablemos',
  'priceCard.select': 'Elegir {tier}',
  'priceCard.line.inBrands': 'En marcas',
  'priceCard.line.inCapacity': 'En capacidad',
  'priceCard.line.inEvents': 'En eventos',
  'priceCard.line.perUnit': 'Por unidad',
  'priceCard.brandsOne': '{count} marca',
  'priceCard.brandsMany': '{count} marcas',
  'priceCard.xotskiesValue': '{count} xotskies',
  'priceCard.editorSeatOne': '{count} asiento de editor (transferible)',
  'priceCard.editorSeatMany': '{count} asientos de editor (transferibles)',
  'priceCard.editorSeatsUnlimited': '{count} asientos de editor (transferibles)',
  'priceCard.unlimitedReporters': 'Reporteros ilimitados (gratis)',
  'priceCard.tier1Games': 'Juegos branded Tier 1',
  'priceCard.tier2Games': 'Biblioteca de juegos Tier 2 incluida',
  'priceCard.realtime': 'Dashboard en tiempo real + Inventario inteligente',
  'priceCard.enterpriseExtras': 'SSO + Bitácora + API + CSM dedicado',
  'priceCard.feature.notifications': 'Notificaciones push',
  'priceCard.feature.activityLog': 'Bitácora de actividad',
  'priceCard.feature.stockImport': 'Importación de inventario CSV',
  'priceCard.feature.aiAssistant': 'Asistente IA',
  'priceCard.feature.userManagement': 'Gestión de usuarios',

  // ── Upgrade dialog ─────────────────────────────────────────────────
  'upgradeDialog.title': 'Alcanzaste tu límite de {resource}',
  'upgradeDialog.body': 'Tu plan {plan} incluye {limit} {resource}. Mejora tu plan para seguir creando.',
  'upgradeDialog.maybeLater': 'Quizás después',
  'upgradeDialog.seePlans': 'Ver planes',
  'upgradeDialog.resource.projects': 'proyectos',
  'upgradeDialog.resource.groups': 'grupos de eventos',
  'upgradeDialog.resource.events': 'eventos',
  'upgradeDialog.resource.stations': 'estaciones',
  'upgradeDialog.resource.stocks': 'xotskies',
  'upgradeDialog.resource.brands': 'marcas',
  'upgradeDialog.resource.users': 'usuarios',
  'upgradeDialog.resource.roles': 'roles',
  'upgradeDialog.resource.surveys': 'journeys',

  // ── Billing page ───────────────────────────────────────────────────
  'billingPage.title': 'Facturación',
  'billingPage.subtitle': 'Monitorea uso, administra tu plan y descarga facturas.',
  'billingPage.currentBadge': 'Plan actual',
  'billingPage.currentTitle': 'Plan actual',
  'billingPage.currentDesc': 'El endpoint de detalles del plan aún no está expuesto en el backend.',
  'billingPage.changePlan': 'Cambiar plan',
  'billingPage.usageTitle': 'Uso',
  'billingPage.usageDesc': 'Cuánto has consumido de tu plan.',
  'billingPage.invoicesTitle': 'Facturas',
  'billingPage.invoicesDesc': 'Historial de compras de tu plan.',
  'billingPage.noInvoices': 'Aún no hay facturas.',
  'billingPage.col.date': 'Fecha',
  'billingPage.col.plan': 'Plan',
  'billingPage.col.amount': 'Monto',
  'billingPage.col.status': 'Estado',
  'billingPage.invoice.paid': 'Pagada',
  'billingPage.invoice.pending': 'Pendiente',
  'billingPage.invoice.status.active': 'Activa',
  'billingPage.invoice.status.expired': 'Expirada',
  'billingPage.invoice.status.refunded': 'Reembolsada',
  'billingPage.invoice.status.exhausted': 'Agotada',
  'billingPage.resource.brands': 'Marcas',
  'billingPage.resource.users': 'Usuarios',
  'billingPage.resource.roles': 'Roles',
  'billingPage.resource.surveys': 'Journeys',
  'billingPage.resource.responsesPerSurvey': 'Interactions / journey',
  'billingPage.resource.projects': 'Proyectos de marca',
  'billingPage.resource.eventGroups': 'Grupos de eventos',
  'billingPage.resource.events': 'Eventos',
  'billingPage.resource.stations': 'Estaciones',
  'billingPage.resource.stocks': 'Xotskies',
  'billingPage.featuresTitle': 'Características del plan',
  'billingPage.featuresDesc': 'Lo que está activo en tu workspace ahora mismo.',
  'billingPage.feature.notifications': 'Notificaciones push',
  'billingPage.feature.activeLog': 'Bitácora de actividad',
  'billingPage.feature.aiAssistant': 'Asistente IA',
  'billingPage.feature.stockImport': 'Importar stock por CSV',
  'billingPage.feature.userManagement': 'Gestión de usuarios',
  'billingPage.gamesTitle': 'Juegos habilitados',
  'billingPage.gamesDesc': 'Mecánicas de juego incluidas en tu plan.',
  'billingPage.gamesEmpty': 'Este plan no incluye mecánicas de juego.',

  // ── Checkout result ────────────────────────────────────────────────
  'checkoutResult.successTitle': '¡Listo!',
  'checkoutResult.successBody': 'Tu plan ya está activo y las cuotas se actualizaron.',
  'checkoutResult.seeBilling': 'Ver facturación',
  'checkoutResult.backToDashboard': 'Volver al panel',
  'checkoutResult.cancelTitle': 'Checkout cancelado',
  'checkoutResult.cancelBody': 'No se realizó ningún cargo. Puedes elegir otro plan cuando quieras.',
  'checkoutResult.seePlans': 'Ver planes',

  // ── Mock checkout page ─────────────────────────────────────────────
  'mockCheckout.paymentConfirmed': 'Pago confirmado',
  'mockCheckout.confirmationFailed': 'Falló la confirmación',
  'mockCheckout.invalidSession': 'Sesión de checkout inválida',
  'mockCheckout.backToPlans': 'Volver a planes',
  'mockCheckout.badge': 'Checkout demo',
  'mockCheckout.planTitle': 'Plan {plan}',
  'mockCheckout.planDesc': 'Se cobrará hoy y el mismo día cada mes. Cancela cuando quieras.',
  'mockCheckout.subtotal': 'Subtotal',
  'mockCheckout.tax': 'Impuestos',
  'mockCheckout.taxIncluded': 'Incluidos',
  'mockCheckout.totalDue': 'Total a pagar hoy',
  'mockCheckout.pay': 'Pagar {amount}',
  'mockCheckout.cancelGoBack': 'Cancelar y volver',
  'mockCheckout.mockEnv': 'Entorno demo — no se mueve dinero real.',
  'mockCheckout.whatsIn': '¿Qué incluye {plan}?',

  // ── Settings page (v2 list-style page) ─────────────────────────────
  'settings.comingSoon': 'Próximamente',
  'settings.section.accountDesc': 'Datos de la empresa, zona horaria, idioma',
  'settings.section.access': 'Gestión de accesos',
  'settings.section.brand': 'Marca',
  'settings.section.brandDesc': 'Ajustes por marca y opt-in de Inventario de marca',
  'settings.brand.title': 'Ajustes de marca',
  'settings.brand.subtitle': 'Toggles por marca. Afecta la experiencia de la marca en Xotski.',
  'settings.brand.inventoryTitle': 'Inventario de marca',
  'settings.brand.inventoryDescription':
    'Modelo de inventario en dos niveles (PRD §9): los artículos viven en un pool a nivel de marca y se asignan a almacenes de proyecto. Está apagado hasta que lo enciendas por marca.',
  'settings.brand.lockedBadge': 'Plan bloqueado',
  'settings.brand.noBrands': 'Aún no hay marcas visibles para tu espacio de trabajo.',
  'settings.brand.savedToast': 'Guardado',
  'settings.brand.errorToast': 'No pudimos guardar el cambio',
  'settings.section.accessDesc': 'Invita miembros, asigna roles, comparte accesos',
  'settings.section.billing': 'Facturación',
  'settings.section.billingDesc': 'Plan, uso y facturas',
  'settings.section.followup': 'Correos de seguimiento',
  'settings.section.followupDesc': 'Plantillas, cupones y nutrición post-evento',
  'settings.section.operations': 'Operaciones',
  'settings.section.operationsDesc': 'Almacenes y protocolos de assets',
  'settings.section.integrations': 'Integraciones CRM',
  'settings.section.integrationsDesc': 'Conecta HubSpot, Mailchimp o un webhook',
  'settings.section.crm': 'Integraciones CRM',
  'settings.section.crmDesc': 'Conecta HubSpot, Salesforce o un webhook',
  'settings.section.api': 'API keys',
  'settings.section.apiDesc': 'Genera llaves para acceso programático',
  'settings.section.notificationsDesc': 'Alertas por correo de inventario y eventos',
  'settings.section.activeLog': 'Active Log',
  'settings.section.activeLogDesc': 'Historial de auditoría de cada cambio en tu workspace',

  // ── Insights page (v2 distribution overview) ───────────────────────
  'insights.resetFilters': 'Limpiar filtros',
  'insights.filter.brand': 'Marca',
  'insights.filter.event': 'Evento',
  'insights.filter.classification': 'Clasificación',
  'insights.filter.from': 'Desde',
  'insights.filter.to': 'Hasta',
  'insights.filter.allBrands': 'Todas las marcas ({count})',
  'insights.filter.allEvents': 'Todos los eventos ({count})',
  'insights.filter.all': 'Todas',
  'insights.filter.unclassified': 'Sin clasificar',
  'insights.kpi.distributed': 'Distribuidos',
  'insights.kpi.distributedHint': '{rate}% de lo asignado',
  'insights.kpi.remaining': 'Restante en estaciones',
  'insights.kpi.remainingHint': '{total} asignados en total',
  'insights.kpi.value': 'Valor distribuido',
  'insights.kpi.valueHint': 'Basado en costo unitario',
  'insights.kpi.activeEvents': 'Eventos activos',
  'insights.kpi.activeEventsHintOne': '{brands} marca · {events} evento en alcance',
  'insights.kpi.activeEventsHintMany': '{brands} marcas · {events} eventos en alcance',
  'insights.restockAlertOne': '{count} estación al o bajo umbral de reposición.',
  'insights.restockAlertMany': '{count} estaciones al o bajo umbral de reposición.',
  'insights.restockRefill': 'Reabastece antes del próximo evento.',
  'insights.chart.byBrand': 'Distribución por marca',
  'insights.chart.topEvents': 'Eventos top por unidades distribuidas',
  'insights.chart.topStocks': 'Top 10 xotskies (unidades distribuidas)',
  'insights.chart.byClassification': 'Unidades distribuidas por clasificación',
  'insights.chart.byRarity': 'Unidades distribuidas por rareza',
  'insights.chart.usedAvailable': 'Stock usado vs disponible (top 10)',
  'insights.chart.empty': 'Sin datos para los filtros actuales',
  'insights.rarity.common': 'Común',
  'insights.rarity.uncommon': 'Poco común',
  'insights.rarity.rare': 'Raro',
  'insights.rarity.epic': 'Épico',
  'insights.rarity.legendary': 'Legendario',
  'insights.rarity.tier': 'Tier {n}',
  'insights.unclassifiedLabel': 'Sin clasificar',

  // ── Contacts page (v2 list) ────────────────────────────────────────
  'contacts.responsesCount': '{count} interactions',
  'contacts.col.when': 'Cuándo',
  'contacts.col.survey': 'Journey',
  'contacts.col.station': 'Estación',
  'contacts.col.source': 'Origen',
  'contacts.col.prize': 'Premio',
  'contacts.noun': 'interactions',
  // ── Caso 01 — Leads CRM (integración real /co-contacts) ────────────
  'contacts.export': 'Exportar CSV',
  'contacts.applySearch': 'Buscar',
  'contacts.filter.consentOnly': 'Solo con consentimiento',
  'contacts.totalCount': '{count} contactos',
  'contacts.col.name': 'Nombre',
  'contacts.col.contact': 'Contacto',
  'contacts.col.interactions': 'Visitas',
  'contacts.col.prizes': 'Premios',
  'contacts.col.lastSeen': 'Última visita',
  'contacts.col.consent': 'Consentimiento',
  'contacts.anonymousName': 'Participante anónimo',
  'contacts.anonymousSnippet': 'Sin identidad capturada',
  'contacts.consent.yes': 'Sí',
  'contacts.consent.no': 'No',
  'contacts.detail.title': 'Detalle del contacto',
  'contacts.detail.back': 'Volver a contactos',
  'contacts.detail.invalidId': 'Identificador inválido.',
  'contacts.detail.delete': 'Eliminar (GDPR)',
  'contacts.detail.deleteConfirm':
    '¿Eliminar permanentemente los datos de {name}? Esto no se puede deshacer.',
  'contacts.detail.engagement.title': 'Engagement',
  'contacts.detail.engagement.interactions': 'Visitas',
  'contacts.detail.engagement.prizes': 'Premios ganados',
  'contacts.detail.engagement.firstSeen': 'Primera visita',
  'contacts.detail.engagement.lastSeen': 'Última visita',
  'contacts.detail.demographics.title': 'Demografía',
  'contacts.detail.demographics.age': 'Edad',
  'contacts.detail.demographics.gender': 'Género',
  'contacts.detail.demographics.city': 'Ciudad',
  'contacts.detail.demographics.state': 'Estado',
  'contacts.detail.demographics.zip': 'Código postal',
  'contacts.detail.demographics.language': 'Idioma',
  'contacts.detail.controls.title': 'Controles',
  'contacts.detail.controls.revokeConsent': 'Revocar consentimiento',
  'contacts.detail.controls.grantConsent': 'Conceder consentimiento',
  'contacts.detail.controls.tags': 'Etiquetas',
  'contacts.detail.controls.add': 'Añadir',
  'contacts.detail.controls.addTagPlaceholder': 'Añade una etiqueta…',
  'contacts.detail.controls.removeTag': 'Quitar etiqueta',
  'contacts.detail.timeline.title': 'Interacciones ({count})',
  'contacts.detail.timeline.empty': 'Aún no hay interacciones registradas.',
  'contacts.detail.timeline.atEvent': 'Evento #{id}',
  'contacts.detail.timeline.unknownEvent': 'Evento desconocido',
  'contacts.detail.timeline.station': 'Estación #{id}',
  'contacts.detail.timeline.prize': 'Premio #{id}',

  // ── Caso 02 — Reporte de resultados por evento ─────────────────────
  'eventReport.invalidId': 'Identificador de evento inválido.',
  'eventReport.backToEvent': 'Volver al evento',
  'eventReport.print': 'Imprimir / Guardar PDF',
  'eventReport.createShare': 'Crear enlace público',
  'eventReport.shareCopied': 'Enlace copiado al portapapeles',
  'eventReport.publicFooter':
    'Resultados agregados — no se comparten datos individuales de participantes.',
  'eventReport.publicInvalid': 'Enlace inválido.',
  'eventReport.publicNotFound.title': 'Este reporte no está disponible',
  'eventReport.publicNotFound.body':
    'El enlace puede haber expirado o sido revocado. Pide uno nuevo al organizador.',
  'eventReport.reach.interactions': 'Interacciones',
  'eventReport.reach.last24h': 'Últimas 24 h',
  'eventReport.reach.distinctContacts': 'Contactos únicos',
  'eventReport.reach.leads': 'Leads capturados',
  'eventReport.prizes.title': 'Premios',
  'eventReport.prizes.allocated': 'Asignados',
  'eventReport.prizes.distributed': 'Entregados',
  'eventReport.prizes.remaining': 'Restantes',
  'eventReport.prizes.value': 'Valor entregado',
  'eventReport.costs.title': 'Costo por resultado',
  'eventReport.costs.perInteraction': 'Por interacción',
  'eventReport.costs.perLead': 'Por lead',
  'eventReport.topAnswers.title': 'Interactions top',
  'eventReport.demographics.title': 'Demografía',

  // ── Caso 03 — Canje de premios (pantalla operador + stats) ─────────
  'redemption.title': 'Canjear un premio',
  'redemption.subtitle': 'Escribe o escanea el código del consumidor y confirma la entrega.',
  'redemption.codeLabel': 'Código de canje',
  'redemption.lookUp': 'Buscar',
  'redemption.scan': 'Escanear',
  // Caso 11 v2 — Overlay del scanner por cámara.
  'operator.scanner.title': 'Escanear código QR',
  'operator.scanner.hint': 'Apunta la cámara al QR del consumidor.',
  'operator.scanner.unsupported':
    'Este navegador no admite escaneo por cámara. Ingresa el código manualmente.',
  'operator.scanner.cameraError':
    'No se pudo acceder a la cámara. Revisa el permiso e inténtalo de nuevo.',
  'redemption.preview.title': 'Premio listo para entregar',
  'redemption.preview.code': 'Código',
  'redemption.preview.stockId': 'Premio',
  'redemption.preview.station': 'Estación',
  'redemption.preview.won': 'Ganado',
  'redemption.preview.expires': 'Expira',
  'redemption.preview.confirm': 'Confirmar entrega',
  'redemption.preview.confirming': 'Confirmando…',
  'redemption.preview.state.delivered': 'Ya fue canjeado.',
  'redemption.preview.state.expired': 'Este canje expiró.',
  'redemption.preview.state.voided': 'Este canje fue anulado.',
  'redemption.success.title': '¡Entregado!',
  'redemption.success.body': 'Código {code} confirmado.',
  'redemption.error.generic': 'No se pudo canjear. Intenta de nuevo.',
  'redemption.error.tooShort': 'Los códigos tienen 6+ caracteres.',
  'redemption.stats.title': 'Canje de premios',
  'redemption.stats.won': 'Ganados',
  'redemption.stats.delivered': 'Entregados',
  'redemption.stats.expired': 'Expirados',
  'redemption.stats.rate': 'Tasa',

  // ── Caso 04 — Seguimiento post-evento ──────────────────────────────
  'followup.title': 'Correo de seguimiento',
  'followup.subtitle':
    'Configura el correo que se envía a participantes con consentimiento tras su evento.',
  'followup.enabled': 'Activa',
  'followup.disabled': 'Apagada',
  'followup.newTemplate': 'Nueva plantilla',
  'followup.confirmDelete': '¿Eliminar esta plantilla?',
  'followup.templates.title': 'Plantillas',
  'followup.templates.description':
    'Gana la coincidencia más específica: primero la del evento, luego la de marca.',
  'followup.templates.empty':
    'Aún no hay plantillas. Crea una para empezar a nutrir.',
  'followup.editor.newTitle': 'Nueva plantilla',
  'followup.editor.editTitle': 'Editar plantilla',
  'followup.editor.name': 'Nombre',
  'followup.editor.nameRequired': 'El nombre es requerido — Guardar se habilita cuando lo llenas.',
  'followup.editor.scope': 'Aplicar a',
  'followup.editor.scopeHint': 'Las plantillas específicas de evento ganan sobre la brand-wide para ese evento.',
  'followup.scope.brandWide': 'Todos los eventos (brand-wide)',
  'followup.scope.event': 'Evento',
  'followup.toast.created': 'Plantilla creada',
  'followup.toast.updated': 'Plantilla actualizada',
  'followup.toast.deleted': 'Plantilla eliminada',
  'followup.toast.saveError': 'No se pudo guardar la plantilla',
  'followup.toast.deleteError': 'No se pudo eliminar la plantilla',
  'followup.editor.subject': 'Asunto',
  'followup.editor.body': 'Cuerpo (HTML)',
  'followup.editor.variables':
    'Variables: {{firstName}} {{lastName}} {{prizeName}} {{couponCode}} {{eventName}} {{unsubscribeUrl}}',
  'followup.editor.delay': 'Demora de envío (horas)',
  'followup.defaults.subject': '¡Gracias por participar!',
  'followup.defaults.bodyHtml':
    '<p>Hola {{firstName}},</p><p>Gracias por visitarnos en {{eventName}}. Usa el código <strong>{{couponCode}}</strong> en tu próxima compra.</p><p><a href="{{unsubscribeUrl}}">Cancelar suscripción</a></p>',

  // ── Caso 14 — Checklist de primeros pasos ─────────────────────────
  'firstSteps.title': 'Pon en marcha tu primera activación',
  'firstSteps.step.brand.title': 'Añade tu marca',
  'firstSteps.step.brand.description': 'Logo + colores para que cada pantalla lleve tu identidad.',
  'firstSteps.step.brand.cta': 'Añadir marca',
  'firstSteps.step.event.title': 'Crea tu primer evento',
  'firstSteps.step.event.description': 'Fechas, lugar y a qué marca pertenece.',
  'firstSteps.step.event.cta': 'Crear evento',
  'firstSteps.step.station.title': 'Añade una estación',
  'firstSteps.step.station.description':
    'Un booth o touchpoint del evento. Genera el QR.',
  'firstSteps.step.station.cta': 'Añadir estación',
  'common.dismiss': 'Descartar',

  // ── Caso 14 v1 — Asistente modal guiado ────────────────────────
  'firstEventWizard.title': 'Lancemos tu primer Xotski',
  'firstEventWizard.subtitle':
    'Tres pasos y estás en vivo: marca, evento, estación.',
  'firstEventWizard.progress': 'Avance del setup',
  'firstEventWizard.dontShowAgain': 'No mostrar esto otra vez',
  'firstEventWizard.step.brand.title': 'Crea tu marca',
  'firstEventWizard.step.brand.description':
    'Logo, colores y tono — la identidad que heredan tus journeys.',
  'firstEventWizard.step.brand.cta': 'Abrir brand studio',
  'firstEventWizard.step.event.title': 'Crea tu primer evento',
  'firstEventWizard.step.event.description':
    'Una activación en vivo: cuándo y dónde corre.',
  'firstEventWizard.step.event.cta': 'Crear evento',
  'firstEventWizard.step.station.title':
    'Añade una estación e imprime el QR',
  'firstEventWizard.step.station.description':
    'Un booth, kiosko o touchpoint donde los invitados escanean para jugar.',
  'firstEventWizard.step.station.cta': 'Añadir estación',
  // Caso 14 v2 — Creación inline de marca.
  'firstEventWizard.brand.nameLabel': 'Nombre de la marca',
  'firstEventWizard.brand.namePlaceholder': 'ej. Atledi',
  'firstEventWizard.brand.createSubmit': 'Crear marca',
  'firstEventWizard.brand.openStudio': 'Abrir Brand Studio completo →',
  'firstEventWizard.brand.created': 'Marca creada.',
  'firstEventWizard.brand.createFailed': 'No se pudo crear la marca.',

  // ── Project detail page ────────────────────────────────────────────
  'project.status.planning': 'Planificación',
  'project.status.live': 'En vivo',
  'project.status.wrapped': 'Cerrado',
  'project.backToBrand': 'Volver a la marca',
  'project.editProject': 'Editar proyecto',
  'project.eventsCardTitle': 'Eventos',
  'project.newEvent': 'Nuevo evento',
  'project.viewTrends': 'Ver tendencias',

  // ── Caso 12 parte 2 — Tendencias entre eventos ──────────────────
  'trends.title': 'Tendencias de eventos',
  'trends.subtitle':
    'Compara cómo desempeñó cada activación lado a lado.',
  'trends.empty': 'Aún no hay suficientes eventos para comparar.',
  'trends.chart.title': 'Métricas por evento',
  'trends.chart.subtitle':
    'Las barras se agrupan por evento para detectar patrones semana a semana.',
  'trends.table.title': 'Tabla detallada',
  'trends.table.event': 'Evento',
  'trends.table.date': 'Fecha',
  'trends.table.status': 'Estado',
  'trends.metric.responses': 'Interactions',
  'trends.metric.leads': 'Leads',
  'trends.metric.prizes': 'Premios entregados',
  'trends.metric.prizesDistributed': 'Premios distribuidos',
  'project.field.eventName': 'Nombre del evento',
  'project.field.eventNamePlaceholder': 'Brooklyn Block Party',
  'project.field.address': 'Dirección',
  'project.field.addressPlaceholder': '123 Wythe Ave, Brooklyn NY',
  'project.field.starts': 'Inicia',
  'project.field.ends': 'Termina',
  'project.field.sections': 'Estaciones',
  'project.field.codeRef': 'Código',
  'project.field.codeRefPlaceholder': 'LOLL26',
  'project.createEvent': 'Crear evento',
  'project.eventsEmpty': 'Aún no hay eventos. Agrega uno para colocar estaciones.',
  'project.noAddress': 'Sin dirección',
  'project.tbd': 'Por definir',
  'project.noun.events': 'eventos',
  'project.notFound': 'Proyecto no encontrado',
  'project.eventCreatedToast': 'Evento creado',
  'project.eventCreateFailedToast': 'Falló crear',
  'project.updatedToast': 'Proyecto "{name}" actualizado',
  'project.updateFailedToast': 'No se pudo actualizar el proyecto',
  'project.deletedToast': 'Proyecto "{name}" eliminado',
  'project.deleteFailedToast': 'No se pudo eliminar el proyecto',
  'project.edit.title': 'Editar proyecto',
  'project.edit.description': 'Actualiza el nombre, código, estado y fechas. Los eventos se mantienen.',
  'project.edit.field.name': 'Nombre del proyecto',
  'project.edit.field.codeRef': 'Código de referencia',
  'project.edit.field.codeRefPlaceholder': 'JST26',
  'project.edit.field.status': 'Estado',
  'project.edit.note': 'las fechas del proyecto se derivan de los eventos. Edita cada evento para cambiar el calendario.',
  'project.edit.noteLabel': 'Nota:',
  'project.edit.field.startDate': 'El proyecto inicia',
  'project.edit.field.endDate': 'El proyecto termina',
  'project.edit.rangeNoteLabel': 'Rango de fechas:',
  'project.edit.rangeNote': 'esta ventana rige las fechas permitidas en cada evento del proyecto. Déjalo vacío para derivarlo de los eventos.',
  'project.edit.rangeInvalid': 'El fin del proyecto debe ser posterior al inicio.',
  // Pestañas del workspace de proyecto
  'project.tab.plan': 'Planificar',
  'project.tab.track': 'Seguir',
  'project.tab.measure': 'Report',
  // Pestaña PLAN — Planner
  'planner.xotskiCarousel.title': 'Xotskis',
  'planner.xotskiCarousel.empty': 'Esta marca aún no tiene xotskis.',
  'planner.xotskiCarousel.availableTip': '{n} disponibles en el pool de la marca',
  'planner.xotskiCarousel.outOfStock': 'Sin stock — repón para poder asignarlo',
  'planner.xotskiCarousel.out': 'Sin stock',
  'planner.stationSource.label': '+ Estación',
  'planner.reporters.title': 'Reporteros',
  'planner.reporters.emailPlaceholder': 'correo@dominio.com',
  'planner.reporters.namePlaceholder': 'Nombre (opcional)',
  'planner.reporters.inviteToSelected': 'Invitar al evento seleccionado',
  'planner.reporters.selectEventFirst': 'Selecciona un evento para invitar',
  'planner.reporters.empty': 'Aún no hay reporteros. Invita uno arriba o arrástralo a un evento.',
  'planner.reporters.dragHint': 'Arrastra a un evento para (re)invitar',
  'planner.reporters.invitedToast': 'Reportero invitado',
  'planner.reporters.inviteFailedToast': 'No se pudo invitar al reportero',
  'planner.reporters.statusConfirmed': 'Confirmado',
  'planner.reporters.statusPending': 'Pendiente',
  'planner.reporters.statusRevoked': 'Revocado',
  'planner.reporters.statusExpired': 'Expirado',
  'planner.reporters.remove': 'Quitar reportero',
  'planner.reporters.removed': 'Reportero eliminado',
  'planner.reporters.removeFailed': 'No se pudo eliminar el reportero',
  'planner.alloc.available': 'quedan {n}',
  'planner.alloc.increase': 'Aumentar cantidad',
  'planner.alloc.decrease': 'Disminuir cantidad',
  'planner.alloc.atCeiling': 'No quedan más unidades en el brand',
  'planner.alloc.remove': 'Quitar del evento',
  'planner.alloc.removed': 'Xotski eliminado del evento',
  'planner.alloc.removeFailed': 'No se pudo eliminar el xotski',
  'planner.alloc.qtyFailed': 'No se pudo actualizar la cantidad',
  // Editor de allocations por estación (en línea)
  'planner.station.expand': 'Expandir estación',
  'planner.station.collapse': 'Contraer estación',
  'planner.station.sliceCount': '{count} slices',
  'planner.station.empty': 'Aún no hay xotskis aquí. Suelta uno en el evento para surtir cada estación.',
  'planner.station.drag': 'Arrastra para reordenar',
  'planner.station.duplicate': 'Duplicar como otro slice',
  'planner.station.duplicated': 'Slice duplicado',
  'planner.station.duplicateFailed': 'No se pudo duplicar el slice',
  'planner.station.reorderFailed': 'No se pudo guardar el nuevo orden',
  'planner.station.preview': 'Previsualizar la ruleta',
  'planner.station.previewTitle': 'Preview · {name}',
  'planner.station.previewNoSurvey': 'Este evento aún no tiene journey configurado — elige un template en la pestaña Journey del evento.',
  'planner.alloc.groupedHint': '{n} asignaciones · {total} unidades en total. Editá las cantidades por station en el detalle de la station.',
  'planner.alloc.removeGroupTitle': '¿Quitar “{name}” de este evento?',
  'planner.alloc.removeGroupBody': 'Esto elimina las {n} asignaciones de “{name}” en todas las stations de este evento ({total} unidades en total) y lo saca de sus ruletas. Las unidades vuelven al pool de la marca; los premios ya ganados se conservan. Si querés quitarlo de una sola station, hacelo desde el detalle de esa station.',
  'planner.properties.title': 'Propiedades del evento',
  'planner.properties.noSelection': 'Selecciona un evento para editar sus propiedades.',
  'planner.properties.savedToast': 'Evento guardado',
  'planner.properties.saveFailedToast': 'No se pudo guardar el evento',
  'planner.properties.endAfterStart': 'El fin debe ser posterior al inicio.',
  'planner.properties.outOfRange': 'Las fechas deben estar dentro del rango del proyecto.',
  'planner.properties.save': 'Guardar',
  'planner.properties.sectionsReadonly': 'Solo lectura — igual al número de estaciones del evento',
  'planner.properties.sectionsHint': 'solo lectura',
  'planner.properties.surveyTemplate': 'Plantilla de journey',
  'planner.properties.pickTemplate': 'Elige una plantilla…',
  'planner.properties.applyingTemplate': 'Aplicando…',
  'planner.properties.templateApplied': 'Plantilla aplicada al evento',
  'planner.properties.templateFailed': 'No se pudo aplicar la plantilla',
  'planner.events.title': 'Eventos en planificación',
  'planner.events.empty': 'No hay eventos por planificar. Usa “Nuevos eventos” para crear.',
  'planner.events.collapse': 'Colapsar',
  'planner.events.expand': 'Expandir',
  'planner.events.openConfig': 'Abrir config',
  'planner.events.xotskis': 'Xotskis',
  'planner.events.reporters': 'Reporteros',
  'planner.events.dropXotskiHint': 'Arrastra un xotski aquí para asignarlo.',
  'planner.events.xotskiRollupEmpty': 'Aún no hay xotskis — suelta uno en una estación abajo para agregarlo.',
  'planner.events.dropReporterHint': 'Arrastra un reportero aquí para invitarlo.',
  'planner.events.stationCount': '{count} estación(es)',
  'planner.events.dropStationHint': 'Arrastra el chip “+ Estación” aquí para añadir una.',
  'planner.events.openStationConfig': 'Abrir configuración de la estación',
  'planner.events.removeStation': 'Quitar estación',
  'planner.events.lastStation': 'Un evento debe mantener al menos una estación',
  'planner.events.stationRemoved': 'Estación eliminada',
  'planner.events.stationRemoveFailed': 'No se pudo eliminar la estación',
  'planner.phase.live': 'En vivo',
  'planner.phase.upcoming': 'Próximo',
  'planner.phase.edit': 'En edición',
  'planner.phase.done': 'Completado',
  'planner.batch.open': 'Nuevos eventos',
  'planner.batch.title': 'Crear varios eventos',
  'planner.batch.description': 'Elige cuántos eventos crear. Los nombres y fechas se completan automáticamente y cada evento nace con una estación.',
  'planner.batch.countLabel': '¿Cuántos eventos?',
  'planner.batch.noRange': 'sin rango de proyecto definido',
  'planner.batch.previewNames': 'Nombres: {first} … {last}',
  'planner.batch.previewRange': 'Fechas repartidas en {range}',
  'planner.batch.previewStation': 'Cada evento empieza con una estación.',
  'planner.batch.cancel': 'Cancelar',
  'planner.batch.create': 'Crear eventos',
  'planner.batch.createdToast': '{count} evento(s) creados',
  'planner.batch.failedToast': 'No se pudieron crear los eventos',
  'planner.drop.xotskiDone': '{name} asignado a todas las estaciones',
  'planner.drop.xotskiToStation': '{name} asignado a esta estación',
  'planner.drop.xotskiUseStation': 'Suelta el xotski sobre una estación para asignarlo.',
  'planner.drop.xotskiOnStationExists': '{name} ya está en esta estación — usa +/- o duplícalo',
  'planner.drop.reporterDone': '{name} invitado',
  'planner.drop.stationDone': 'Estación añadida',
  'planner.drop.failed': 'Falló al soltar',
  'planner.drop.noStations': 'Este evento aún no tiene estaciones.',
  'planner.drop.allStationsFailed': 'No se pudo asignar a ninguna estación.',
  'planner.drop.partial': 'Asignado, pero {failed} de {total} estaciones fallaron.',
  'planner.drop.here': 'Soltar aquí',
  'planner.drop.xotskiExists': '{name} ya está en este evento — usa +/- para cambiar la cantidad',
  'planner.drop.reporterExists': '{name} ya está invitado a este evento',
  'sharedQr.pick.title': '¿Dónde estás?',
  'sharedQr.pick.body': 'Elige tu estación para empezar.',
  'sharedQr.loading.title': 'Cargando…',
  'sharedQr.loading.body': 'Obteniendo las estaciones de este QR.',
  'sharedQr.invalid.title': 'QR inválido',
  'sharedQr.invalid.body': 'Este QR compartido no es válido o fue eliminado.',
  'sharedQr.empty.title': 'Sin estaciones',
  'sharedQr.empty.body': 'Este QR aún no tiene estaciones asignadas.',
  'sharedQr.manager.title': 'QR compartido',
  'sharedQr.manager.subtitle': 'Un QR para varias estaciones — el consumidor elige su estación primero.',
  'sharedQr.manager.create': 'Crear QR compartido',
  'sharedQr.manager.empty': 'Este evento aún no tiene QR compartidos.',
  'sharedQr.manager.copy': 'Copiar enlace',
  'sharedQr.manager.copied': 'Enlace copiado',
  'sharedQr.manager.delete': 'Eliminar',
  'sharedQr.manager.removed': 'QR compartido eliminado',
  'sharedQr.manager.removeFailed': 'No se pudo eliminar el QR compartido',
  'sharedQr.manager.dialogTitle': 'Crear un QR compartido',
  'sharedQr.manager.dialogBody': 'Elige 2 o más estaciones para compartir un QR. Cada una conserva sus xotskis.',
  'sharedQr.manager.nameLabel': 'Nombre',
  'sharedQr.manager.defaultName': 'QR compartido',
  'sharedQr.manager.pickStations': 'Estaciones',
  'sharedQr.manager.needTwo': 'Este evento necesita al menos 2 estaciones para compartir un QR.',
  'sharedQr.manager.cancel': 'Cancelar',
  'sharedQr.manager.createBtn': 'Crear',
  'sharedQr.manager.createdToast': 'QR compartido creado',
  'sharedQr.manager.createFailed': 'No se pudo crear el QR compartido',
  // Pestaña TRACK
  'track.empty': 'Este proyecto aún no tiene eventos.',
  'track.noDate': 'Sin fecha',
  'track.edit': 'Editar',
  'track.view': 'Ver',
  'track.group.live': 'En vivo ahora',
  'track.group.upcoming': 'Próximos',
  'track.group.edit': 'En edición',
  'track.group.done': 'Completados',
  // Pestaña MEASURE
  'measure.range': 'Rango de fechas',
  'measure.events': 'Eventos',
  'measure.live': 'En vivo ahora',
  'measure.stockChartTitle': 'Asignación de stock en el proyecto',
  'measure.noStockData': 'Aún no se ha asignado stock.',
  'measure.quantity': 'Asignado',
  'measure.available': 'Disponible',
  'project.edit.confirmTitle': '¿Eliminar este proyecto?',
  'project.edit.confirmBody': 'Sus eventos, estaciones y asignaciones de stock se eliminarán permanentemente.',
  'project.edit.deleting': 'Eliminando…',
  'project.edit.confirmDelete': 'Confirmar eliminación',
  'project.edit.deleteProject': 'Eliminar proyecto',
  'project.edit.cancel': 'Cancelar',
  'project.edit.saving': 'Guardando…',
  'project.edit.saveChanges': 'Guardar cambios',

  // ── Access Manager ─────────────────────────────────────────────────
  'accessManager.title': 'Gestor de accesos',
  'accessManager.scope': 'alcance: {app}',
  'accessManager.subtitle': 'Administra miembros, roles y permisos externos para tu espacio y agencias hijas. Los listados y nuevas entradas se limitan a la app {app} — cambia de app para gestionar otros módulos.',
  'accessManager.tab.members': 'Miembros',
  'accessManager.tab.roles': 'Roles',
  'accessManager.tab.grants': 'Compartido externo',
  'accessManager.currentApp': 'actual',

  // ── Members tab ────────────────────────────────────────────────────
  'members.title': 'Miembros',
  'members.addMember': 'Agregar miembro',
  'members.empty.title': 'Aún no hay miembros',
  'members.empty.body': 'Agrega al primer miembro de tu equipo.',
  'members.confirmDelete': '¿Eliminar suavemente a {email}?',
  'members.deletedToast': 'Miembro removido',
  'members.col.name': 'Nombre',
  'members.col.email': 'Correo',
  'members.col.company': 'Marca',
  'members.col.role': 'Rol',
  'members.col.actions': 'Acciones',
  'members.youBadge': 'tú',
  'members.openRoleTitle': 'Abrir rol "{role}" en la pestaña de roles',
  'members.cantDeleteSelf': 'No puedes eliminar tu propia cuenta',
  'members.setPassword.action': 'Cambiar contraseña',
  'members.setPassword.title': 'Cambiar contraseña',
  'members.setPassword.description': 'Define una nueva contraseña para {name}. La usará en su próximo inicio de sesión.',
  'members.setPassword.newPassword': 'Nueva contraseña',
  'members.setPassword.confirm': 'Confirmar contraseña',
  'members.setPassword.submit': 'Guardar contraseña',
  'members.setPassword.show': 'Mostrar contraseña',
  'members.setPassword.hide': 'Ocultar contraseña',
  'members.setPassword.minLength': 'La contraseña debe tener al menos {n} caracteres.',
  'members.setPassword.mismatch': 'Las contraseñas no coinciden.',
  'members.setPassword.successToast': 'Contraseña actualizada para {name}',
  'members.setPassword.errorToast': 'No se pudo cambiar la contraseña',

  // ── Roles tab ──────────────────────────────────────────────────────
  'roles.title': 'Roles',
  'roles.createRole': 'Crear rol',
  'roles.empty.title': 'Aún no hay roles',
  'roles.empty.body': 'Crea roles para asignar paquetes de permisos reutilizables.',
  'roles.confirmDelete': '¿Eliminar rol {name}?',
  'roles.deletedToast': 'Rol eliminado',
  'roles.templateBadge': 'plantilla',
  'roles.permsCount': '{count} permiso(s)',
  'roles.cloneTitle': 'Crear un nuevo rol con estos mismos permisos',
  'roles.clone': 'Clonar',
  'roles.readOnlyTitle': 'Las plantillas del sistema son de solo lectura. Clona para personalizar.',
  'roles.readOnlyLabel': 'Plantilla de solo lectura',

  // ── Grants tab ─────────────────────────────────────────────────────
  'grants.title': 'Compartido externo',
  'grants.comingSoonNotice': 'Compartido externo estará disponible próximamente. Esta funcionalidad aún no está habilitada — ¡pronto la tendrás!',
  'grants.shareAccess': 'Compartir acceso',
  'grants.confirmRevoke': '¿Revocar acceso compartido con {email}?',
  'grants.revokedToast': 'Acceso revocado',
  'grants.tab.outgoing': 'Otorgados por mí',
  'grants.tab.incoming': 'Otorgados a mí',
  'grants.empty.outgoingTitle': 'No hay accesos activos',
  'grants.empty.incomingTitle': 'No hay accesos entrantes',
  'grants.empty.outgoingBody': 'Comparte el acceso a una de tus empresas con un usuario externo.',
  'grants.empty.incomingBody': 'Aún no han compartido acceso contigo.',
  'grants.user': 'usuario #{id}',
  'grants.expires': 'expira {date}',
  'grants.permsCount': '{count} permiso(s)',
  'grants.grantedBy': 'otorgado por {email}',
  'grants.revoke': 'Revocar',

  // ── Brand Identity page (additional descriptive variants) ──────────
  'brandIdentity.subtitleLong': 'Valores por defecto a nivel marca para logo, colores, tipografía y nombre de campaña. Las personalizaciones por evento (recolección de datos, age gate, juego, premios) viven en la Configuración de Journey de cada evento.',
  'brandIdentity.saveChanges': 'Guardar cambios',
  'brandIdentity.savedToast': 'Identidad de marca guardada',
  'brandIdentity.saveFailedToast': 'Falló al guardar',

  // ── Brand Studio (new) ─────────────────────────────────────────────
  'brandStudio.title': 'Brand Studio',
  'brandStudio.editing': 'Editando',
  'brandStudio.newBrandInherit': 'Nueva marca · heredada de',
  'brandStudio.back': 'Volver',
  'brandStudio.deleting': 'Eliminando…',
  'brandStudio.confirmDelete': 'Confirmar eliminación',
  'brandStudio.deleteBrand': 'Eliminar marca',
  'brandStudio.cancel': 'Cancelar',
  'brandStudio.saving': 'Guardando…',
  'brandStudio.saveChanges': 'Guardar cambios',
  'brandStudio.savePublish': 'Guardar y publicar',
  'brandStudio.confirmDeleteTitle': '¿Eliminar esta marca?',
  'brandStudio.confirmDeleteBody': 'Todos sus proyectos, eventos, estaciones, stock y journeys serán removidos permanentemente. Haz clic en',
  'brandStudio.confirmDeleteEmph': 'Confirmar eliminación',
  'brandStudio.confirmDeleteEnd': 'otra vez para continuar.',
  'brandStudio.brandCreated': 'Marca "{name}" creada',
  'brandStudio.brandUpdated': 'Marca "{name}" actualizada',
  'brandStudio.brandDeleted': 'Marca "{name}" eliminada',
  'brandStudio.createFailed': 'No se pudo crear la marca',
  'brandStudio.updateFailed': 'No se pudo actualizar la marca',
  'brandStudio.kitSaveFailed': 'La marca se guardó pero el kit (colores / defaults de experiencia) no se pudo persistir',
  'brandStudio.deleteFailed': 'No se pudo eliminar la marca',
  'brandStudio.error.nameRequired': 'El nombre de la marca es obligatorio',
  'brandStudio.error.primaryHex': 'El color primario debe ser un valor hex (#RGB o #RRGGBB)',
  'brandStudio.error.secondaryHex': 'El color secundario debe ser un valor hex',
  'brandStudio.error.textHex': 'El color de texto debe ser un valor hex',
  'brandStudio.fixOne': 'Corrige {count} problema antes de guardar:',
  'brandStudio.fixMany': 'Corrige {count} problemas antes de guardar:',
  'brandStudio.tab.identity': 'Identidad',
  'brandStudio.tab.experience': 'Experiencia',
  'brandStudio.tab.lookFeel': 'Estilo',
  'brandStudio.tab.game': 'Juego',
  'brandStudio.tab.prizes': 'Premios',
  'brandStudio.tab.output': 'Salida',
  'brandStudio.livePreview': 'Vista previa en vivo',
  'brandStudio.previewFooter': 'La vista previa se actualiza al cambiar los ajustes — bienvenida, CTA del juego y pantalla de premio comparten estos estilos.',
  // Caso 05 v1 — Simulación de animación de celebración
  'brandStudio.game.celebration': 'Animación de celebración',
  'brandStudio.game.test': 'Probar celebración',
  'brandStudio.game.themeHint.confetti': 'Lluvia de papel — se siente festivo.',
  'brandStudio.game.themeHint.fireworks': 'Mayor impacto — explosiones radiales.',
  'brandStudio.game.themeHint.sparks': 'Brillo sutil — minimalista y elegante.',
  // Identity tab
  'brandStudio.identity.hint': 'Sube tu logo primero — en el futuro Atledi sugerirá colores de marca automáticamente. Por ahora, define tu paleta de 5 colores.',
  'brandStudio.identity.brandName': 'Nombre de la marca',
  'brandStudio.identity.brandNameHint': 'Cómo aparecerá esta marca en Xotski.',
  'brandStudio.identity.brandNamePlaceholder': 'p. ej. Joyly',
  'brandStudio.identity.industry': 'Industria',
  'brandStudio.identity.industryHint': 'Opcional. Ayuda a Atledi a adaptar plantillas.',
  'brandStudio.identity.industryPlaceholder': 'Bebidas',
  'brandStudio.identity.campaignName': 'Nombre de campaña',
  'brandStudio.identity.campaignNameHint': 'Usado en URLs de QR, etiquetas de estación y reportes.',
  'brandStudio.identity.campaignNamePlaceholder': 'p. ej. Joyly Spring Tour 2026',
  'brandStudio.identity.brandLogo': 'Logo de marca',
  'brandStudio.identity.brandLogoHint': 'Heredado de la empresa hasta que se sobrescriba.',
  'brandStudio.identity.noLogo': 'Sin logo',
  'brandStudio.identity.qrThumb': 'Thumbnail del QR',
  'brandStudio.identity.qrThumbHint': 'Imagen cuadrada dentro del QR. Si está vacío usa el logo.',
  'brandStudio.identity.noThumb': 'Sin thumb',
  'brandStudio.identity.backgroundImage': 'Imagen de fondo',
  'brandStudio.identity.backgroundImageHint': 'Foto a sangre detrás de la journey. Vacío = color de fondo sólido.',
  'brandStudio.identity.noImage': 'Sin imagen',
  'brandStudio.identity.palette': 'Paleta de 5 colores',
  'brandStudio.identity.color.primary': 'Primario',
  'brandStudio.identity.color.primaryDesc': 'Headers, fondo del QR, superficies principales',
  'brandStudio.identity.color.secondary': 'Secundario',
  'brandStudio.identity.color.secondaryDesc': 'Superficies de apoyo, bordes, divisores',
  'brandStudio.identity.color.accent': 'Acento',
  'brandStudio.identity.color.accentDesc': 'Botones, estados activos, primer plano del QR',
  'brandStudio.identity.color.background': 'Fondo',
  'brandStudio.identity.color.backgroundDesc': 'Fondos de tarjetas y contenedores',
  'brandStudio.identity.color.text': 'Texto',
  'brandStudio.identity.color.textDesc': 'Texto principal en superficies de marca',
  'brandStudio.identity.brandFont': 'Tipografía de marca',
  // Experience tab
  'brandStudio.experience.hint': 'Las journeys bilingües logran hasta 34% más finalización en eventos multiculturales.',
  'brandStudio.experience.surveyLanguage': 'Idioma de la journey',
  'brandStudio.experience.english': 'Inglés',
  'brandStudio.experience.spanish': 'Español',
  'brandStudio.experience.bilingualHint': 'Modo bilingüe — los usuarios eligen el idioma al inicio',
  'brandStudio.experience.contactInfo': 'Datos de contacto',
  'brandStudio.experience.collectContact': 'Recolectar datos de contacto',
  'brandStudio.experience.fullName': 'Nombre completo',
  'brandStudio.experience.email': 'Correo',
  'brandStudio.experience.phone': 'Teléfono',
  'brandStudio.experience.ageGate': 'Filtro de edad',
  'brandStudio.experience.disabled': 'Desactivado',
  'brandStudio.experience.demographics': 'Demografía',
  'brandStudio.experience.ageRange': 'Rango de edad',
  'brandStudio.experience.gender': 'Género',
  'brandStudio.experience.ethnicity': 'Etnia',
  'brandStudio.experience.nationality': 'Nacionalidad',
  'brandStudio.experience.pronouns': 'Pronombres',
  'brandStudio.experience.customQuestions': 'Preguntas personalizadas',
  'brandStudio.experience.customQuestionsLabel': '{count} pregunta(s) personalizada(s) agregada(s)',
  'brandStudio.experience.customQuestionsAccessOn': 'Tu plan permite preguntas personalizadas. El editor llega en Fase 4.',
  'brandStudio.experience.customQuestionsAccessOff': 'Actualiza tu plan para agregar preguntas personalizadas al flujo del consumidor.',
  // Look & Feel tab
  'brandStudio.look.hint': 'Estilos de botón y celebraciones consistentes construyen reconocimiento de marca.',
  'brandStudio.look.buttonStyle': 'Estilo de botón',
  'brandStudio.look.next': 'Siguiente',
  'brandStudio.look.celebration': 'Tema de celebración',
  'brandStudio.look.noAnimation': 'Sin animación',
  'brandStudio.look.consent': 'Texto de consentimiento',
  'brandStudio.look.consentHint': 'Mostrado antes de la recolección de datos. Debe cumplir tu política de privacidad.',
  'brandStudio.look.gameCTAEn': 'CTA del juego (EN)',
  'brandStudio.look.gameCTAEnPlaceholder': 'Spin to win!',
  'brandStudio.look.gameCTAEs': 'CTA del juego (ES)',
  'brandStudio.look.gameCTAEsPlaceholder': '¡Gira para ganar!',
  // Game tab
  'brandStudio.game.hint': 'Cada juego destaca tu marca de forma distinta. La ruleta viene en todos los planes; los premium se desbloquean con upgrades.',
  'brandStudio.game.chooseYourGame': 'Elige tu juego',
  'brandStudio.game.brandIntegration': 'Integración de marca',
  'brandStudio.brandIntegration': 'Integración de marca',
  'brandStudio.game.wheel.name': 'Ruleta',
  'brandStudio.game.wheel.desc': 'Gira para ganar — el clásico. Logo en el centro, colores de marca en los segmentos.',
  'brandStudio.game.wheel.s1': 'Logo en el centro de la ruleta',
  'brandStudio.game.wheel.s2': 'Color de marca en los segmentos',
  'brandStudio.game.wheel.s3': 'Foto del premio llena la pantalla de revelación',
  'brandStudio.game.scratch.name': 'Tarjeta para raspar',
  'brandStudio.game.scratch.desc': 'Raspa para revelar — táctil y satisfactorio.',
  'brandStudio.game.scratch.s1': 'Marca de agua del logo bajo la superficie',
  'brandStudio.game.scratch.s2': 'Degradado del color de marca',
  'brandStudio.game.scratch.s3': 'Foto del premio revelada abajo',
  'brandStudio.game.pirinola.name': 'Pirinola',
  'brandStudio.game.pirinola.desc': 'Gira la pirinola — clásico cultural con un giro digital.',
  'brandStudio.game.pirinola.s1': 'Colores de marca en las caras',
  'brandStudio.game.pirinola.s2': 'Logo en la cara superior',
  'brandStudio.game.pirinola.s3': 'Nombre del premio en la cara ganadora',
  'brandStudio.game.random.name': 'Sorteo aleatorio',
  'brandStudio.game.random.desc': 'Revelación estilo lotería — suspenso y sorpresa.',
  'brandStudio.game.random.s1': 'Animación con color de marca',
  'brandStudio.game.random.s2': 'Logo en el encabezado',
  'brandStudio.game.random.s3': 'Tarjeta de premio con foto del producto',
  'brandStudio.game.plinko.name': 'Plinko',
  'brandStudio.game.plinko.desc': 'Suelta la bola — diversión basada en física con pegs personalizados.',
  'brandStudio.game.plinko.s1': 'Color de marca en pegs y tablero',
  'brandStudio.game.plinko.s2': 'Logo en la bola',
  'brandStudio.game.plinko.s3': 'Casillas de premio abajo',
  'brandStudio.game.soccer.name': 'Balón de Fútbol',
  'brandStudio.game.soccer.desc': 'Toca para patear el balón al arco — los slots dentro revelan el premio.',
  'brandStudio.game.soccer.s1': 'Color de marca en el balón y el marco del arco',
  'brandStudio.game.soccer.s2': 'Logo en los slots de premio',
  'brandStudio.game.soccer.s3': 'Ideal para marcas deportivas y activaciones de bebidas',
  'brandStudio.game.loteria.name': 'Lotería',
  'brandStudio.game.loteria.desc':
    'Juego de mesa folclórico mexicano. Tablero de 4×3. Completa una fila para ganar. La mayor resonancia cultural para eventos latinos.',
  'brandStudio.game.loteria.s1': 'Colores de marca en la fila ganadora y el banner de Jackpot',
  'brandStudio.game.loteria.s2': 'Tablero auto-marcado · mayor tiempo de juego de todos',
  'brandStudio.game.loteria.s3': 'Ancla de festival; ideal para activaciones en mercado hispano',
  'brandStudio.game.slots.name': 'Tragamonedas',
  'brandStudio.game.slots.desc':
    'Tres rodillos de íconos de premio. Toca para girar; alinea la fila central para ganar.',
  'brandStudio.game.slots.s1': 'Colores de marca en el gabinete y el marco de rodillos',
  'brandStudio.game.slots.s2': 'Íconos de premio en cada rodillo',
  'brandStudio.game.slots.s3': 'Mecánica de alta energía; ideal para stands estilo arcade',
  'brandStudio.gamePicker.selectedGame': 'Juego seleccionado',
  'brandStudio.gamePicker.tierRequired': 'Plan requerido',
  'publicSurvey.soccer.kick': '¡Patea!',
  'publicSurvey.soccer.kicking': 'Pateando…',
  'publicSurvey.soccer.goal': '¡GOL!',
  'publicSurvey.soccer.fieldAlt': 'Campo de fútbol con arco',
  'publicSurvey.loteria.label': 'Lotería',
  'publicSurvey.loteria.cardCalled': 'Carta cantada',
  'publicSurvey.loteria.getReady': 'Prepárate…',
  'publicSurvey.loteria.start': '¡Lotería!',
  'publicSurvey.loteria.hint': 'Las cartas se marcan solas · Completa una fila para ganar',
  // PRD §18 — Experiencia de juego unificada (labels compartidos; sin emojis)
  'publicSurvey.games.youWon': 'Ganaste',
  'publicSurvey.games.tapToPlay': 'Toca para jugar',
  'publicSurvey.games.sound': 'Sonido',
  'publicSurvey.games.soundOn': 'Sonido activado',
  'publicSurvey.games.soundOff': 'Sonido desactivado',
  'publicSurvey.games.wheel.action': 'Gira la ruleta',
  'publicSurvey.games.wheel.busy': 'Girando…',
  'publicSurvey.games.wheel.hub': 'GIRA',
  'publicSurvey.games.scratch.action': 'Raspa para revelar',
  'publicSurvey.games.scratch.hint': 'Raspa con tu dedo',
  'publicSurvey.games.scratch.revealed': '¡Revelado!',
  'publicSurvey.games.slots.action': 'Jala',
  'publicSurvey.games.slots.busy': 'Girando…',
  'publicSurvey.games.plinko.action': 'Suelta la bola',
  'publicSurvey.games.plinko.busy': 'Cayendo…',
  'publicSurvey.games.plinko.tagline': 'Suelta · Rebota · Gana',
  'publicSurvey.games.pirinola.action': 'Gira la pirinola',
  'publicSurvey.games.pirinola.busy': 'Girando…',
  'publicSurvey.games.randomDraw.action': 'Saca una carta',
  'publicSurvey.games.randomDraw.busy': 'Barajando…',
  'publicSurvey.games.randomDraw.done': '¡Revelado!',
  'publicSurvey.games.loteria.waiting': 'Esperando…',
  'brandStudio.game.wheelTaglineEn': 'Tagline de la ruleta (EN)',
  'brandStudio.game.wheelTaglineEs': 'Tagline de la ruleta (ES)',
  'brandStudio.game.wheelTaglineEnPlaceholder': 'Spin to win!',
  'brandStudio.game.wheelTaglineEsPlaceholder': '¡Gira para ganar!',
  'brandStudio.game.heroImage': 'Imagen hero de la ruleta',
  'brandStudio.game.heroImageHint': 'Opcional. Foto de producto o banner detrás de la ruleta.',
  'brandStudio.game.noHero': 'Sin hero',
  'brandStudio.game.tier.free': 'Gratis',
  'brandStudio.game.tier.premium': 'Premium',
  'brandStudio.game.tier.pro': 'Pro',
  // Prizes tab
  'brandStudio.prizes.hint': '3–5 premios con rareza variada funcionan mejor. Raro (1–2 estrellas) crea emoción; común (4–5 estrellas) mantiene a todos ganando.',
  'brandStudio.prizes.inventory': 'Inventario de premios',
  'brandStudio.prizes.selected': '{count} seleccionados',
  'brandStudio.prizes.units': '{count} unidades',
  'brandStudio.prizes.revealPreview': 'Vista previa de revelación de premio',
  'brandStudio.prizes.youWon': '¡Ganaste!',
  'brandStudio.prizes.fallback': 'Premio',
  'brandStudio.prizes.claim': 'Muestra esta pantalla al staff del evento para reclamar tu premio',
  'brandStudio.prizes.rare': 'Raro',
  'brandStudio.prizes.medium': 'Medio',
  'brandStudio.prizes.common': 'Común',
  'brandStudio.prizes.inv.tshirt': 'Playera personalizada',
  'brandStudio.prizes.inv.hat': 'Gorra snapback',
  'brandStudio.prizes.inv.sticker': 'Pack de stickers',
  'brandStudio.prizes.inv.tote': 'Tote bag de lona',
  'brandStudio.prizes.inv.bottle': 'Botella de agua',
  'brandStudio.prizes.inv.hoodie': 'Hoodie premium',
  // Output tab
  'brandStudio.output.hint': 'Cada estación tiene su propio QR — los colores y logo configurados aquí se aplican automáticamente.',
  'brandStudio.output.universalQr': 'Código QR universal',
  'brandStudio.output.embedLogo': 'El QR incluye tu logo embebido',
  'brandStudio.output.uploadLogo': 'Sube un logo para embeberlo en el QR',
  'brandStudio.output.shareableLink': 'Enlace para compartir',
  'brandStudio.output.uniquePerStation': 'Cada estación tiene su propio enlace y QR',
  'brandStudio.output.uniquePerStationBody': 'Los enlaces públicos se generan por estación, no por marca. Ve a {path} para descargar QRs individuales. Los ajustes de marca configurados aquí se aplican automáticamente.',
  'brandStudio.output.path': 'Proyectos → Evento → Estación',
  'brandStudio.output.downloadPng': 'Descargar PNG',
  'brandStudio.output.downloadPngHint': 'Disponible tras guardar y conectar a una estación',
  'brandStudio.output.printDisplay': 'Imprimir',
  'brandStudio.output.upload': 'Subir',
  'brandStudio.output.clear': 'Limpiar',
  // Preview
  'brandStudio.preview.yourBrand': 'Tu marca',
  'brandStudio.preview.brandWorkspace': 'Espacio de marca',
  'brandStudio.preview.fallbackCampaign': 'Muestreo de {brand}',
  'brandStudio.preview.continue': 'Continuar',
  'brandStudio.preview.spinToWin': 'Gira y gana',

  // ── Surveys list / templates / preview / builder ───────────────────
  'surveys.browseTemplates': 'Ver plantillas',
  'surveys.emptyTitle': 'Aún no hay journeys',
  'surveys.emptyBody': 'Empieza desde una plantilla o crea una journey desde cero.',
  'surveys.allSurveys': 'Todas las journeys',
  'surveys.locked': 'Bloqueada',
  'surveys.template': 'Plantilla',
  'surveys.preview': 'Vista previa',
  'surveys.templates.emptyTitle': 'Aún no hay plantillas',
  'surveys.templates.emptyBody': 'Las plantillas del sistema aparecerán aquí.',
  'surveys.templates.stepsCount': '{n} pasos',
  'surveys.toast.cloned': 'Plantilla clonada',
  'surveys.toast.cloneFailed': 'Falló la clonación',
  'surveys.preview.badge': 'Vista previa',
  'surveys.preview.editInBuilder': 'Editar en builder',
  'surveys.preview.stepsAndComponents': '{steps} pasos · {components} componentes',
  'surveys.preview.openBuilder': 'Abrir constructor',
  'surveys.preview.stepsTitle': 'Pasos',
  'surveys.preview.noSteps': 'Aún no hay pasos.',
  'surveys.preview.stepN': 'Paso {n}',
  'surveys.preview.noLabel': 'sin etiqueta',
  'surveys.preview.publicLink': 'Enlace público',
  'surveys.preview.publicLinkBody':
    'Esto es lo que ven los consumidores al escanear el QR de la estación. Asocia la journey a una estación para verla con contexto completo (marca, stocks, ruleta).',
  'surveys.preview.openConsumer': 'Abrir vista del consumidor',
  'surveys.builder.backToSurveys': 'Volver a journeys',
  'surveys.builder.settings': 'Ajustes de la journey',
  'surveys.builder.field.name': 'Nombre',
  'surveys.builder.field.description': 'Descripción',
  'surveys.builder.field.webhookUrl': 'URL de webhook',
  'surveys.builder.translatable': 'Traducible',
  'surveys.builder.translatableHelp': 'Muestra el selector de idioma al consumidor (EN/ES).',
  'surveys.builder.stepsComponents': 'Pasos y componentes',
  'surveys.builder.toast.saved': 'Journey guardada',
  'surveys.builder.toast.saveFailed': 'Fallo al guardar',

  // ── Public-survey end-user surface ─────────────────────────────────
  'publicSurvey.finishPage.title': '¡Gracias por participar!',
  'publicSurvey.finishPage.body': 'Vuelve cuando quieras — siempre hay más por desbloquear.',
  'publicSurvey.missingSurveyOrStation': 'Falta la journey o la estación.',
  // Caso 15 — Pantallas terminales por plan-cap / lock.
  'publicSurvey.locked.limitExhausted':
    'Esta experiencia llegó al límite por ahora. ¡Gracias por participar!',
  'publicSurvey.locked.planExpired':
    'Esta experiencia ya no está disponible. Contacta al organizador para más información.',
  'publicSurvey.locked.manualDisable':
    'Esta experiencia está temporalmente deshabilitada por el organizador.',
  'publicSurvey.ageGate.eyebrow': 'Verificación de edad',
  'publicSurvey.ageGate.question': '¿Tienes {age} años o más?',
  'publicSurvey.ageGate.body': 'Necesitas confirmar para continuar.',
  'publicSurvey.ageGate.yes': 'Sí',
  'publicSurvey.ageGate.no': 'No',
  'publicSurvey.continueArrow': 'Continuar →',
  'publicSurvey.prizeReveal.claimHint': 'Muestra esta pantalla en la barra / estación para reclamar tu premio.',
  'publicSurvey.prizeReveal.directDelivery': 'El personal del evento te entregará tu premio directamente. No necesitas código.',
  'publicSurvey.photoFilm.eyebrow': 'Aviso',
  'publicSurvey.photoFilm.title': 'Fotografía y filmación',
  'publicSurvey.photoFilm.body': 'Al participar en este evento reconoces que podrías ser fotografiado o filmado para uso de marketing.',
  'publicSurvey.photoFilm.bodyWithBrand': 'Al participar en {brand} reconoces que podrías ser fotografiado o filmado para uso de marketing.',
  'publicSurvey.photoFilm.notACheckbox': 'Esto es un aviso, no un consentimiento — no requiere checkbox.',
  'publicSurvey.photoFilm.acknowledge': 'Entiendo',
  'publicSurvey.prizeReveal.continueExternal': 'Continuar al sitio de la marca →',
  'publicSurvey.prizeReveal.brandedThankYou': 'Gracias por jugar — mantén esta pantalla lista cuando el embajador de la marca venga a entregar tu premio.',
  'calendar.eyebrow': 'Actividad',
  'calendar.title': 'Calendario',
  'calendar.searchPlaceholder': 'Buscar eventos…',
  'calendar.statusAll': 'Todos los estados',
  'calendar.statusActive': 'Activo',
  'calendar.statusScheduled': 'Programado',
  'calendar.statusArchived': 'Archivado',
  'calendar.loading': 'Cargando eventos…',
  'calendar.empty': 'No hay eventos que coincidan con los filtros.',
  'calendar.view.list': 'Lista',
  'calendar.view.month': 'Mes',
  'insights.tabs.recap': 'Resumen Atledi',
  'insights.tabs.throughput': 'Throughput',
  'insights.tabs.distribution': 'Distribución',
  'insights.tabs.demographics': 'Demografía',
  'insights.tabs.leads': 'Leads',
  'insights.leads.eyebrow': 'Exportación CSV locked',
  'insights.leads.title': 'Captura y seguimiento de leads',
  'insights.leads.body': 'Las identidades de leads — nombres, contacto, demografía y consentimiento de marketing — viven en la superficie Contactos junto con la exportación CSV de 22 columnas locked (PRD §10.1).',
  'insights.leads.cta': 'Abrir Contactos',
  'insights.exportPdf': 'Exportar PDF',
  'insights.pdfPaid': 'PDF · Pago',
  'insights.recap.eyebrow': 'Resumen de Atledi',
  'insights.recap.narrative': 'Narrativa',
  'insights.recap.narrativePlaceholder':
    'La narrativa en vivo llega con el endpoint Atledi/Sonnet en Fase 4. Cada evento tendrá una historia de 2–3 oraciones de lo que acaba de pasar, en la voz de marca configurada en Brand Studio.',
  'insights.recap.kpis': 'Resumen de KPIs',
  'insights.recap.recommendation': 'Recomendación',
  'insights.recap.recommendationPlaceholder':
    'Una próxima acción concreta con nivel de confianza aparecerá aquí cuando se conecte el endpoint de recap.',
  'insights.recap.confidence.low': 'Baja',
  'insights.recap.confidence.medium': 'Media',
  'insights.recap.confidence.high': 'Alta',
  'insights.kpi.moments': 'Momentos',
  'insights.kpi.throughput': 'Throughput',
  'insights.kpi.inventory': 'Inventario',
  'legal.eyebrow': 'Legal · Reconocimiento único',
  'legal.brandObligations': 'Obligaciones de la marca',
  'legal.xotskiObligations': 'Obligaciones de Xotski',
  'legal.xotskiObligationsBody':
    'Xotski procesa datos personales solo bajo tus instrucciones documentadas, los mantiene en las regiones que selecciones y te notifica cualquier violación dentro de 24 horas de detección.',
  'legal.signerName': 'Nombre del firmante',
  'legal.signerTitle': 'Cargo del firmante',
  'legal.later': 'Recordarme más tarde',
  'legal.submitting': 'Guardando…',
  'legal.accept': 'Acepto',
  'reporterAdmin.eyebrow': 'Reporters',
  'reporterAdmin.title': 'Reporters de campo para este evento',
  'reporterAdmin.invite': 'Invitar reporter',
  'reporterAdmin.freeUnlimited': 'Gratis · Ilimitados',
  'reporterAdmin.paidOnly': 'Pago',
  'reporterAdmin.paidHint': 'Actualiza tu plan para invitar reporters con enlaces mágicos de 24h — reporters ilimitados gratis en todos los planes pagos.',
  'reporterAdmin.loading': 'Cargando reporters…',
  'reporterAdmin.empty': 'Aún no hay reporters. Envía un enlace mágico a tu primer BA.',
  'reporterAdmin.expiresAt': 'expira {date}',
  'reporterAdmin.state.active': 'Activo',
  'reporterAdmin.state.revoked': 'Revocado',
  'reporterAdmin.state.expired': 'Expirado',
  'reporterAdmin.reissueHint': 'Re-emitir enlace (copia la URL nueva)',
  'reporterAdmin.revokeHint': 'Revocar enlace activo',
  'reporterAdmin.revoked': 'Enlace de reporter revocado',
  'reporterAdmin.reissuedCopied': 'Nuevo enlace copiado al portapapeles',
  'reporterAdmin.invitedCopied': 'URL mágica copiada al portapapeles',
  'reporterAdmin.inviteError': 'No se pudo enviar la invitación',
  'reporterAdmin.copied': 'Copiado',
  'reporterAdmin.copy': 'Copiar URL',
  'reporterAdmin.modal.eyebrow': 'PRD §12 · Vista del Reporter',
  'reporterAdmin.modal.title': 'Invitar a un reporter de campo',
  'reporterAdmin.modal.body': 'Recibe un enlace mágico de 24h a una superficie de 4 tabs — sin cuenta ni contraseña.',
  'reporterAdmin.modal.email': 'Correo',
  'reporterAdmin.modal.nameOptional': 'Nombre (opcional)',
  'reporterAdmin.modal.namePlaceholder': 'Nombre del BA',
  'reporterAdmin.modal.send': 'Enviar invitación',
  'reporterAdmin.modal.sending': 'Enviando…',
  'reporterAdmin.modal.successTo': 'Enlace mágico enviado a {email}',
  'reporterAdmin.modal.magicUrl': 'URL mágica',
  'reporterAdmin.modal.expiresHint': 'El enlace expira {date}',
  'reporter.tabs.today': 'Hoy',
  'reporter.tabs.inventory': 'Inventario',
  'reporter.tabs.notes': 'Notas',
  'reporter.tabs.claims': 'Reclamos',
  'reporter.header.eyebrow': 'Vista del reporter',
  'reporter.header.expiresAt': 'El enlace expira {date}',
  'reporter.verifying.title': 'Verificando tu enlace…',
  'reporter.verifying.body': 'Un momento mientras abrimos tu evento.',
  'reporter.invalidLink.title': 'Este enlace no es válido',
  'reporter.invalidLink.body': 'Pídele a la marca un nuevo enlace.',
  'reporter.expired.title': 'Enlace expirado',
  'reporter.expired.body': 'Los enlaces de reporter duran 24 horas. Pide uno nuevo a la marca.',
  'reporter.terms.eyebrow': 'Términos del reporter',
  'reporter.terms.title': 'Antes de empezar en {event}',
  'reporter.terms.body': 'Vas a iniciar como reporter de la marca. Confirma estos puntos antes de capturar notas:',
  'reporter.terms.point1': 'Las notas y fotos solo se comparten con la marca organizadora.',
  'reporter.terms.point2': 'No compartas el enlace ni capturas de pantalla con códigos de premio.',
  'reporter.terms.point3': 'Puedes dejar de reportar en cualquier momento cerrando esta pestaña.',
  'reporter.terms.checkbox': 'Entiendo y quiero comenzar a reportar.',
  'reporter.terms.accept': 'Comenzar a reportar',
  'reporter.terms.submitting': 'Guardando…',
  'reporter.today.scheduleTitle': 'Agenda',
  'reporter.today.scheduleBody': 'Estaciones y horarios para {event}. Vista en vivo llega con la cola offline en Fase 3.',
  'reporter.today.atlediEyebrow': 'Briefing de Atledi',
  'reporter.today.atlediTitle': 'Tu foco ahora mismo',
  'reporter.today.atlediBody': 'Tips de capacidad + foco para la primera hora desde Atledi. Llega en Fase 4.',
  'reporter.today.atlediBriefing.morning': 'Buen día en {event}. Despliega los posters en cada entrada y enciende los lectores QR — la primera hora suele rendir 30–40% de las capturas del día.',
  'reporter.today.atlediBriefing.afternoon': 'Empuje de tarde en {event}. Refresca las estaciones agotadas, camina por la barra y re-engancha a los grupos que aún no escanearon.',
  'reporter.today.atlediBriefing.evening': 'Flujo nocturno en {event}. Vigila los timers de premios y avisa cualquier expiración cercana antes de quedarte sin inventario.',
  'reporter.today.atlediBriefing.night': 'Turno tarde en {event}. Cuenta lo que queda, captura fotos de cierre y deja notas para el manager mañana.',
  'reporter.inventory.title': 'Inventario de marca',
  'reporter.inventory.body': 'Artículos asignados a {event} con ajustes rápidos para dañados o descartados.',
  'reporter.notes.title': 'Notas de campo',
  'reporter.notes.body': 'Notas de texto + fotos para el manager. Toca "+" abajo a la derecha para agregar.',
  'reporter.notes.offlineTitle': 'Offline primero',
  'reporter.notes.offlineBody': 'Las notas sin señal se almacenan en IndexedDB y sincronizan cuando vuelve la conexión.',
  'reporter.claims.title': 'Verificación de reclamo',
  'reporter.claims.body': 'Escanea el QR del invitado o teclea el código para marcar un premio entregado en {event}.',
  'brandStudio.look.atlediTone': 'Voz de Atledi',
  'brandStudio.look.atlediToneHint': 'Da forma a la narrativa de resumen + bienvenida que Atledi escribe para esta marca.',
  'brandStudio.look.atlediTone.playful': 'Divertida',
  'brandStudio.look.atlediTone.professional': 'Profesional',
  'brandStudio.look.atlediTone.warm': 'Cálida',
  'brandStudio.look.atlediTone.bold': 'Audaz',
  'brandStudio.look.atlediTone.custom': 'Personalizada',
  'brandStudio.look.atlediToneCustomPlaceholder': 'Describe la voz — ej. "concisa, basada en datos, sin signos de exclamación".',
  'brandStudio.look.consentVariant': 'Variante de consentimiento',
  'brandStudio.look.consentVariantHint': 'Anónima (A) omite PII y marketing. PII (B) recopila contacto y muestra el opt-in de marketing por separado.',
  'brandStudio.look.consentVariantA': 'A · Anónima',
  'brandStudio.look.consentVariantADesc': 'Sin PII ni marketing. Solo aceptación de T&C.',
  'brandStudio.look.consentVariantB': 'B · PII + opt-in de marketing',
  'brandStudio.look.consentVariantBDesc': 'Recopila contacto + checkbox de marketing opcional por separado.',
  'publicSurvey.claim.preparing': 'Generando tu código de canje…',
  'publicSurvey.claim.codeLabel': 'Código de canje',
  'publicSurvey.claim.showToBa':
    'Muestra este código al promotor antes de que termine el tiempo.',
  'publicSurvey.claim.expiredAskBa':
    'El tiempo se agotó. Pide al promotor que lo extienda.',
  'publicSurvey.wheel.emptyTitle': 'Stock vacío',
  'publicSurvey.wheel.emptyBody': 'Todos los premios de esta estación ya fueron reclamados.',

  // ── Output tab (event QR + short URLs) ─────────────────────────────
  'output.noClonedSurvey': 'Este BrandStudioEvent aún no tiene journey clonada — primero elige una plantilla.',
  'output.noStations':
    'Aún no hay estaciones en este evento. Crea estaciones en la pestaña Estaciones y aparecerán aquí para distribuir QRs.',
  'output.intro': 'Cada estación tiene su propio URL corto + QR. Imprime o compártelo con el equipo en sitio.',
  'output.toast.copied': 'URL copiada',
  'output.toast.copyFailed': 'No se pudo copiar',
  'output.copied': 'Copiado',
  'output.copy': 'Copiar',
  'output.saveSvg': 'Guardar SVG',
  'output.open': 'Abrir',

  // ── Active Log (auditoría) ─────────────────────────────────────────
  'activeLog.title': 'Registro de actividad',
  'activeLog.subtitle': 'Cada cambio en tu espacio — quién, cuándo, dónde.',
  'sidebar.activeLog': 'Auditoría',
  'activeLog.noAccessTitle': 'Sin acceso a auditoría',
  'activeLog.noAccessBody': 'Tu rol no tiene acceso al registro de auditoría.',
  'activeLog.empty.title': 'Sin actividad aún',
  'activeLog.empty.body': 'Cuando el equipo trabaje, cada acción relevante aparecerá aquí.',
  'activeLog.filter.search': 'Buscar',
  'activeLog.filter.searchPlaceholder': 'Descripción, URL, usuario…',
  'activeLog.filter.action': 'Acción',
  'activeLog.filter.actionAll': 'Todas las acciones',
  'activeLog.filter.entity': 'Entidad',
  'activeLog.filter.userId': 'ID de usuario',
  'activeLog.filter.eventId': 'ID de evento',
  'activeLog.filter.from': 'Desde',
  'activeLog.filter.to': 'Hasta',
  'activeLog.filter.apply': 'Aplicar',
  'activeLog.filter.reset': 'Limpiar',
  'activeLog.table.when': 'Cuándo',
  'activeLog.table.user': 'Usuario',
  'activeLog.table.action': 'Acción',
  'activeLog.table.entity': 'Entidad',
  'activeLog.table.description': 'Descripción',
  'activeLog.pager.summary': 'Mostrando {from}–{to} de {total}',
  'activeLog.pager.prev': 'Página anterior',
  'activeLog.pager.next': 'Página siguiente',
  'activeLog.detail.id': 'ID',
  'activeLog.detail.companyId': 'Marca',
  'activeLog.detail.user': 'Usuario',
  'activeLog.detail.email': 'Correo',
  'activeLog.detail.method': 'Método HTTP',
  'activeLog.detail.appType': 'App',
  'activeLog.detail.eventId': 'ID de evento',
  'activeLog.detail.ip': 'Dirección IP',
  'activeLog.detail.url': 'URL',
  'activeLog.detail.userAgent': 'User agent',
  'activeLog.detail.description': 'Descripción',
  'activeLog.detail.oldData': 'Datos previos',
  'activeLog.detail.newData': 'Datos nuevos',

  // ── Paywall (servicio premium) ─────────────────────────────────────
  'paywall.badge.premium': 'Premium',
  'paywall.badge.locked': 'Bloqueado',
  'paywall.cta.viewPlans': 'Ver planes',
  'paywall.activeLog.title': 'Active Log es un servicio premium',
  'paywall.activeLog.description':
    'Audita cada acción de tu equipo: quién creó, editó o eliminó qué, desde dónde y cuándo. Activa este servicio en tu plan para empezar a ver el historial completo.',
  'paywall.activeLog.perk1': 'Historial granular de cada cambio (antes/después)',
  'paywall.activeLog.perk2': 'Filtrado por usuario, entidad, fecha e IP',
  'paywall.activeLog.perk3': 'Exportación y retención extendida',
  'paywall.notifications.title': 'Notificaciones es un servicio premium',
  'paywall.notifications.description':
    'Envía mensajes en tiempo real (push, in-app y email) a tu equipo y a tus operadores en estación. Activa este servicio en tu plan para empezar a comunicarte.',
  'paywall.notifications.perk1': 'Push instantáneo a dispositivos de operadores',
  'paywall.notifications.perk2': 'Email opcional al mismo destinatario',
  'paywall.notifications.perk3': 'Segmentación por usuarios o grupos',
  'paywall.aiAssistant.title': 'AI Assistant es un servicio premium',
  'paywall.aiAssistant.description':
    'Haz preguntas sobre tus proyectos, eventos, journeys y interactions en lenguaje natural. El asistente transmite interactions basadas en tus datos en vivo.',
  'paywall.aiAssistant.perk1':
    'Preguntas y interactions instantáneas sobre tus eventos, estaciones y interactions',
  'paywall.aiAssistant.perk2':
    'Insights al alcance sin exportar a una hoja de cálculo',
  'paywall.aiAssistant.perk3':
    'Siempre basado en los datos que tu plan habilita',
};

const DICTS: Record<AppLanguage, Dict> = { en: EN, es: ES };

export type TranslationKey = keyof typeof EN;

export function translate(language: AppLanguage, key: string): string {
  return DICTS[language]?.[key] ?? EN[key] ?? key;
}
